import { apiService, RequestConfig } from './api';
import { SystemSettings } from '../types/subscription';
import type { LogPurgeProgress } from '../types/logManagement';
import { userActionLogger } from './userActionLogger';
import { extractAuthErrorMessage, extractRequiresCaptcha, extractCaptchaError, extractLockoutMessageFields } from './loginCaptchaService';
import { databaseLogger } from './databaseLogger';
import { adminTokenStorage } from './adminTokenStorage';
import { normalizeStaticPagesMap, type StaticPagesMap } from './publicSystemConfigService';
import { normalizeReleaseChanges } from './appReleaseService';

export type { LogPurgeProgress };
// Import adminTokenSystem - using a function to access it lazily to avoid initialization order issues
import * as adminTokenSystemModule from './adminTokenSystem';
const getAdminTokenSystem = () => adminTokenSystemModule.adminTokenSystem;

// ============================================================================
// ADMIN AUTHENTICATION TYPES
// ============================================================================

export interface AdminLoginDto {
  email: string;
  password: string;
  captchaId?: string;
  captchaCode?: string;
}

export interface AdminAuthResponseDto {
  success: boolean;
  message?: string;
  messageKey?: string;
  lockoutMinutesRemaining?: number;
  token?: string;
  expiresAt?: string;
  user?: AdminUserDto;
  requiresCaptcha?: boolean;
  captchaError?: boolean;
}

export interface AdminUserDto {
  id: string;
  email: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  profilePhoto?: string | undefined;
  phoneNumber?: string;
  bio?: string;
  nationality?: string;
  location?: string;
  age?: number;
  gender?: string;
  roles: string[];
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  currentLevel?: number;
  completedLessons?: number;
  completedQuizzes?: number;
  averageScore?: number;
  overallProgress?: number;
  // Verification codes for admin display
  emailVerificationCode?: string;
  phoneVerificationCode?: string;
  emailConfirmed?: boolean;
  phoneConfirmed?: boolean;
  lockoutEnabled?: boolean;
  lockoutEnd?: string | null;
  accessFailedCount?: number;
}

// Server response interface for admin users (what the API actually returns)
interface ServerAdminUserDto {
  id: string;
  email: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string; // Server uses this field name
  profilePhoto?: string; // Fallback in case server uses this name
  phoneNumber?: string;
  bio?: string;
  nationality?: string;
  age?: number;
  gender?: string;
  location?: string;
  roles: string[];
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  currentLevel?: number;
  completedLessons?: number;
  completedQuizzes?: number;
  averageScore?: number;
  overallProgress?: number;
  // Verification codes for admin display
  emailVerificationCode?: string;
  phoneVerificationCode?: string;
  emailConfirmed?: boolean;
  phoneConfirmed?: boolean;
  lockoutEnabled?: boolean;
  lockoutEnd?: string | null;
  accessFailedCount?: number;
}

// ============================================================================
// PAGINATION AND FILTERING TYPES
// ============================================================================

export interface PaginationParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  filters?: Record<string, any>;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// ============================================================================
// TIMEZONE MANAGEMENT TYPES
// ============================================================================

export interface TimezoneInfo {
  id: string;
  displayName: string;
  standardName: string;
  daylightName?: string;
  baseUtcOffset: string;
  supportsDaylightSavingTime: boolean;
}

export interface CurrentTimezoneInfo {
  timeZoneId: string;
  displayName: string;
  currentUtcOffset: string;
  isDaylightSavingTime: boolean;
  notes?: string;
  lastUpdated?: string;
  updatedBy?: string;
}

export interface UpdateTimezoneDto {
  timeZoneId: string;
  notes?: string;
}

export interface SystemTimeInfo {
  currentTime: string;
  timeZoneId: string;
  timeZoneName: string;
  utcOffset: string;
  isDaylightSavingTime: boolean;
}

export interface UserFilters {
  role?: string;
  status?: 'active' | 'inactive';
  level?: number;
  dateFrom?: string;
  dateTo?: string;
}

// ============================================================================
// USER MANAGEMENT TYPES
// ============================================================================

export interface CreateUserDto {
  email: string;
  password: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  bio?: string;
  nationality?: string;
  location?: string;
  age?: number;
  gender?: string;
  roles?: string[];
  isActive: boolean;
}

export interface UpdateUserDto {
  email?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  bio?: string;
  nationality?: string;
  location?: string;
  age?: number;
  gender?: string;
  profilePhoto?: string;
  profilePhotoFile?: File; // For file uploads
  roles?: string[];
  isActive?: boolean;
}

export interface AssignRoleDto {
  userId: string;
  roleNames: string[];
}

export interface RoleDto {
  id?: string | number; // Can be string (GUID) or number depending on backend
  name: string;
  description?: string;
  permissions?: string[];
  userCount?: number; // Number of users assigned to this role
}

// ============================================================================
// QUICK LAUNCH CARDS (ADMIN)
// ============================================================================

export interface QuickLaunchCardDto {
  id: number;
  cardKey: string;
  color: string;
  backgroundImageUrl?: string | null;
  displayOrder: number;
}

export interface UpdateQuickLaunchCardDto {
  color?: string;
  backgroundImageUrl?: string | null;
}

// ============================================================================
// ACHIEVEMENT / GAMIFICATION (ADMIN)
// ============================================================================

export interface AchievementAdminDto {
  id: number;
  name: string;
  description: string;
  iconUrl?: string | null;
  xpReward: number;
  type: string;
  category: string;
  requiredValue: number;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  earnedCount: number;
  profileFieldCriteria?: string[] | null;
}

export interface CreateAchievementDto {
  name: string;
  description: string;
  iconUrl?: string | null;
  xpReward?: number;
  type: string;
  category: string;
  requiredValue?: number;
  isActive?: boolean;
  displayOrder?: number;
  profileFieldCriteria?: string[] | null;
}

export interface UpdateAchievementDto {
  name?: string;
  description?: string;
  iconUrl?: string | null;
  xpReward?: number;
  type?: string;
  category?: string;
  requiredValue?: number;
  isActive?: boolean;
  displayOrder?: number;
  profileFieldCriteria?: string[] | null;
}

// ============================================================================
// DASHBOARD AND ANALYTICS TYPES
// ============================================================================

export interface OnlineUserDto {
  userId: string;
  email: string;
  fullName: string;
  userName?: string;
  lastActivityAt?: string;
  activitySource: string; // "ApiCall", "UserAction", "Login"
}

export interface AdminDashboardStatsDto {
  totalUsers: number;
  newUsers: number; // New users for selected duration
  onlineUsers: number; // Users active in last 15 minutes
  totalCourses: number;
  totalLevels: number;
  totalLessons: number;
  freeUsers: number; // Users without active subscription
  activeSubscribers: number; // Users with active subscription
  pendingCommentCount: number; // Count of pending comments
  unresolvedErrorCount: number; // Count of unresolved error logs
  mostUsedEndpointsAllTime: MostUsedEndpointDto[];
  mostUsedEndpointsByDuration: MostUsedEndpointDto[];
  mostUsedTablesAllTime: MostUsedTableDto[];
  mostUsedTablesByDuration: MostUsedTableDto[];
  
  // Legacy fields for backward compatibility
  activeUsers: number;
  newUsersToday?: number;
  newUsersThisMonth?: number;
  totalContent: number;
  systemHealth?: string;
  contentStats?: any;
  roleDistribution?: any[];
}

export interface MostUsedEndpointDto {
  endpoint: string;
  httpMethod: string;
  callCount: number;
  averageResponseTimeMs: number;
}

export interface MostUsedTableDto {
  tableName: string;
  operationCount: number;
  averageExecutionTimeMs: number;
}

export interface UserAnalyticsDto {
  totalUsers: number;
  activeUsers: number;
  usersByLevel: Record<number, number>;
  usersByNationality: Record<string, number>;
  usersByLocation?: Record<string, number>;
  usersByAgeGroup: Record<string, number>;
  usersByGender?: Record<string, number>;
  engagementMetrics?: EngagementMetricsDto;
  learningPatterns?: Record<string, number>;
}

export interface EngagementMetricsDto {
  averageSessionDuration: number;
  averageActivitiesPerSession: number;
  retentionRates: RetentionRateDto;
  mostPopularContent: PopularContentDto[];
}

export interface RetentionRateDto {
  day1: number;
  day7: number;
  day30: number;
}

export interface PopularContentDto {
  type: string;
  title: string;
  usageCount: number;
  averageScore: number;
}

export interface LearningPatternDto {
  timeOfDay: string;
  activityType: string;
  frequency: number;
  averageScore: number;
}

// ============================================================================
// CONTENT MANAGEMENT TYPES
// ============================================================================

export interface ContentStatisticsDto {
  totalVocabulary: number;
  totalGrammarLessons: number;
  totalDialogues: number;
  totalQuizzes: number;
  contentByLevel: Record<number, ContentLevelStatsDto>;
}

export interface ContentLevelStatsDto {
  vocabulary: number;
  grammar: number;
  dialogues: number;
  quizzes: number;
}

// ============================================================================
// SUBSCRIPTION MANAGEMENT TYPES
// ============================================================================

export interface SubscriptionPlanDto {
  id: number;
  name: string;
  duration: number; // in days
  durationDays?: number;
  price: number; // in euros
  description?: string;
  icon?: string;
  color?: string;
  isActive: boolean;
  createdAt: string;
}

export interface CreateSubscriptionPlanDto {
  name: string;
  duration: number;
  price: number;
  description?: string;
  icon?: string;
  color?: string;
  isActive: boolean;
}

export interface UpdateSubscriptionPlanDto {
  name?: string;
  duration?: number;
  price?: number;
  description?: string;
  icon?: string;
  color?: string;
  isActive?: boolean;
}

export interface XpSubscriptionOfferDto {
  id: number;
  name: string;
  xpCost: number;
  grantDurationDays: number;
  subscriptionPlanId: number;
  subscriptionPlanName: string;
  subscriptionPlanIcon?: string;
  subscriptionPlanColor?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string;
}

export interface XpRedemptionHistoryAdminDto {
  id: number;
  userId: string;
  userEmail?: string;
  xpSpent: number;
  xpBalanceBefore?: number | null;
  xpBalanceAfter?: number | null;
  grantDurationDays: number;
  planName: string;
  offerName?: string | null;
  planIcon?: string;
  planColor?: string;
  subscriptionEndDate: string;
  createdAt: string;
}

export interface CreateUpdateXpSubscriptionOfferDto {
  name: string;
  xpCost: number;
  grantDurationDays: number;
  subscriptionPlanId: number;
  isActive: boolean;
  sortOrder: number;
}

export interface DefaultTrialSettingsDto {
  id: number;
  defaultTrialDays: number;
  trialAccessFeatures?: Record<string, boolean>;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UpdateDefaultTrialSettingsDto {
  defaultTrialDays: number;
  trialAccessFeatures?: Record<string, boolean>;
  isActive?: boolean;
}

export interface UserTrialSettingsDto {
  id: number;
  userId: string;
  customTrialDays?: number;
  customTrialAccessFeatures?: Record<string, boolean>;
  createdAt: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UpdateUserTrialSettingsDto {
  userId: string;
  customTrialDays?: number;
  customTrialAccessFeatures?: Record<string, boolean>;
}

export interface AssignSubscriptionPlanDto {
  userId: string;
  subscriptionPlanId: number;
  startDate?: string;
  endDate?: string;
  extendExisting?: boolean;
}

export interface UserSubscriptionDto {
  id: number;
  userId: string;
  subscriptionPlan: SubscriptionPlanDto;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isExpired: boolean;
  remainingDays: number;
  remainingHours: number;
  createdAt: string;
}

export interface SubscriptionAnalyticsDto {
  totalActiveSubscriptions: number;
  totalTrialUsers: number;
  totalExpiredSubscriptions: number;
  totalRevenue: number;
  monthlyRevenue: number;
  conversionRate: number;
  averageSubscriptionDuration: number;
  popularPlans: PopularPlanDto[];
  revenueByMonth: RevenueByMonthDto[];
  subscriptionsByStatus: SubscriptionStatusCountDto[];
}

export interface PopularPlanDto {
  planName: string;
  subscriptionCount: number;
  revenue: number;
  conversionRate?: number;
  percentage?: number;
}

export interface RevenueByMonthDto {
  month?: string;
  monthName?: string;
  year?: number;
  Month?: number;
  revenue: number;
  subscriptionCount: number;
}

export interface SubscriptionStatusCountDto {
  status: string;
  count: number;
  percentage: number;
}

export interface UserSubscriptionOverviewDto {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  subscriptionStatus: 'Active' | 'Trial' | 'Expired' | 'None';
  currentPlan?: string;
  currentSubscriptionPlanId?: number;
  subscriptionEndDate?: string;
  trialEndDate?: string;
  remainingDays: number;
  totalSpent: number;
  subscriptionCount: number;
  lastPaymentDate?: string;
}

export interface TrialExtensionDto {
  userId: string;
  additionalDays: number;
  reason?: string;
}

export interface TrialInitializationDto {
  userId: string;
  trialDays: number;
}

// ============================================================================
// POEM MANAGEMENT TYPES
// ============================================================================

export interface PoemDto {
  id: number;
  title: string;
  content: string;
  author: string;
  category: string;
  isActive: boolean;
  translation?: string;
  description?: string;
  displayOrder?: number;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CreatePoemDto {
  title: string;
  content: string;
  author: string;
  category: string;
  isActive: boolean;
  translation?: string;
  description?: string;
  displayOrder?: number;
  createdBy?: string;
}

export interface UpdatePoemDto {
  id?: number;
  title?: string;
  content?: string;
  author?: string;
  category?: string;
  isActive?: boolean;
  translation?: string;
  description?: string;
  displayOrder?: number;
  updatedBy?: string;
}

export interface PoemCategoryDto {
  name: string;
  nameFa?: string; // Persian translation
  count: number;
  translations?: Record<string, string>; // Multi-language translations
}

// ============================================================================
// SYSTEM CONFIGURATION TYPES
// ============================================================================

export interface SystemConfigDto {
  apiSettings: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
    enableCaching: boolean;
    cacheTimeout: number;
  };
  contentSettings: {
    maxVocabularyPerLevel: number;
    maxGrammarLessonsPerLevel: number;
    maxDialoguesPerLevel: number;
    maxQuizzesPerLevel: number;
    defaultQuizTimeLimit: number;
    enableAudioFeatures: boolean;
  };
  persianSettings: {
    defaultFont: string;
    enableRTL: boolean;
    enableBidirectionalText: boolean;
    persianNumberFormatting: boolean;
  };
  securitySettings: {
    tokenExpirationMinutes: number;
    maxLoginAttempts: number;
    captchaAfterFailedAttempts: number;
    lockoutDurationMinutes: number;
    /** @deprecated Idle logout disabled; kept for older API payloads */
    sessionTimeoutMinutes?: number;
  };
}

export interface UpdateSystemSettingsDto {
  defaultTrialDays?: number;
  apiBaseUrl?: string;
  apiTimeout?: number;
  apiRetryAttempts?: number;
  enableCaching?: boolean;
  cacheTimeout?: number;
  maxVocabularyPerLevel?: number;
  maxGrammarLessonsPerLevel?: number;
  maxDialoguesPerLevel?: number;
  maxQuizzesPerLevel?: number;
  defaultQuizTimeLimit?: number;
  enableAudioFeatures?: boolean;
  defaultFont?: string;
  enableRTL?: boolean;
  enableBidirectionalText?: boolean;
  persianNumberFormatting?: boolean;
  tokenExpirationMinutes?: number;
  sessionTimeoutMinutes?: number;
  maxLoginAttempts?: number;
  enableTwoFactor?: boolean;
  passwordMinLength?: number;
}

export interface UpdateSecuritySettingsDto {
  tokenExpirationMinutes?: number;
  maxLoginAttempts?: number;
  captchaAfterFailedAttempts?: number;
  lockoutDurationMinutes?: number;
}

export interface ReleaseChangeSections {
  features: string[];
  modules: string[];
  bugFixes: string[];
  improvements: string[];
}

export interface AppReleaseDto {
  id: number;
  versionLabel: string;
  title?: string | null;
  titleFa?: string | null;
  summary: string;
  summaryFa?: string | null;
  heroImageUrl?: string | null;
  changes: ReleaseChangeSections;
  changesFa?: ReleaseChangeSections | null;
  releasedAt: string;
  isPublished: boolean;
  pwaNotificationActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateAppReleasePayload {
  versionLabel: string;
  title?: string;
  titleFa?: string;
  summary: string;
  summaryFa?: string;
  heroImageUrl?: string | null;
  changes: ReleaseChangeSections;
  changesFa?: ReleaseChangeSections;
  releasedAt?: string;
  isPublished?: boolean;
}

export interface UpdateAppReleasePayload {
  versionLabel: string;
  title?: string;
  titleFa?: string;
  summary: string;
  summaryFa?: string;
  heroImageUrl?: string | null;
  changes: ReleaseChangeSections;
  changesFa?: ReleaseChangeSections;
  releasedAt?: string;
  isPublished?: boolean;
}

export interface AnnouncePwaReleaseResult {
  shellVersion: number;
  releaseId: number;
  message: string;
}

export interface UpdateCombinedSettingsDto {
  systemSettings?: UpdateSystemSettingsDto;
  securitySettings?: UpdateSecuritySettingsDto;
}

export interface ApiEndpointTestDto {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  expectedStatus: number;
  requiresAuth: boolean;
  testData?: any;
}

export interface ApiTestResultDto {
  endpoint: string;
  method: string;
  status: 'success' | 'failed' | 'timeout';
  responseTime: number;
  statusCode: number;
  errorMessage?: string;
  responseData?: any;
  timestamp: string;
}

export interface PersianTextValidationDto {
  text: string;
  isValid: boolean;
  encoding: string;
  direction: 'rtl' | 'ltr' | 'mixed';
  containsPersianChars: boolean;
  containsArabicChars: boolean;
  issues?: string[];
  recommendations?: string[];
}

// Import unified log management types
import {
  LogType,
  BaseLogDto,
  ErrorLogDto as ErrorLogDtoType,
  RawErrorLogDto,
  UserActionLogDto,
  ApiLogDto,
  DatabaseLogDto,
  SecurityLogDto,
  LogQueryParams,
  EnhancedLogQueryParams,
  UnifiedLogFilters,
  SystemHealthStatus,
  LogStatistics,
  LogTrendData,
  CrossLogAnalytics,
  BulkOperationRequest,
  BulkOperationResult,
  ExportConfiguration,
  ExportResult,
  TestLogConfig,
  ValidationResult,
  LoadTestConfig,
  LoadTestResult,
  AlertConfiguration,
  ActiveAlert
} from '../types/logManagement';

// Re-export ErrorLogDto for backward compatibility
export interface ErrorLogDto {
  id: string;
  level: 'error' | 'warning' | 'info';
  message: string;
  source: string;
  stackTrace?: string;
  userId?: string;
  endpoint?: string;
  timestamp: string;
  resolved: boolean;
  tags?: string[];
}

export interface SystemDiagnosticsDto {
  systemHealth: 'healthy' | 'warning' | 'critical';
  apiConnectivity: boolean;
  databaseConnectivity: boolean;
  cacheConnectivity: boolean;
  diskSpace: {
    total: string;
    used: string;
    available: string;
    percentage: number;
  };
  memory: {
    total: string;
    used: string;
    available: string;
    percentage: number;
  };
  uptime: string;
  lastHealthCheck: string;
  issues?: string[];
}

// ============================================================================
// POEM MANAGEMENT TYPES
// ============================================================================

export interface PoemCategoryDto {
  name: string;
  count: number;
}

// ============================================================================
// DATA SEEDING TYPES
// ============================================================================

export interface SeedingResultDto {
  success: boolean;
  message: string;
  seededData?: {
    vocabulary?: number;
    grammarLessons?: number;
    dialogues?: number;
    quizzes?: number;
  };
  itemsCreated?: number;
  levels?: number[];
  seededAt: string;
  duration?: string;
  errors?: string[];
}

export interface SeedingHistoryDto {
  id: string;
  operation: string;
  contentType: string;
  status: 'success' | 'failed' | 'partial';
  itemsCreated: number;
  duration: string;
  timestamp: string;
  errors?: string[];
  canRollback: boolean;
}

export interface SeedingStatusDto {
  isSeeding: boolean;
  currentOperation?: string;
  progress?: number;
  estimatedTimeRemaining?: string;
  lastSeeded?: string;
}

// ============================================================================
// PERFORMANCE AND HEALTH MONITORING TYPES
// ============================================================================

export interface ApiHealthDto {
  status: string;
  timestamp: string;
  recentRequests: number;
  averageResponseTime: number;
  cacheHitRate: number;
  databaseQueryCount: number;
  failedQueries: number;
  errorMessage?: string;
}

export interface PerformanceMetricsDto {
  generatedAt: string;
  timeRange: string;
  apiMetrics: ApiMetrics;
  databaseMetrics: DatabaseMetrics;
  cacheMetrics: CacheMetrics;
}

export interface ApiMetrics {
  totalRequests: number;
  averageResponseTime: number;
  medianResponseTime: number;
  p95ResponseTime: number;
  endpointCounts?: Record<string, number>;
  statusCodeCounts?: Record<string, number>;
  slowestRequests?: SlowRequestDto[];
}

export interface DatabaseMetrics {
  totalQueries: number;
  averageQueryTime: number;
  medianQueryTime: number;
  failedQueries: number;
  operationCounts?: Record<string, number>;
  slowestQueries?: SlowQueryDto[];
}

export interface CacheMetrics {
  totalOperations: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  operationCounts?: Record<string, number>;
}

export interface SlowRequestDto {
  endpoint: string;
  method: string;
  responseTime: number;
  timestamp: string;
}

export interface SlowQueryDto {
  query: string;
  executionTime: number;
  timestamp: string;
}

// ============================================================================
// ADMIN SERVICE CLASS
// ============================================================================

class AdminService {
  constructor() {
    // Initialize admin token from localStorage if available
    this.initializeAdminToken();
    // Initialize concurrent request handling
    this.initializeConcurrentRequestHandling();
  }

  /**
   * Initialize admin token from storage on service creation
   * ENHANCED: Uses reliable token storage service with concurrent request handling
   */
  private initializeAdminToken(): void {
    console.log('AdminService: Initializing admin token from storage');

    if (adminTokenStorage.isAuthenticated()) {
      const token = adminTokenStorage.getToken();
      if (token) {
        console.log('AdminService: Admin is authenticated, setting token in apiService');
        // Set the admin token in apiService for admin API calls
        apiService.setAdminToken(token);
        
        // Update admin token system state
        getAdminTokenSystem().updateApiServiceAuth(token);
        
        // Lazy load apiCallManager only when needed (not during initialization)
        // This prevents blocking the login page load
        // The apiCallManager will be loaded when dashboard is accessed
      }
    } else {
      console.log('AdminService: Admin not authenticated or no valid token available');
      // Clear any stale tokens
      apiService.clearAdminToken();
      getAdminTokenSystem().clearAuthenticationState();
    }
  }

  /**
   * Ensure admin token is set before making API calls
   * ENHANCED: Uses reliable token storage with concurrent request handling
   */
  private ensureAdminToken(): void {
    if (adminTokenStorage.isAuthenticated()) {
      const token = adminTokenStorage.getToken();
      if (token) {
        // Re-set the token to ensure it's current
        apiService.setAdminToken(token);
        console.log('AdminService: Admin token refreshed in apiService');
        return;
      }
    }
    
    console.log('AdminService: No valid admin token available');
    throw new Error('Admin authentication required. Please log in again.');
  }

  /**
   * Ensure admin token with concurrent request safety
   * Use this for operations that might be called simultaneously
   */
  private async ensureAdminTokenConcurrentSafe(): Promise<void> {
    try {
      await this.ensureAdminTokenEnhanced();
    } catch (error) {
      console.error('AdminService: Concurrent-safe token validation failed:', error);
      throw error;
    }
  }

  /**
   * Maps server admin user response to client interface
   * Handles field name differences between server and client
   */
  private mapServerAdminUserToClient(serverUser: ServerAdminUserDto): AdminUserDto {
    // Handle property name variations from JSON serialization
    const serverUserAny = serverUser as any;
    const profileImageUrl = serverUserAny.profileImageUrl || 
                           serverUserAny.ProfileImageUrl || 
                           serverUserAny.profilePhoto ||
                           serverUserAny.ProfilePhoto;

    // Extract all properties except profile image related ones
    const { profileImageUrl: _, ProfileImageUrl: __, profilePhoto: ___, ProfilePhoto: ____, ...rest } = serverUserAny;

    // Convert /uploads/profiles/filename to full image endpoint URL
    let profilePhoto: string | undefined = undefined;
    if (profileImageUrl && typeof profileImageUrl === 'string' && profileImageUrl.trim() !== '') {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5127';

      if (profileImageUrl.startsWith('/uploads/profiles/') || profileImageUrl.startsWith('uploads/profiles/')) {
        const filename = profileImageUrl.split('/').pop();
        if (filename) {
          profilePhoto = `${baseUrl}/api/UserProfile/image/${filename}`;
        }
      } else if (profileImageUrl.startsWith('/api/UserProfile/image/') && !profileImageUrl.startsWith('http')) {
        profilePhoto = `${baseUrl}${profileImageUrl}`;
      } else if (!profileImageUrl.startsWith('http') && !profileImageUrl.startsWith('data:')) {
        profilePhoto = `${baseUrl}${profileImageUrl.startsWith('/') ? profileImageUrl : '/' + profileImageUrl}`;
      } else {
        profilePhoto = profileImageUrl;
      }
    }

    // Handle learning statistics - use new calculated fields if available, fallback to old stored fields
    const completedLessons = rest.completedLessons ?? rest.CompletedLessons ?? rest.totalLessonsCompleted ?? rest.TotalLessonsCompleted ?? 0;
    const completedQuizzes = rest.completedQuizzes ?? rest.CompletedQuizzes ?? rest.totalQuizzesTaken ?? rest.TotalQuizzesTaken ?? 0;
    const averageScore = rest.averageScore ?? rest.AverageScore ?? rest.averageQuizScore ?? rest.AverageQuizScore ?? 0;
    const overallProgress = rest.overallProgress ?? rest.OverallProgress ?? undefined;

    return {
      ...rest,
      nationality: rest.nationality ?? rest.location,
      location: rest.nationality ?? rest.location,
      profilePhoto: profilePhoto || undefined,
      completedLessons: completedLessons,
      completedQuizzes: completedQuizzes,
      averageScore: averageScore,
      overallProgress: overallProgress
    };
  }

  // ============================================================================
  // AUTHENTICATION METHODS
  // ============================================================================

  /**
   * SIMPLE admin login - just call API and store token if successful
   */
  async adminLogin(credentials: AdminLoginDto): Promise<AdminAuthResponseDto> {
    try {
      console.log('AdminService: Simple admin login starting...');
      console.log('AdminService: Calling /api/Admin/login with credentials');

      // Keep login responsive: short timeout, no retries to avoid long loader
      const loginRequestConfig: RequestConfig = {
        timeout: 5000,
        retries: 0,
      };

      // Make the API call - that's it!
      const response = await apiService.post<any>(
        '/api/Admin/login',
        credentials,
        loginRequestConfig
      );

      console.log('AdminService: API response received:', response);

      // Handle both swagger format {success, token, user} and actual API format {token, user}
      const hasToken = response.token;
      const hasUser = response.user;
      const isSuccessful = response.success !== false; // If success field exists, check it; otherwise assume true if we have token/user

      if (hasToken && hasUser && isSuccessful) {
        console.log('AdminService: Login successful, storing token');

        // Store token using reliable token storage service
        adminTokenStorage.storeToken(response.token, response.expiresAt, response.user);

        // Ensure API layer has the token immediately (before any dashboard requests)
        apiService.setAdminToken(response.token);

        // Update admin token system with new authentication state
        getAdminTokenSystem().updateApiServiceAuth(response.token);

        // Record activity for session tracking
        try {
          const { beginAdminSession } = await import('../utils/sessionExpirationPolicy');
          beginAdminSession();
          (window as any).__sessionRedirectInProgress = false;
          console.log('AdminService: Admin idle clock started after login');
        } catch (error) {
          console.warn('AdminService: Failed to start admin session policy after login:', error);
        }

        console.log('AdminService: ✅ Admin login completed successfully');
        console.log('AdminService: Token stored using adminTokenStorage');
        console.log('AdminService: Authentication check after login:', adminTokenStorage.isAuthenticated());

        return {
          success: true,
          token: response.token,
          user: response.user,
          expiresAt: response.expiresAt
        };
      } else {
        console.log('AdminService: Login failed');
        console.log('AdminService: Response keys:', Object.keys(response));
        console.log('AdminService: Has token:', hasToken);
        console.log('AdminService: Has user:', hasUser);
        console.log('AdminService: Success field:', response.success);

        return {
          success: false,
          message: response.message || 'Login failed',
          requiresCaptcha: !!response.requiresCaptcha,
          captchaError: !!response.captchaError,
        };
      }
    } catch (error: any) {
      console.error('AdminService: Login error:', error);

      if (error.statusCode === 401 || error.statusCode === 400) {
        const lockout = extractLockoutMessageFields(error);
        return {
          success: false,
          message: extractAuthErrorMessage(error, 'Invalid email or password'),
          messageKey: lockout.messageKey,
          lockoutMinutesRemaining: lockout.lockoutMinutesRemaining,
          requiresCaptcha: extractRequiresCaptcha(error),
          captchaError: extractCaptchaError(error),
        };
      }

      let message = 'Login failed';
      if (error.statusCode === 401) {
        message = 'Invalid email or password';
      } else if (error.statusCode === 403) {
        message = 'Access denied - insufficient permissions';
      } else if (error.message) {
        message = error.message;
      }

      return {
        success: false,
        message
      };
    }
  }

  /**
   * Check if user has admin session
   * FIXED: Uses reliable token storage service
   */
  isAdminAuthenticated(): boolean {
    return adminTokenStorage.isAuthenticated();
  }

  /**
   * Get current admin user info
   * FIXED: Uses reliable token storage service
   */
  getCurrentAdminUser(): AdminUserDto | null {
    return adminTokenStorage.getCurrentUser();
  }

  /**
   * Admin logout
   */
  async adminLogout(): Promise<void> {
    const adminUserId = this.getCurrentAdminUserId();
    const sessionDuration = this.getAdminSessionDuration();

    // Log admin logout action before clearing data
    if (adminUserId) {
      try {
        const { userActionLogger } = await import('./userActionLogger');
        await userActionLogger.logLogout(
          adminUserId,
          'admin_logout',
          sessionDuration
        );
        console.log('AdminService: Admin logout logged successfully');
      } catch (logError) {
        console.warn('AdminService: Failed to log admin logout:', logError);
      }
    }

    this.clearAdminSession();
    // CRITICAL: Clear admin token from apiService to prevent unauthorized API calls
    apiService.clearAdminToken();
    // Dispatch logout event
    window.dispatchEvent(new CustomEvent('admin:logout'));
  }

  /**
   * Get current admin user ID for logging
   * FIXED: Uses reliable token storage service
   */
  private getCurrentAdminUserId(): string | null | undefined {
    return adminTokenStorage.getUserId() || 'admin';
  }

  /**
   * Get admin session duration for logging
   * FIXED: Uses reliable token storage service
   */
  private getAdminSessionDuration(): number | undefined {
    return adminTokenStorage.getSessionDuration() || undefined;
  }

  /**
   * Refresh admin authentication - useful for long-running sessions
   * FIXED: Uses reliable token storage service
   */
  refreshAdminAuth(): boolean {
    try {
      this.initializeAdminToken();
      return adminTokenStorage.isAuthenticated();
    } catch (error) {
      console.error('AdminService: Failed to refresh admin auth:', error);
      return false;
    }
  }

  /**
   * Refresh admin token from server
   * FIXED: Uses reliable token storage service
   */
  async refreshAdminToken(): Promise<string> {
    try {
      const { adminMaySilentlyExtendSession, enforceAdminIdleTimeout } = await import('../utils/sessionExpirationPolicy');
      if (!adminMaySilentlyExtendSession()) {
        await enforceAdminIdleTimeout();
        const idleError = new Error('Admin session expired. Please log in again.');
        (idleError as { adminIdleRedirect?: boolean }).adminIdleRedirect = true;
        throw idleError;
      }

      const currentAdminToken = adminTokenStorage.getToken();
      if (!currentAdminToken) {
        throw new Error('No admin token available for refresh');
      }

      console.log('AdminService: Attempting to refresh admin token...');

      // Try to refresh the token using the admin refresh endpoint
      const response = await apiService.post<any>('/api/Admin/refresh-token', {}, {
        timeout: 10000,
        retries: 1
      });

      if (response.token) {
        // Update stored tokens using reliable storage service
        adminTokenStorage.storeToken(
          response.token, 
          response.expiresAt, 
          response.user || adminTokenStorage.getCurrentUser()
        );

        // Update API service token
        apiService.setAdminToken(response.token);

        console.log('AdminService: Admin token refreshed successfully');
        return response.token;
      } else {
        throw new Error('No token received from refresh endpoint');
      }
    } catch (error: any) {
      if (error?.adminIdleRedirect || (window as any).__sessionRedirectStarted) {
        throw error;
      }
      console.error('AdminService: Failed to refresh admin token:', error);
      
      // If refresh endpoint is missing, keep the current token until it actually expires
      if (error?.response?.status === 404 || error?.statusCode === 404) {
        console.warn('AdminService: Refresh token endpoint not found (404) — continuing with current token');
        const currentToken = adminTokenStorage.getToken();
        if (currentToken && adminTokenStorage.isAuthenticated()) {
          return currentToken;
        }
      }

      // Only clear session on genuine authentication failures
      if (this.isGenuineAuthenticationFailure(error)) {
        this.clearAdminSession();
        const currentPath = window.location.pathname;
        const returnUrl = encodeURIComponent(currentPath);
        window.location.href = `/admin/login?returnUrl=${returnUrl}&reason=auth_required`;
        throw new Error('Please log in again.');
      }

      const fallback = adminTokenStorage.getToken();
      if (fallback && adminTokenStorage.isAuthenticated()) {
        return fallback;
      }

      this.clearAdminSession();
      throw new Error('Failed to refresh admin token. Please log in again.');
    }
  }

  // ============================================================================
  // CONCURRENT REQUEST HANDLING METHODS
  // ============================================================================

  /**
   * Handle concurrent authentication requests without conflicts
   * Prevents race conditions during simultaneous token validation
   * Requirements: 1.3, 2.4, 2.5
   */
  async handleConcurrentAuthentication(): Promise<string> {
    try {
      console.log('AdminService: Handling concurrent authentication request');
      
      // Use the admin token system for concurrent validation
      const token = await getAdminTokenSystem().handleConcurrentValidation();
      
      console.log('AdminService: Concurrent authentication successful');
      return token;
    } catch (error) {
      console.error('AdminService: Concurrent authentication failed:', error);
      throw new Error('Failed to handle concurrent authentication request');
    }
  }

  /**
   * Validate token with race condition prevention
   * Ensures consistent authentication state across concurrent requests
   */
  async validateTokenSafely(token?: string): Promise<boolean> {
    try {
      console.log('AdminService: Safe token validation requested');
      
      // Use admin token system for safe validation
      const isValid = await getAdminTokenSystem().validateToken(token);
      
      if (isValid) {
        console.log('AdminService: Token validation successful');
        // Ensure API service is updated
        const currentToken = adminTokenStorage.getToken();
        if (currentToken) {
          apiService.setAdminToken(currentToken);
        }
      } else {
        console.log('AdminService: Token validation failed');
      }
      
      return isValid;
    } catch (error) {
      console.error('AdminService: Safe token validation error:', error);
      return false;
    }
  }

  /**
   * Get authentication state with concurrent request awareness
   * Provides real-time authentication status including validation progress
   */
  getEnhancedAuthenticationState(): {
    isAuthenticated: boolean;
    token: string | null;
    expiresAt: Date | null;
    lastValidated: Date | null;
    validationInProgress: boolean;
    hasActiveRequests: boolean;
  } {
    const baseState = getAdminTokenSystem().getAuthenticationState();
    
    return {
      ...baseState,
      hasActiveRequests: baseState.validationInProgress
    };
  }

  /**
   * Subscribe to authentication state changes
   * Allows components to react to authentication events in real-time
   */
  subscribeToAuthenticationChanges(callback: (state: any) => void): () => void {
    console.log('AdminService: Setting up authentication state subscription');
    
    return getAdminTokenSystem().subscribeToStateChanges((state) => {
      console.log('AdminService: Authentication state changed:', state);
      callback(state);
    });
  }

  /**
   * Ensure admin token is valid and API service is configured
   * Enhanced version with concurrent request handling
   */
  private async ensureAdminTokenEnhanced(): Promise<void> {
    try {
      console.log('AdminService: Ensuring admin token with enhanced validation');
      
      // Use concurrent-safe validation
      const isValid = await this.validateTokenSafely();
      
      if (!isValid) {
        console.log('AdminService: No valid admin token available');
        throw new Error('Admin authentication required. Please log in again.');
      }
      
      console.log('AdminService: Admin token validated and API service configured');
    } catch (error) {
      console.error('AdminService: Enhanced token validation failed:', error);
      throw new Error('Admin authentication required. Please log in again.');
    }
  }

  /**
   * Initialize concurrent request handling
   * Sets up monitoring and cleanup for authentication state
   */
  private initializeConcurrentRequestHandling(): void {
    console.log('AdminService: Initializing concurrent request handling');
    
    // Set up token expiration monitoring
    getAdminTokenSystem().onTokenExpired(() => {
      console.log('AdminService: Token expired, clearing admin session');
      this.clearAdminSession();
    });
    
    // Set up authentication state monitoring
    getAdminTokenSystem().subscribeToStateChanges((state) => {
      if (!state.isAuthenticated && state.token === null) {
        console.log('AdminService: Authentication lost, dispatching logout event');
        window.dispatchEvent(new CustomEvent('admin:logout', {
          detail: { reason: 'authentication_lost', timestamp: new Date().toISOString() }
        }));
      }
    });
    
    console.log('AdminService: Concurrent request handling initialized');
  }



  /**
   * Clear admin session
   * ENHANCED: Uses reliable token storage service with concurrent request handling
   */
  clearAdminSession(): void {
    try {
      void import('../utils/sessionExpirationPolicy').then(({ endAdminSession }) => endAdminSession());
    } catch {
      /* ignore */
    }
    // Get admin info before clearing for logging
    const adminUserId = adminTokenStorage.getUserId();
    const sessionDuration = adminTokenStorage.getSessionDuration();

    // Log admin logout before clearing session (non-blocking)
    if (adminUserId) {
      // Get IP address asynchronously without blocking
      const getIpAddress = async () => {
        try {
          return await (userActionLogger['getClientIP']?.() || Promise.resolve('unknown'));
        } catch {
          return 'unknown';
        }
      };
      
      getIpAddress().then(ipAddress => {
        userActionLogger.logUserAction({
          type: 'admin_logout',
          resourceType: 'admin_authentication',
          userId: adminUserId,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: ipAddress,
          userAgent: navigator.userAgent,
          duration: sessionDuration || undefined,
          details: {
            logoutReason: 'session_cleared',
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AdminService',
          feature: 'admin_authentication'
        }).catch(error => {
          console.warn('AdminService: Failed to log admin logout:', error);
        });
      }).catch(() => {
        // If IP fetch fails, log without IP
        userActionLogger.logUserAction({
          type: 'admin_logout',
          resourceType: 'admin_authentication',
          userId: adminUserId,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: 'unknown',
          userAgent: navigator.userAgent,
          duration: sessionDuration || undefined,
          details: {
            logoutReason: 'session_cleared',
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AdminService',
          feature: 'admin_authentication'
        }).catch(error => {
          console.warn('AdminService: Failed to log admin logout:', error);
        });
      });
    }

    // Clear authentication state using enhanced token system
    getAdminTokenSystem().clearAuthenticationState();
    
    // Clear token using reliable storage service
    adminTokenStorage.clearToken();
    
    // CRITICAL: Clear admin token from apiService to ensure no stale tokens remain
    apiService.clearAdminToken();

    // Dispatch admin logout event for health service and other listeners
    window.dispatchEvent(new CustomEvent('admin:logout', {
      detail: { userId: adminUserId, timestamp: new Date().toISOString() }
    }));
  }



  // ============================================================================
  // DASHBOARD METHODS
  // ============================================================================

  /**
   * Get list of online users
   */
  async getOnlineUsers(limit: number = 20): Promise<OnlineUserDto[]> {
    try {
      this.ensureAdminToken();
      const endpoint = `/api/Admin/dashboard/online-users?limit=${limit}`;
      console.log('AdminService: Fetching online users from:', endpoint);
      
      const onlineUsers = await apiService.get<OnlineUserDto[]>(endpoint, { cache: false });
      
      console.log('AdminService: Received online users:', onlineUsers?.length || 0, onlineUsers);
      
      // Log successful online users retrieval
      try {
        await userActionLogger.logUserAction({
          type: 'admin_get_online_users',
          resourceType: 'online_users',
          details: {
            count: onlineUsers?.length || 0,
            limit
          }
        });
      } catch (logError) {
        console.warn('AdminService: Failed to log online users retrieval:', logError);
      }
      
      return onlineUsers || [];
    } catch (error: any) {
      console.error('AdminService: Error getting online users:', error);
      console.error('AdminService: Error details:', {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });
      
      // Log failed online users retrieval
      try {
        await userActionLogger.logUserAction({
          type: 'admin_get_online_users_failed',
          resourceType: 'online_users',
          details: {
            error: error?.message || 'Unknown error'
          }
        });
      } catch (logError) {
        console.warn('AdminService: Failed to log failed online users retrieval:', logError);
      }
      
      return [];
    }
  }

  /**
   * Get admin dashboard statistics - OPTIMIZED to avoid unnecessary API calls
   */
  async getDashboardStats(duration: string = '24h'): Promise<AdminDashboardStatsDto> {
    const operationStartTime = Date.now();

    try {
      // Try to get stats from dedicated dashboard endpoint first
      try {
        this.ensureAdminToken();
        const dashboardStats = await apiService.get<AdminDashboardStatsDto>(`/api/Admin/dashboard/stats?duration=${duration}`, { cache: false });
        
        const operationDuration = Date.now() - operationStartTime;
        
        // Log successful dashboard stats retrieval
        await userActionLogger.logUserAction({
          type: 'admin_get_dashboard_stats',
          resourceType: 'dashboard_stats',
          duration: operationDuration,
          details: {
            operation: 'read',
            source: 'dashboard_endpoint',
            adminUserId: this.getCurrentAdminUserId()
          }
        }, {
          component: 'AdminService',
          feature: 'dashboard_management'
        });

        return dashboardStats;
      } catch (dashboardError) {
        console.log('AdminService: Dashboard stats endpoint not available, using fallback data');
      }

      // Fallback to mock data instead of fetching all users
      const stats: AdminDashboardStatsDto = {
        totalUsers: 0, // Will be populated by actual user count when needed
        activeUsers: 0, // Will be populated by actual active user count when needed
        newUsersToday: 0, // Will be calculated when needed
        totalContent: 0, // Simplified - no content API call needed for dashboard
        systemHealth: 'healthy'
      };

      const operationDuration = Date.now() - operationStartTime;

      // Log the admin operation
      await userActionLogger.logUserAction({
        type: 'admin_get_dashboard_stats',
        resourceType: 'dashboard_stats',
        duration: operationDuration,
        details: {
          operation: 'read',
          statsRetrieved: Object.keys(stats).length,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'dashboard_management'
      });

      return stats;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed operation
      await userActionLogger.logUserAction({
        type: 'admin_get_dashboard_stats_failed',
        resourceType: 'dashboard_stats',
        duration: operationDuration,
        details: {
          operation: 'read',
          error: this.getErrorInfo(error).message,
          statusCode: this.getErrorInfo(error).statusCode,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'dashboard_management'
      });

      throw this.handleAdminError(error);
    }
  }

  /**
   * Get system analytics - OPTIMIZED to avoid unnecessary API calls
   */
  async getAnalytics(): Promise<UserAnalyticsDto> {
    const operationStartTime = Date.now();

    try {
      // Try to get analytics from dedicated analytics endpoint first
      try {
        this.ensureAdminToken();
        const analyticsData = await apiService.get<UserAnalyticsDto>('/api/Admin/analytics', { cache: false });
        
        const operationDuration = Date.now() - operationStartTime;
        
        // Log successful analytics retrieval
        await userActionLogger.logUserAction({
          type: 'admin_get_analytics',
          resourceType: 'analytics_data',
          duration: operationDuration,
          details: {
            operation: 'read',
            source: 'analytics_endpoint',
            adminUserId: this.getCurrentAdminUserId()
          }
        }, {
          component: 'AdminService',
          feature: 'analytics_management'
        });

        return analyticsData;
      } catch (analyticsError) {
        console.log('AdminService: Analytics endpoint not available, using fallback data');
      }

      // Fallback to mock analytics data instead of fetching all users
      const usersByLevel: Record<number, number> = {
        1: 0, 2: 0, 3: 0, 4: 0, 5: 0
      };
      const usersByNationality: Record<string, number> = {
        'Unknown': 0
      };
      const usersByAgeGroup: Record<string, number> = {
        'Unknown': 0
      };

      const analytics: UserAnalyticsDto = {
        totalUsers: 0, // Will be populated when actual data is needed
        activeUsers: 0, // Will be populated when actual data is needed
        usersByLevel,
        usersByNationality,
        usersByAgeGroup
      };

      const operationDuration = Date.now() - operationStartTime;

      // Log the admin operation
      await userActionLogger.logUserAction({
        type: 'admin_get_analytics',
        resourceType: 'system_analytics',
        duration: operationDuration,
        details: {
          operation: 'read',
          totalUsers: analytics.totalUsers,
          activeUsers: analytics.activeUsers,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'analytics_management'
      });

      return analytics;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed operation
      await userActionLogger.logUserAction({
        type: 'admin_get_analytics_failed',
        resourceType: 'system_analytics',
        duration: operationDuration,
        details: {
          operation: 'read',
          error: this.getErrorInfo(error).message,
          statusCode: this.getErrorInfo(error).statusCode,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'analytics_management'
      });

      throw this.handleAdminError(error);
    }
  }

  /**
   * Get detailed user analytics with demographics
   */
  async getDetailedUserAnalytics(): Promise<UserAnalyticsDto> {
    const operationStartTime = Date.now();

    try {
      this.ensureAdminToken();

      // Try to get analytics from backend endpoint first
      try {
        console.log('AdminService: Calling /api/Admin/analytics endpoint...');
        const systemAnalytics = await apiService.get<{ userAnalytics?: UserAnalyticsDto } | UserAnalyticsDto>('/api/Admin/analytics', { cache: false });
        console.log('AdminService: Received response from /api/Admin/analytics:', systemAnalytics);
        console.log('AdminService: Response keys:', systemAnalytics ? Object.keys(systemAnalytics) : 'null');
        
        // Handle both response structures: nested { userAnalytics: {...} } or direct UserAnalyticsDto
        let analyticsData: UserAnalyticsDto | null = null;
        
        if (systemAnalytics) {
          // Check if response is nested SystemAnalyticsDto with UserAnalytics property
          const hasUserAnalytics = 'userAnalytics' in systemAnalytics || 'UserAnalytics' in systemAnalytics;
          const hasDirectStructure = 'totalUsers' in systemAnalytics && 'usersByLevel' in systemAnalytics;
          
          console.log('AdminService: Response structure check:', {
            hasUserAnalytics,
            hasDirectStructure,
            userAnalyticsKey: 'userAnalytics' in systemAnalytics ? 'userAnalytics' : ('UserAnalytics' in systemAnalytics ? 'UserAnalytics' : 'none')
          });
          
          if (hasUserAnalytics) {
            const userAnalytics = (systemAnalytics as any).userAnalytics || (systemAnalytics as any).UserAnalytics;
            console.log('AdminService: Extracted userAnalytics:', userAnalytics);
            console.log('AdminService: userAnalytics keys:', userAnalytics ? Object.keys(userAnalytics) : 'null');
            
            if (userAnalytics && ('totalUsers' in userAnalytics || 'TotalUsers' in userAnalytics) && ('usersByLevel' in userAnalytics || 'UsersByLevel' in userAnalytics)) {
              // Map backend PascalCase properties to frontend camelCase
              analyticsData = {
                totalUsers: userAnalytics.totalUsers || userAnalytics.TotalUsers || 0,
                activeUsers: userAnalytics.activeUsers || userAnalytics.ActiveUsers || 0,
                usersByLevel: userAnalytics.usersByLevel || userAnalytics.UsersByLevel || {},
                usersByNationality: userAnalytics.usersByNationality || userAnalytics.UsersByNationality || {},
                usersByLocation: userAnalytics.usersByNationality || userAnalytics.UsersByNationality || {},
                usersByAgeGroup: userAnalytics.usersByAgeGroup || userAnalytics.UsersByAgeGroup || {},
                usersByGender: userAnalytics.usersByGender || userAnalytics.UsersByGender || {},
                engagementMetrics: userAnalytics.engagementMetrics || userAnalytics.EngagementMetrics ? {
                  averageSessionDuration: userAnalytics.engagementMetrics?.averageSessionDuration || userAnalytics.EngagementMetrics?.AverageSessionDuration || 0,
                  averageActivitiesPerSession: userAnalytics.engagementMetrics?.averageActivitiesPerSession || userAnalytics.EngagementMetrics?.AverageActivitiesPerSession || 0,
                  retentionRates: {
                    day1: userAnalytics.engagementMetrics?.retentionRates?.day1 || userAnalytics.EngagementMetrics?.RetentionRates?.Day1 || 0,
                    day7: userAnalytics.engagementMetrics?.retentionRates?.day7 || userAnalytics.EngagementMetrics?.RetentionRates?.Day7 || 0,
                    day30: userAnalytics.engagementMetrics?.retentionRates?.day30 || userAnalytics.EngagementMetrics?.RetentionRates?.Day30 || 0
                  },
                  mostPopularContent: userAnalytics.engagementMetrics?.mostPopularContent || userAnalytics.EngagementMetrics?.MostPopularContent || []
                } : undefined,
                learningPatterns: userAnalytics.learningPatterns || userAnalytics.LearningPatterns || {}
              } as UserAnalyticsDto;
              console.log('AdminService: Extracted UserAnalytics from nested SystemAnalyticsDto:', {
                totalUsers: analyticsData.totalUsers,
                hasEngagementMetrics: !!analyticsData.engagementMetrics,
                engagementMetrics: analyticsData.engagementMetrics,
                hasLearningPatterns: !!analyticsData.learningPatterns,
                learningPatterns: analyticsData.learningPatterns
              });
            } else {
              console.log('AdminService: Backend UserAnalytics structure differs from expected, will calculate from users');
            }
          } 
          // Check if response is direct UserAnalyticsDto (with expected frontend structure)
          else if ('totalUsers' in systemAnalytics && 'usersByLevel' in systemAnalytics) {
            analyticsData = systemAnalytics as UserAnalyticsDto;
            console.log('AdminService: Received direct UserAnalyticsDto:', {
              totalUsers: analyticsData.totalUsers,
              hasEngagementMetrics: !!analyticsData.engagementMetrics,
              hasLearningPatterns: !!analyticsData.learningPatterns
            });
          }
        }
        
        // If we got valid analytics data with expected structure, use it
        // Check if analyticsData exists and has the required structure (even if dictionaries are empty)
        if (analyticsData && ('usersByLevel' in analyticsData || 'UsersByLevel' in analyticsData)) {
          console.log('AdminService: Received analytics from backend:', {
            totalUsers: analyticsData.totalUsers,
            activeUsers: analyticsData.activeUsers,
            hasUsersByGender: !!analyticsData.usersByGender,
            hasEngagementMetrics: !!analyticsData.engagementMetrics,
            engagementMetrics: analyticsData.engagementMetrics,
            hasLearningPatterns: !!analyticsData.learningPatterns,
            learningPatterns: analyticsData.learningPatterns
          });
          
          const operationDuration = Date.now() - operationStartTime;
          
          // Log successful analytics retrieval
          await userActionLogger.logUserAction({
            type: 'admin_get_detailed_analytics',
            resourceType: 'analytics_data',
            duration: operationDuration,
            details: {
              operation: 'read',
              source: 'backend_endpoint',
              adminUserId: this.getCurrentAdminUserId()
            }
          }, {
            component: 'AdminService',
            feature: 'analytics_management'
          });

          console.log('AdminService: Using analytics data from backend');
          return analyticsData;
        } else {
          console.log('AdminService: Backend analytics structure differs - will calculate from user data');
          console.log('AdminService: Backend response:', systemAnalytics);
        }
      } catch (analyticsError) {
        console.error('AdminService: Backend analytics endpoint error:', analyticsError);
        console.error('AdminService: Error details:', {
          message: analyticsError instanceof Error ? analyticsError.message : String(analyticsError),
          stack: analyticsError instanceof Error ? analyticsError.stack : undefined
        });
        console.log('AdminService: Falling back to calculating from user data');
      }

      // Fallback: Get all users to calculate analytics
      console.log('AdminService: Calculating analytics from user data...');
      const users = await this.getUsers();
      console.log('AdminService: Retrieved users:', users.length);

      // Calculate analytics from user data
      const totalUsers = users.length;
      const activeUsers = users.filter(user => user.isActive).length;
      console.log('AdminService: Total users:', totalUsers, 'Active users:', activeUsers);

      // Group users by level
      const usersByLevel: Record<number, number> = {};
      users.forEach(user => {
        const level = user.currentLevel || 0;
        usersByLevel[level] = (usersByLevel[level] || 0) + 1;
      });

      // Group users by nationality
      const usersByNationality: Record<string, number> = {};
      users.forEach(user => {
        const nationality = user.nationality || 'Unknown';
        usersByNationality[nationality] = (usersByNationality[nationality] || 0) + 1;
      });

      // Group users by age group
      const usersByAgeGroup: Record<string, number> = {};
      users.forEach(user => {
        let ageGroup = 'Unknown';
        if (user.age !== undefined && user.age !== null) {
          const age = user.age;
          if (age < 18) ageGroup = 'Under 18';
          else if (age < 25) ageGroup = '18-24';
          else if (age < 35) ageGroup = '25-34';
          else if (age < 45) ageGroup = '35-44';
          else if (age < 55) ageGroup = '45-54';
          else if (age < 65) ageGroup = '55-64';
          else ageGroup = '65+';
        }
        usersByAgeGroup[ageGroup] = (usersByAgeGroup[ageGroup] || 0) + 1;
      });

      // Group users by gender
      const usersByGender: Record<string, number> = {};
      users.forEach(user => {
        const gender = user.gender || 'Unknown';
        usersByGender[gender] = (usersByGender[gender] || 0) + 1;
      });

      // Calculate engagement metrics (if backend didn't provide them)
      const engagementMetrics = {
        averageSessionDuration: 0,
        averageActivitiesPerSession: 0,
        retentionRates: {
          day1: 0,
          day7: 0,
          day30: 0
        },
        mostPopularContent: []
      };

      // Calculate learning patterns (if backend didn't provide them)
      const learningPatterns = {};

      const operationDuration = Date.now() - operationStartTime;

      // Log the admin operation
      await userActionLogger.logUserAction({
        type: 'admin_get_detailed_analytics',
        resourceType: 'system_analytics',
        duration: operationDuration,
        details: {
          operation: 'read',
          totalUsers,
          activeUsers,
          source: 'calculated_from_users',
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'analytics_management'
      });

      const calculatedAnalytics = {
        totalUsers,
        activeUsers,
        usersByLevel,
        usersByNationality,
        usersByLocation: usersByNationality,
        usersByAgeGroup,
        usersByGender,
        engagementMetrics,
        learningPatterns
      };
      
      console.log('AdminService: Calculated analytics:', {
        totalUsers: calculatedAnalytics.totalUsers,
        activeUsers: calculatedAnalytics.activeUsers,
        usersByLevel: calculatedAnalytics.usersByLevel,
        usersByNationality: calculatedAnalytics.usersByNationality,
        usersByLocation: calculatedAnalytics.usersByNationality,
        usersByAgeGroup: calculatedAnalytics.usersByAgeGroup
      });
      
      return calculatedAnalytics;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed operation
      await userActionLogger.logUserAction({
        type: 'admin_get_detailed_analytics_failed',
        resourceType: 'system_analytics',
        duration: operationDuration,
        details: {
          operation: 'read',
          error: this.getErrorInfo(error).message,
          statusCode: this.getErrorInfo(error).statusCode,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'analytics_management'
      });

      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // USER MANAGEMENT METHODS
  // ============================================================================

  /**
   * Get all users
   */
  async getUsers(): Promise<AdminUserDto[]> {
    const operationStartTime = Date.now();

    try {
      this.ensureAdminToken();
      // Disable caching to ensure fresh data is always fetched
      const serverUsers = await apiService.get<ServerAdminUserDto[]>('/api/Admin/users', { cache: false });
      const mappedUsers = serverUsers.map(user => this.mapServerAdminUserToClient(user));

      const operationDuration = Date.now() - operationStartTime;

      // Log the successful admin operation
      await userActionLogger.logUserAction({
        type: 'admin_get_users',
        resourceType: 'user_list',
        duration: operationDuration,
        details: {
          operation: 'read',
          usersCount: mappedUsers.length,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'user_management'
      });

      return mappedUsers;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed admin operation
      await userActionLogger.logUserAction({
        type: 'admin_get_users_failed',
        resourceType: 'user_list',
        duration: operationDuration,
        details: {
          operation: 'read',
          error: this.getErrorInfo(error).message,
          statusCode: this.getErrorInfo(error).statusCode,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'user_management'
      });

      throw this.handleAdminError(error);
    }
  }

  /**
   * Get paginated users with filtering and sorting
   */
  async getUsersPaginated(params: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDirection?: string;
    searchTerm?: string;
    role?: string;
    isActive?: boolean;
    nationality?: string;
    commentStatus?: string;
  } = {}): Promise<{ items: AdminUserDto[], totalCount: number, page: number, pageSize: number, totalPages: number }> {
    try {
      this.ensureAdminToken();
      const queryParams = new URLSearchParams();
      
      queryParams.append('page', (params.page || 1).toString());
      queryParams.append('pageSize', (params.pageSize || 10).toString());

      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);
      if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params.role) queryParams.append('role', params.role);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
      if (params.nationality || (params as any).location) {
        queryParams.append('nationality', params.nationality || (params as any).location);
      }
      if (params.commentStatus) queryParams.append('commentStatus', params.commentStatus);

      // Always bypass cache to get fresh data from DB
      const response = await apiService.get<{
        items: ServerAdminUserDto[];
        totalCount: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>(`/api/Admin/users/paginated?${queryParams.toString()}`, {
        cache: false
      });

      // Map the server user data to client format
      return {
        items: response.items.map(user => this.mapServerAdminUserToClient(user)),
        totalCount: response.totalCount,
        page: response.page,
        pageSize: response.pageSize,
        totalPages: response.totalPages
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<AdminUserDto> {
    try {
      this.ensureAdminToken();
      // Always bypass cache to get fresh data from DB
      const serverUser = await apiService.get<ServerAdminUserDto>(`/api/Admin/users/${userId}`, {
        cache: false
      });
      return this.mapServerAdminUserToClient(serverUser);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Unified chronological path of user actions from action, security, and error logs.
   */
  async getUserActionPath(params: {
    userId: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
    sortDirection?: string;
    sourceTypes?: string;
    searchTerm?: string;
  }): Promise<Record<string, unknown>> {
    this.ensureAdminToken();
    const query = new URLSearchParams();
    if (params.startDate) query.append('startDate', params.startDate);
    if (params.endDate) query.append('endDate', params.endDate);
    if (params.page) query.append('page', String(params.page));
    if (params.pageSize) query.append('pageSize', String(params.pageSize));
    if (params.sortDirection) query.append('sortDirection', params.sortDirection);
    if (params.sourceTypes) query.append('sourceTypes', params.sourceTypes);
    if (params.searchTerm) query.append('searchTerm', params.searchTerm);
    const qs = query.toString();
    return apiService.get<Record<string, unknown>>(
      `/api/Admin/users/${encodeURIComponent(params.userId)}/action-path${qs ? `?${qs}` : ''}`,
      { cache: false }
    );
  }

  /**
   * Aggregated daily activity heatmap (persists after log cleanup).
   */
  async getUserActivityHeatmap(userId: string, days = 365, activityTimeZone?: string): Promise<unknown> {
    this.ensureAdminToken();
    const query = new URLSearchParams();
    query.append('days', String(days));
    if (activityTimeZone) query.append('activityTimeZone', activityTimeZone);
    return apiService.get(
      `/api/Admin/users/${encodeURIComponent(userId)}/activity-heatmap?${query.toString()}`,
      { cache: false }
    );
  }

  /**
   * Create new user
   */
  async createUser(userData: CreateUserDto): Promise<AdminUserDto> {
    const operationStartTime = Date.now();

    try {
      this.ensureAdminToken();
      const serverUser = await apiService.post<ServerAdminUserDto>('/api/Admin/users', userData);

      // Clear the users cache to ensure fresh data is fetched on next request
      apiService.clearCache();
      console.log('AdminService: Cache cleared after user creation');

      const mappedUser = this.mapServerAdminUserToClient(serverUser);

      // Log user creation action
      try {
        const operationDuration = Date.now() - operationStartTime;
        const adminUserId = this.getCurrentAdminUserId();

        await userActionLogger.logUserAction({
          type: 'create_user',
          resourceType: 'user_account',
          resourceId: mappedUser.id,
          userId: adminUserId || undefined,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: (await userActionLogger['getClientIP']?.()) || 'unknown',
          userAgent: navigator.userAgent,
          duration: operationDuration,
          newValue: mappedUser,
          details: {
            createdUserEmail: userData.email,
            createdUserRole: (userData as any).role || (userData.roles && userData.roles[0]) || 'user',
            adminAction: true,
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AdminService',
          feature: 'user_management'
        });

        // Log database operation
        await databaseLogger.logDatabaseOperation({
          type: 'CREATE',
          table: 'users',
          recordId: mappedUser.id,
          duration: operationDuration,
          success: true
        }, {
          userId: adminUserId || undefined,
          tags: ['user-creation', 'admin-service'],
          metadata: {
            createdFields: Object.keys(userData),
            adminOperation: true
          }
        });
      } catch (logError) {
        console.warn('AdminService: Failed to log user creation:', logError);
      }

      return mappedUser;
    } catch (error) {
      // Log failed user creation
      try {
        const adminUserId = this.getCurrentAdminUserId();
        await userActionLogger.logUserAction({
          type: 'create_user_error',
          resourceType: 'user_account',
          userId: adminUserId || undefined,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: (await userActionLogger['getClientIP']?.()) || 'unknown',
          userAgent: navigator.userAgent,
          details: {
            error: this.getErrorInfo(error).message,
            statusCode: this.getErrorInfo(error).statusCode,
            attemptedUserEmail: userData.email,
            adminAction: true,
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AdminService',
          feature: 'user_management'
        });
      } catch (logError) {
        console.warn('AdminService: Failed to log user creation error:', logError);
      }

      throw this.handleAdminError(error);
    }
  }



  /**
   * Upload profile photo for a user (admin endpoint)
   */
  async uploadUserProfilePhoto(userId: string, photoFile: File): Promise<string> {
    console.log('AdminService: uploadUserProfilePhoto called for user:', userId);

    try {
      this.ensureAdminToken();

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('Photo', photoFile);

      console.log('AdminService: Uploading photo file:', {
        name: photoFile.name,
        size: photoFile.size,
        type: photoFile.type
      });

      // Clear cache before upload to ensure fresh data after
      apiService.clearCache();

      // Use the admin endpoint to upload photo for specific user
      const response = await apiService.post<{ fileName: string; url: string; imageUrl: string }>(`/api/Admin/users/${userId}/profile-photo`, formData);

      console.log('AdminService: Photo upload successful:', response);

      // Clear cache after upload to ensure fresh data on next fetch
      apiService.clearCache();

      // Server returns imageUrl as "/uploads/profiles/filename" - return it as-is
      // The mapping function will convert it to the full URL when needed
      if (response.imageUrl) {
        return response.imageUrl; // This will be "/uploads/profiles/filename"
      } else if (response.url) {
        return response.url;
      } else if (response.fileName) {
        // Construct the path from filename
        return `/uploads/profiles/${response.fileName}`;
      }
      
      throw new Error('No photo URL returned from server');
    } catch (error) {
      console.error('AdminService: Photo upload failed:', error);
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update existing user
   */
  async updateUser(userId: string, userData: UpdateUserDto & { id?: string }): Promise<AdminUserDto> {
    console.log('AdminService: updateUser called with:', { userId, userData });
    console.log('AdminService: ProfilePhoto in userData:', {
      hasPhoto: !!userData.profilePhoto,
      hasPhotoFile: !!userData.profilePhotoFile,
      photoFileName: userData.profilePhotoFile?.name
    });

    const operationStartTime = Date.now();
    let previousUserData: AdminUserDto | undefined;

    try {
      // Ensure admin token is set before making the API call
      this.ensureAdminToken();

      // Get previous user data for logging
      try {
        previousUserData = await this.getUserById(userId);
      } catch (error) {
        console.warn('AdminService: Failed to get previous user data for logging:', error);
      }

      let profilePhotoUrl = userData.profilePhoto;

      // Handle photo file upload if provided
      if (userData.profilePhotoFile) {
        console.log('AdminService: Uploading new profile photo file');
        try {
          profilePhotoUrl = await this.uploadUserProfilePhoto(userId, userData.profilePhotoFile);
          console.log('AdminService: Photo uploaded successfully, URL:', profilePhotoUrl);
        } catch (photoError) {
          console.error('AdminService: Photo upload failed:', photoError);
          // Continue with user update even if photo upload fails
          profilePhotoUrl = userData.profilePhoto;
        }
      } else {
        // Check if user wants to remove the photo (profilePhoto is explicitly set to empty string)
        if (userData.profilePhoto === '' && previousUserData?.profilePhoto) {
          // User wants to remove photo - set to empty string
          profilePhotoUrl = '';
          console.log('AdminService: User requested photo removal');
        } else if (!profilePhotoUrl && previousUserData?.profilePhoto) {
          // If no new photo file is uploaded and no explicit removal, preserve the existing photo
          profilePhotoUrl = previousUserData.profilePhoto;
          console.log('AdminService: Preserving existing profile photo:', profilePhotoUrl);
        }
      }

      // Clean up the userData to ensure it matches server expectations
      const cleanedUserData: any = {
        id: userId, // Include the ID as required by the server
        email: userData.email?.trim(),
        userName: userData.userName?.trim() || undefined,
        firstName: userData.firstName?.trim() || undefined,
        lastName: userData.lastName?.trim() || undefined,
        phoneNumber: userData.phoneNumber?.trim() || undefined,
        bio: userData.bio?.trim() || undefined,
        nationality: userData.nationality?.trim() || userData.location?.trim() || undefined,
        dateOfBirth: userData.dateOfBirth || undefined,
        roles: userData.roles || [],
        isActive: userData.isActive
      };

      // Include profilePhoto if we have a value OR if it's explicitly empty (for removal)
      // If profilePhotoUrl is empty string, it means user wants to remove the photo
      if (profilePhotoUrl !== undefined && profilePhotoUrl !== null) {
        cleanedUserData.profileImageUrl = profilePhotoUrl;
      }

      // Remove undefined values to avoid sending null/undefined fields, but keep the ID
      Object.keys(cleanedUserData).forEach(key => {
        if (key !== 'id' && key !== 'profileImageUrl' && (cleanedUserData[key] === undefined || cleanedUserData[key] === '')) {
          delete cleanedUserData[key];
        }
      });

      // Ensure userName is not empty string if provided
      if (cleanedUserData.userName === '') {
        delete cleanedUserData.userName;
      }

      console.log('AdminService: Making PUT request to /api/Admin/users/' + userId);
      console.log('AdminService: Cleaned request payload:', cleanedUserData);
      console.log('AdminService: Previous user data:', previousUserData);

      // Generate description for logging by comparing old vs new values
      const targetEmail = cleanedUserData.email || previousUserData?.email || 'user';
      const fieldsChanged: string[] = [];
      
      // Compare each field to determine what actually changed
      if (previousUserData) {
        // Check basic fields
        if (cleanedUserData.email !== undefined && cleanedUserData.email !== previousUserData.email) {
          fieldsChanged.push('email');
        }
        if (cleanedUserData.userName !== undefined && cleanedUserData.userName !== previousUserData.userName) {
          fieldsChanged.push('userName');
        }
        if (cleanedUserData.firstName !== undefined && cleanedUserData.firstName !== previousUserData.firstName) {
          fieldsChanged.push('firstName');
        }
        if (cleanedUserData.lastName !== undefined && cleanedUserData.lastName !== previousUserData.lastName) {
          fieldsChanged.push('lastName');
        }
        if (cleanedUserData.phoneNumber !== undefined && cleanedUserData.phoneNumber !== previousUserData.phoneNumber) {
          fieldsChanged.push('phoneNumber');
        }
        if (cleanedUserData.bio !== undefined && cleanedUserData.bio !== previousUserData.bio) {
          fieldsChanged.push('bio');
        }
        if (cleanedUserData.nationality !== undefined && cleanedUserData.nationality !== previousUserData.nationality) {
          fieldsChanged.push('nationality');
        }
        if (cleanedUserData.dateOfBirth !== undefined && cleanedUserData.dateOfBirth !== previousUserData.dateOfBirth) {
          fieldsChanged.push('dateOfBirth');
        }
        
        // Check roles (compare arrays)
        if (cleanedUserData.roles !== undefined) {
          const oldRoles = (previousUserData.roles || []).sort().join(',');
          const newRoles = (cleanedUserData.roles || []).sort().join(',');
          if (oldRoles !== newRoles) {
            fieldsChanged.push('roles');
          }
        }
        
        // Check isActive
        if (cleanedUserData.isActive !== undefined && cleanedUserData.isActive !== previousUserData.isActive) {
          fieldsChanged.push('isActive');
        }
        
        // Check profile photo
        if (cleanedUserData.profileImageUrl !== undefined) {
          const oldPhoto = previousUserData.profilePhoto || '';
          const newPhoto = cleanedUserData.profileImageUrl || '';
          if (oldPhoto !== newPhoto) {
            if (newPhoto === '' || !newPhoto) {
              fieldsChanged.push('profilePhoto (removed)');
            } else if (oldPhoto === '' || !oldPhoto) {
              fieldsChanged.push('profilePhoto (added)');
            } else {
              fieldsChanged.push('profilePhoto');
            }
          }
        }
      } else {
        // If we don't have previous data, just list the fields being sent
        Object.keys(cleanedUserData).forEach(key => {
          if (key !== 'id' && cleanedUserData[key] !== undefined) {
            fieldsChanged.push(key);
          }
        });
      }
      
      let description = fieldsChanged.length > 0 
        ? `Edited user ${targetEmail} (changed: ${fieldsChanged.join(', ')})`
        : `Edited user ${targetEmail}`;
      
      // Limit description length to avoid header size limits (most servers limit headers to 8KB)
      if (description.length > 200) {
        description = description.substring(0, 197) + '...';
      }

      console.log('AdminService: Making PUT request with description:', description);
      console.log('AdminService: Fields changed:', fieldsChanged);

      // Make the PUT request with custom description header
      const serverUser = await apiService.put<ServerAdminUserDto>(
        `/api/Admin/users/${userId}`, 
        cleanedUserData,
        {
          headers: {
            'X-Action-Description': description
          }
        }
      );

      console.log('AdminService: PUT request successful, server response:', serverUser);

      // Clear the users cache to ensure fresh data is fetched on next request
      apiService.clearCache();
      console.log('AdminService: Cache cleared to ensure fresh data on next fetch');

      const mappedUser = this.mapServerAdminUserToClient(serverUser);
      console.log('AdminService: Mapped user data:', mappedUser);
      console.log('AdminService: Mapped user photo URL:', mappedUser.profilePhoto);

      // If profile photo was updated, dispatch an event to notify the user's dashboard
      // This helps the public user see the updated photo without waiting for polling
      if (cleanedUserData.profileImageUrl !== undefined) {
      }

      // Log user update action
      try {
        const operationDuration = Date.now() - operationStartTime;
        const adminUserId = this.getCurrentAdminUserId();

        await userActionLogger.logUserAction({
          type: 'update_user',
          resourceType: 'user_account',
          resourceId: userId,
          userId: adminUserId || undefined,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: (await userActionLogger['getClientIP']?.()) || 'unknown',
          userAgent: navigator.userAgent,
          duration: operationDuration,
          previousValue: previousUserData,
          newValue: mappedUser,
          details: {
            fieldsChanged: fieldsChanged,
            updatedFields: fieldsChanged,
            targetUserEmail: mappedUser.email,
            adminAction: true,
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AdminService',
          feature: 'user_management'
        });

        // Log database operation
        await databaseLogger.logDatabaseOperation({
          type: 'UPDATE',
          table: 'users',
          recordId: userId,
          duration: operationDuration,
          success: true
        }, {
          userId: adminUserId || undefined,
          tags: ['user-update', 'admin-service'],
          metadata: {
            updatedFields: Object.keys(userData),
            adminOperation: true
          }
        });
      } catch (logError) {
        console.warn('AdminService: Failed to log user update:', logError);
      }

      return mappedUser;
    } catch (error) {
      console.error('AdminService: updateUser failed with error:', error);

      // Log failed user update
      try {
        const adminUserId = this.getCurrentAdminUserId();
        await userActionLogger.logUserAction({
          type: 'update_user_error',
          resourceType: 'user_account',
          resourceId: userId,
          userId: adminUserId || undefined,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: (await userActionLogger['getClientIP']?.()) || 'unknown',
          userAgent: navigator.userAgent,
          details: {
            error: this.getErrorInfo(error).message,
            statusCode: this.getErrorInfo(error).statusCode,
            attemptedFields: Object.keys(userData),
            targetUserId: userId,
            adminAction: true,
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AdminService',
          feature: 'user_management'
        });
      } catch (logError) {
        console.warn('AdminService: Failed to log user update error:', logError);
      }

      throw this.handleAdminError(error);
    }
  }

  /**
   * Delete user
   */
  /**
   * Reset user password (admin action)
   */
  async resetUserPassword(userId: string): Promise<{ message: string }> {
    const operationStartTime = Date.now();

    try {
      this.ensureAdminToken();

      const response = await apiService.post<{ message: string }>(
        `/api/Admin/users/${userId}/reset-password`,
        {},
        { cache: false }
      );

      const operationDuration = Date.now() - operationStartTime;

      // Log the successful admin operation
      await userActionLogger.logUserAction({
        type: 'admin_reset_user_password',
        resourceType: 'user',
        resourceId: userId,
        duration: operationDuration,
        details: {
          operation: 'reset_password',
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'user_management'
      });

      return response;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed admin operation
      await userActionLogger.logUserAction({
        type: 'admin_reset_user_password_failed',
        resourceType: 'user',
        resourceId: userId,
        duration: operationDuration,
        details: {
          operation: 'reset_password',
          error: this.getErrorInfo(error).message,
          statusCode: this.getErrorInfo(error).statusCode,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'user_management'
      });

      throw this.handleAdminError(error);
    }
  }

  async deleteUser(userId: string): Promise<void> {
    const operationStartTime = Date.now();
    let userToDelete: AdminUserDto | undefined;

    try {
      this.ensureAdminToken();

      // Get user data before deletion for logging
      try {
        userToDelete = await this.getUserById(userId);
      } catch (error) {
        console.warn('AdminService: Failed to get user data before deletion for logging:', error);
      }

      await apiService.delete(`/api/Admin/users/${userId}`);

      // Clear the users cache to ensure fresh data is fetched on next request
      apiService.clearCache();
      console.log('AdminService: Cache cleared after user deletion');

      // Log user deletion action
      try {
        const operationDuration = Date.now() - operationStartTime;
        const adminUserId = this.getCurrentAdminUserId();

        await userActionLogger.logUserAction({
          type: 'delete_user',
          resourceType: 'user_account',
          resourceId: userId,
          userId: adminUserId || undefined,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: (await userActionLogger['getClientIP']?.()) || 'unknown',
          userAgent: navigator.userAgent,
          duration: operationDuration,
          previousValue: userToDelete,
          details: {
            deletedUserEmail: userToDelete?.email || 'unknown',
            deletedUserId: userId,
            adminAction: true,
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AdminService',
          feature: 'user_management'
        });

        // Log database operation
        await databaseLogger.logDatabaseOperation({
          type: 'DELETE',
          table: 'users',
          recordId: userId,
          duration: operationDuration,
          success: true
        }, {
          userId: adminUserId || undefined,
          tags: ['user-deletion', 'admin-service'],
          metadata: {
            deletedUserEmail: userToDelete?.email,
            adminOperation: true
          }
        });
      } catch (logError) {
        console.warn('AdminService: Failed to log user deletion:', logError);
      }
    } catch (error) {
      // Log failed user deletion
      try {
        const adminUserId = this.getCurrentAdminUserId();
        await userActionLogger.logUserAction({
          type: 'delete_user_error',
          resourceType: 'user_account',
          resourceId: userId,
          userId: adminUserId || undefined,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: (await userActionLogger['getClientIP']?.()) || 'unknown',
          userAgent: navigator.userAgent,
          details: {
            error: this.getErrorInfo(error).message,
            statusCode: this.getErrorInfo(error).statusCode,
            targetUserId: userId,
            adminAction: true,
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AdminService',
          feature: 'user_management'
        });
      } catch (logError) {
        console.warn('AdminService: Failed to log user deletion error:', logError);
      }

      throw this.handleAdminError(error);
    }
  }

  /**
   * Clear account lockout and reset failed login attempts
   */
  async clearUserLockout(userId: string): Promise<AdminUserDto> {
    try {
      this.ensureAdminToken();
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        apiService.setAdminToken(adminToken);
      }

      const serverUser = await apiService.post<ServerAdminUserDto>(
        `/api/Admin/users/${userId}/clear-lockout`,
        {}
      );
      return this.mapServerAdminUserToClient(serverUser);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Toggle user active status
   */
  async toggleUserStatus(userId: string): Promise<AdminUserDto> {
    const operationStartTime = Date.now();

    try {
      this.ensureAdminToken();

      console.log(`AdminService: Toggling status for user ID: ${userId}`);
      const endpoint = `/api/Admin/users/${userId}/toggle-status`;
      console.log(`AdminService: Using endpoint: ${endpoint}`);
      console.log(`AdminService: Current admin token exists:`, !!localStorage.getItem('adminToken'));
      
      // Ensure admin token is set in apiService before making the request
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        apiService.setAdminToken(adminToken);
        console.log(`AdminService: Admin token set in apiService before request`);
      }

      // Get user before toggle to know the current status
      const userBeforeToggle = await this.getUserById(userId);
      const wasActive = userBeforeToggle.isActive;
      const willBeActive = !wasActive;
      
      // Generate description for logging (before toggle, so we know what action will happen)
      const actionDescription = willBeActive 
        ? `Activated user ${userBeforeToggle.email || userId}`
        : `Deactivated user ${userBeforeToggle.email || userId}`;
      
      // Use PATCH method as specified in Swagger
      // Increased timeout to 20 seconds to handle slower database operations
      console.log(`AdminService: Making PATCH request to ${endpoint} with timeout 20000ms`);
      const serverUser = await apiService.patch<ServerAdminUserDto>(
        endpoint, 
        {},
        { 
          timeout: 20000, // 20 second timeout for toggle
          headers: {
            'X-Action-Description': actionDescription
          }
        }
      );
      console.log(`AdminService: Received response from toggle endpoint`);
      const mappedUser = this.mapServerAdminUserToClient(serverUser);

      const operationDuration = Date.now() - operationStartTime;

      // Log the successful admin operation
      const adminUserId = this.getCurrentAdminUserId();
      await userActionLogger.logUserAction({
        type: 'toggle_user_status',
        resourceType: 'user_account',
        resourceId: userId,
        userId: adminUserId || undefined,
        sessionId: userActionLogger['getSessionId']?.() || 'unknown',
        ipAddress: (await userActionLogger['getClientIP']?.()) || 'unknown',
        userAgent: navigator.userAgent,
        duration: operationDuration,
        previousValue: { email: mappedUser.email, isActive: !mappedUser.isActive },
        newValue: { email: mappedUser.email, isActive: mappedUser.isActive },
        details: {
          operation: 'toggle_status',
          targetUserEmail: mappedUser.email,
          newStatus: mappedUser.isActive,
          adminUserId: adminUserId
        }
      }, {
        component: 'AdminService',
        feature: 'user_status_management'
      });

      // Log the database operation
      await databaseLogger.logUserManagementOperation(
        'toggle_user_status',
        userId,
        operationDuration,
        true,
        undefined,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['user-status', 'admin-service'],
          metadata: {
            newStatus: mappedUser.isActive,
            endpoint: `/api/Admin/users/${userId}/toggle-status`
          }
        }
      );

      console.log(`AdminService: User toggle successful for user ${userId}:`, serverUser);
      return mappedUser;
    } catch (error: any) {
      const operationDuration = Date.now() - operationStartTime;

      console.error(`AdminService: User toggle failed for user ${userId}:`, error);
      console.error(`AdminService: Error status:`, error.response?.status);
      console.error(`AdminService: Error data:`, error.response?.data);
      console.error(`AdminService: Error config:`, error.config);

      // Log the failed admin operation
      await userActionLogger.logUserAction({
        type: 'admin_toggle_user_status_failed',
        resourceType: 'user',
        resourceId: userId,
        duration: operationDuration,
        details: {
          operation: 'toggle_status',
          error: error.message,
          statusCode: error.response?.status,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'user_status_management'
      });

      // Log the failed database operation
      await databaseLogger.logUserManagementOperation(
        'toggle_user_status',
        userId,
        operationDuration,
        false,
        error,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['user-status', 'admin-service', 'error'],
          metadata: {
            errorMessage: this.getErrorInfo(error).message,
            statusCode: this.getErrorInfo(error).statusCode,
            endpoint: `/api/Admin/users/${userId}/toggle-status`
          }
        }
      );

      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // ROLE MANAGEMENT METHODS
  // ============================================================================

  /** Normalize role DTOs from API (id casing) for consistent client usage. */
  private normalizeRoleDto(role: RoleDto & { Id?: string }): RoleDto {
    const rawId = role.id ?? role.Id;
    const normalized: RoleDto = {
      name: role.name,
      permissions: role.permissions,
      userCount: role.userCount
    };
    if (role.description !== undefined) {
      normalized.description = role.description;
    }
    if (rawId != null && String(rawId).length > 0) {
      normalized.id = String(rawId);
    }
    return normalized;
  }

  private normalizeRoleList(roles: (RoleDto & { Id?: string })[]): RoleDto[] {
    return roles.map(r => this.normalizeRoleDto(r));
  }

  private invalidateRolesCache(): void {
    apiService.clearCache();
  }

  /**
   * Get all roles
   */
  async getRoles(): Promise<RoleDto[]> {
    try {
      this.ensureAdminToken();
      const roles = await apiService.get<RoleDto[]>('/api/Admin/roles', { cache: false });
      return this.normalizeRoleList(roles);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get current user's permissions
   */
  async getMyPermissions(): Promise<{ permissions: string[]; isAdmin: boolean }> {
    try {
      this.ensureAdminToken();
      return await apiService.get<{ permissions: string[]; isAdmin: boolean }>('/api/Admin/permissions/me');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get role by ID
   */
  async getRoleById(roleId: string | number): Promise<RoleDto> {
    try {
      this.ensureAdminToken();
      const role = await apiService.get<RoleDto>(`/api/Admin/roles/${roleId}`, { cache: false });
      return this.normalizeRoleDto(role);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Create new role
   */
  async createRole(roleData: RoleDto): Promise<RoleDto> {
    try {
      this.ensureAdminToken();
      const created = await apiService.post<RoleDto>('/api/Admin/roles', roleData);
      this.invalidateRolesCache();
      return this.normalizeRoleDto(created);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update existing role
   */
  async updateRole(roleId: string | number, roleData: RoleDto): Promise<RoleDto> {
    try {
      this.ensureAdminToken();
      const id = String(roleId);
      const payload: RoleDto = {
        id,
        name: roleData.name,
        permissions: roleData.permissions ?? []
      };
      if (roleData.description !== undefined) {
        payload.description = roleData.description;
      }
      const updated = await apiService.put<RoleDto>(`/api/Admin/roles/${id}`, payload);
      this.invalidateRolesCache();
      return this.normalizeRoleDto(updated);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Delete role
   */
  async deleteRole(roleId: string | number): Promise<void> {
    try {
      this.ensureAdminToken();
      await apiService.delete(`/api/Admin/roles/${roleId}`);
      this.invalidateRolesCache();
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Assign roles to user
   */
  async assignRolesToUser(assignmentData: AssignRoleDto): Promise<void> {
    try {
      this.ensureAdminToken();
      await apiService.post('/api/Admin/users/assign-roles', assignmentData);
      this.invalidateRolesCache();
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Remove role from user
   */
  async removeRoleFromUser(userId: string, roleName: string): Promise<void> {
    try {
      this.ensureAdminToken();
      await apiService.delete(`/api/Admin/users/${userId}/roles/${roleName}`);
      this.invalidateRolesCache();
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get user roles
   */
  async getUserRoles(userId: string): Promise<RoleDto[]> {
    try {
      return await apiService.get<RoleDto[]>(`/api/Admin/users/${userId}/roles`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // ACHIEVEMENTS / GAMIFICATION (ADMIN)
  // ============================================================================

  async getAchievements(includeInactive = false): Promise<AchievementAdminDto[]> {
    try {
      return await apiService.get<AchievementAdminDto[]>(
        `/api/Admin/achievements?includeInactive=${includeInactive}`,
        { cache: false }
      );
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async getAchievement(id: number): Promise<AchievementAdminDto> {
    try {
      return await apiService.get<AchievementAdminDto>(`/api/Admin/achievements/${id}`, { cache: false });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async createAchievement(dto: CreateAchievementDto): Promise<AchievementAdminDto> {
    try {
      const payload = {
        name: dto.name,
        description: dto.description,
        iconUrl: dto.iconUrl ?? null,
        xpReward: dto.xpReward ?? 10,
        type: dto.type,
        category: dto.category,
        requiredValue: dto.requiredValue ?? 1,
        isActive: dto.isActive ?? true,
        displayOrder: dto.displayOrder ?? 0,
        profileFieldCriteria: dto.profileFieldCriteria ?? null
      };
      return await apiService.post<AchievementAdminDto>('/api/Admin/achievements', payload);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async updateAchievement(id: number, dto: UpdateAchievementDto): Promise<AchievementAdminDto> {
    try {
      return await apiService.put<AchievementAdminDto>(`/api/Admin/achievements/${id}`, dto);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async deleteAchievement(id: number): Promise<void> {
    try {
      await apiService.delete(`/api/Admin/achievements/${id}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /** Reorder achievements; order in array = display order for learners. */
  async reorderAchievements(orderedIds: number[]): Promise<void> {
    try {
      await apiService.put('/api/Admin/achievements/reorder', orderedIds);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /** Upload achievement icon (Lottie/image) to /uploads/admin-assets/ — persists like other uploads. */
  async uploadAchievementIcon(file: File): Promise<{ url: string }> {
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiService.post<{ url: string }>('/api/Admin/achievements/upload-icon', form);
      return res ?? { url: '' };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /** Upload image to admin-assets with Media Upload settings validation. */
  async uploadAdminAssetImage(file: File): Promise<{ url: string }> {
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiService.post<{ url: string }>('/api/Admin/upload-admin-asset-image', form);
      return res ?? { url: '' };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async getQuickLaunchCards(): Promise<QuickLaunchCardDto[]> {
    try {
      return await apiService.get<QuickLaunchCardDto[]>('/api/Admin/quick-launch-cards');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async updateQuickLaunchCard(
    cardKey: string,
    dto: UpdateQuickLaunchCardDto
  ): Promise<QuickLaunchCardDto> {
    try {
      return await apiService.put<QuickLaunchCardDto>(`/api/Admin/quick-launch-cards/${cardKey}`, dto);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // CONTENT MANAGEMENT METHODS
  // ============================================================================

  /**
   * Get content statistics
   */
  async getContentStats(): Promise<ContentStatisticsDto> {
    try {
      return await apiService.get<ContentStatisticsDto>('/api/Admin/content/stats');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Bulk delete content items
   */
  async bulkDeleteContent(contentType: string, contentIds: number[]): Promise<void> {
    try {
      await apiService.post(`/api/admin/content/${contentType}/bulk-delete`, { contentIds });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // PERFORMANCE AND HEALTH MONITORING METHODS - REMOVED TO PREVENT LOGOUT ISSUES
  // ============================================================================

  /**
   * Get API health status - DISABLED to prevent logout issues
   */
  async getApiHealth(): Promise<ApiHealthDto> {
    // Return mock data to prevent authentication issues
    return {
      status: 'healthy',
      timestamp: new Date().toISOString(),
      recentRequests: 0,
      averageResponseTime: 0,
      cacheHitRate: 0,
      databaseQueryCount: 0,
      failedQueries: 0
    };
  }

  /**
   * Get performance metrics - DISABLED to prevent logout issues
   */
  async getPerformanceMetrics(timeRange?: string): Promise<PerformanceMetricsDto> {
    // Return mock data to prevent authentication issues
    return {
      generatedAt: new Date().toISOString(),
      timeRange: timeRange || '24h',
      apiMetrics: {
        totalRequests: 0,
        averageResponseTime: 0,
        medianResponseTime: 0,
        p95ResponseTime: 0
      },
      databaseMetrics: {
        totalQueries: 0,
        averageQueryTime: 0,
        medianQueryTime: 0,
        failedQueries: 0
      },
      cacheMetrics: {
        totalOperations: 0,
        cacheHits: 0,
        cacheMisses: 0,
        hitRate: 0
      }
    };
  }

  // ============================================================================
  // DATA SEEDING METHODS
  // ============================================================================

  /**
   * Seed all sample data
   */
  async seedAllData(): Promise<SeedingResultDto> {
    try {
      return await apiService.post<SeedingResultDto>('/api/Seed/all');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Seed vocabulary data only
   */
  async seedVocabulary(): Promise<SeedingResultDto> {
    try {
      return await apiService.post<SeedingResultDto>('/api/Seed/vocabulary');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Seed grammar data only
   */
  async seedGrammar(): Promise<SeedingResultDto> {
    try {
      return await apiService.post<SeedingResultDto>('/api/Seed/grammar');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Seed dialogue data only
   */
  async seedDialogue(): Promise<SeedingResultDto> {
    try {
      return await apiService.post<SeedingResultDto>('/api/Seed/dialogue');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Seed quiz data only
   */
  async seedQuiz(): Promise<SeedingResultDto> {
    try {
      return await apiService.post<SeedingResultDto>('/api/Seed/quiz');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get seeding history
   */
  async getSeedingHistory(): Promise<SeedingHistoryDto[]> {
    try {
      return await apiService.get<SeedingHistoryDto[]>('/api/admin/seeding/history');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get current seeding status
   */
  async getSeedingStatus(): Promise<SeedingStatusDto> {
    try {
      return await apiService.get<SeedingStatusDto>('/api/admin/seeding/status');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Rollback seeding operation
   */
  async rollbackSeeding(operationId: string): Promise<void> {
    try {
      await apiService.post(`/api/admin/seeding/rollback/${operationId}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // SYSTEM CONFIGURATION METHODS
  // ============================================================================

  /**
   * Get system configuration
   */
  async getSystemConfig(): Promise<SystemConfigDto> {
    const operationStartTime = Date.now();

    try {
      const config = await apiService.get<SystemConfigDto>('/api/admin/config');
      const operationDuration = Date.now() - operationStartTime;

      // Log the admin operation
      await userActionLogger.logUserAction({
        type: 'admin_get_system_config',
        resourceType: 'system_configuration',
        duration: operationDuration,
        details: {
          operation: 'read',
          configSections: Object.keys(config).length,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'system_configuration'
      });

      return config;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed operation
      await userActionLogger.logUserAction({
        type: 'admin_get_system_config_failed',
        resourceType: 'system_configuration',
        duration: operationDuration,
        details: {
          operation: 'read',
          error: this.getErrorInfo(error).message,
          statusCode: this.getErrorInfo(error).statusCode,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'system_configuration'
      });

      throw this.handleAdminError(error);
    }
  }

  /**
   * Update system configuration
   */
  async updateSystemConfig(config: Partial<SystemConfigDto>): Promise<SystemConfigDto> {
    const operationStartTime = Date.now();

    try {
      const updatedConfig = await apiService.put<SystemConfigDto>('/api/admin/config', config);
      const operationDuration = Date.now() - operationStartTime;

      // Log the admin operation
      await userActionLogger.logUserAction({
        type: 'admin_update_system_config',
        resourceType: 'system_configuration',
        duration: operationDuration,
        newValue: config,
        details: {
          operation: 'update',
          updatedSections: Object.keys(config),
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'system_configuration'
      });

      // Log the database operation
      await databaseLogger.logAdminCrudOperation(
        'UPDATE',
        'system_configuration',
        'config',
        operationDuration,
        1,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['system-config', 'admin-action'],
          metadata: {
            updatedSections: Object.keys(config),
            component: 'AdminService'
          }
        }
      );

      return updatedConfig;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed operation
      await userActionLogger.logUserAction({
        type: 'admin_update_system_config_failed',
        resourceType: 'system_configuration',
        duration: operationDuration,
        details: {
          operation: 'update',
          error: error.message,
          statusCode: error.response?.status,
          attemptedSections: Object.keys(config),
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'system_configuration'
      });

      // Log the failed database operation
      await databaseLogger.logAdminCrudOperation(
        'UPDATE',
        'system_configuration',
        'config',
        operationDuration,
        0,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['system-config', 'admin-action', 'error'],
          metadata: {
            errorMessage: this.getErrorInfo(error).message,
            statusCode: this.getErrorInfo(error).statusCode,
            attemptedSections: Object.keys(config),
            component: 'AdminService'
          }
        }
      );

      throw this.handleAdminError(error);
    }
  }

  /**
   * Reset system configuration to defaults
   */
  async resetSystemConfig(): Promise<SystemConfigDto> {
    try {
      return await apiService.post<SystemConfigDto>('/api/admin/config/reset');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Test API endpoint
   */
  async testApiEndpoint(testConfig: ApiEndpointTestDto): Promise<ApiTestResultDto> {
    try {
      return await apiService.post<ApiTestResultDto>('/api/admin/test/endpoint', testConfig);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Test multiple API endpoints
   */
  async testMultipleEndpoints(testConfigs: ApiEndpointTestDto[]): Promise<ApiTestResultDto[]> {
    try {
      return await apiService.post<ApiTestResultDto[]>('/api/admin/test/endpoints/batch', testConfigs);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Validate Persian text
   */
  async validatePersianText(text: string): Promise<PersianTextValidationDto> {
    try {
      return await apiService.post<PersianTextValidationDto>('/api/admin/validate/persian-text', { text });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get error logs using new LogManagement API
   */
  async getErrorLogs(filters?: {
    logId?: number;
    level?: string;
    source?: string;
    category?: string;
    userId?: string;
    userName?: string;
    resource?: string;
    method?: string;
    ipAddress?: string;
    traceId?: string;
    startDate?: string;
    endDate?: string;
    searchTerm?: string;
    isResolved?: boolean;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{ logs: ErrorLogDtoType[], totalCount?: number }> {
    try {
      this.ensureAdminToken();

      const params = new URLSearchParams();
      if (filters) {
        // Map parameter names to API parameter names (from swagger.json)
        const paramMapping: Record<string, string> = {
          logId: 'LogId',
          level: 'Level',
          source: 'Source',
          category: 'Category',
          userId: 'UserId',
          userName: 'UserName',
          resource: 'Resource',
          method: 'Method',
          ipAddress: 'IpAddress',
          traceId: 'TraceId',
          startDate: 'StartDate',
          endDate: 'EndDate',
          searchTerm: 'SearchTerm',
          isResolved: 'IsResolved',
          page: 'Page',
          pageSize: 'PageSize',
          sortBy: 'SortBy',
          sortDirection: 'SortDirection'
        };

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            const apiKey = paramMapping[key] || key;
            params.append(apiKey, value.toString());
          }
        });
      }
      const queryString = params.toString();

      const rawResponse = await apiService.get<any>(`/api/LogManagement/error-logs${queryString ? `?${queryString}` : ''}`);

      console.log('AdminService: Raw error logs API response:', rawResponse);
      console.log('AdminService: Response type:', typeof rawResponse, 'Is array:', Array.isArray(rawResponse));

      // Handle different response formats
      let logsArray: any[] = [];

      if (Array.isArray(rawResponse)) {
        // Direct array response
        logsArray = rawResponse;
        console.log('AdminService: Direct array response with', logsArray.length, 'error logs');
      } else if (rawResponse && typeof rawResponse === 'object') {
        // Object with items array (based on your sample data)
        if (rawResponse.items && Array.isArray(rawResponse.items)) {
          logsArray = rawResponse.items;
          console.log('AdminService: Found items array with', logsArray.length, 'error logs');
        } else if (rawResponse.data && Array.isArray(rawResponse.data)) {
          logsArray = rawResponse.data;
          console.log('AdminService: Found data array with', logsArray.length, 'error logs');
        } else {
          console.log('AdminService: Response object structure:', Object.keys(rawResponse));
        }
      }

      if (logsArray.length === 0) {
        console.log('AdminService: No error logs found in response');
        return { logs: [], totalCount: 0 };
      }

      // Transform raw API response to ErrorLogDto format
      const transformedLogs = logsArray.map((rawLog: any, index: number) => {
        console.log('AdminService: Transforming log:', rawLog);
        return {
          id: rawLog.id ? rawLog.id.toString() : `error-${Date.now()}-${index}`, // Convert ID to string
          timestamp: rawLog.createdAt || rawLog.timestamp || new Date().toISOString(), // Use createdAt from your sample
          level: rawLog.level ? rawLog.level.toLowerCase() : 'error', // Convert to lowercase
          message: rawLog.message || 'No message',
          source: rawLog.source || 'Unknown',
          category: rawLog.category,
          userId: rawLog.userId,
          ipAddress: rawLog.ipAddress,
          traceId: rawLog.traceId,
          stackTrace: rawLog.stackTrace,
          resolved: rawLog.isResolved || rawLog.resolved || false, // Use isResolved from your sample
          details: rawLog.details,
          exceptionType: rawLog.exceptionType,
          userAgent: rawLog.userAgent,
          requestPath: rawLog.requestPath,
          httpMethod: rawLog.httpMethod,
          endpoint: rawLog.requestPath, // Map requestPath to endpoint
          tags: [],
          metadata: {
            userAgent: rawLog.userAgent,
            httpMethod: rawLog.httpMethod,
            requestPath: rawLog.requestPath,
            exceptionType: rawLog.exceptionType,
            details: rawLog.details,
            userEmail: rawLog.userEmail,
            resolvedAt: rawLog.resolvedAt,
            resolvedBy: rawLog.resolvedBy,
            resolutionNotes: rawLog.resolutionNotes
          }
        } as ErrorLogDtoType;
      });

      console.log('AdminService: Transformed', transformedLogs.length, 'error logs');

      // Return logs with pagination info if available
      const result = {
        logs: transformedLogs,
        totalCount: rawResponse.totalCount || rawResponse.total || transformedLogs.length
      };

      console.log('AdminService: Returning result:', result);
      return result;
    } catch (apiError: any) {
      console.error('LogManagement error-logs API failed:', apiError);
      console.error('API Error details:', {
        message: apiError.message,
        status: apiError.response?.status,
        statusText: apiError.response?.statusText,
        data: apiError.response?.data
      });

      // No mock data - let the real error be thrown to see what's happening

      throw new Error(`Failed to load error logs: ${apiError.message || 'API not available'}`);
    }
  }



  /**
   * Toggle error log resolved state using LogManagement API
   * The API toggles the resolved state each time it's called
   */
  async toggleErrorLogResolved(logId: string, currentlyResolved: boolean, notes?: string): Promise<void> {
    try {
      const body = {
        errorLogId: parseInt(logId, 10),
        resolutionNotes: notes || (currentlyResolved ? "Marked as unresolved" : "Marked as resolved")
      };
      await apiService.patch(`/api/LogManagement/error-logs/${logId}/resolve`, body);
    } catch (error) {
      console.warn('LogManagement resolve API not available, operation ignored:', error);
      // Silently ignore for development - in production this would be handled by the backend
    }
  }

  /**
   * Mark error log as resolved using new LogManagement API
   * @deprecated Use toggleErrorLogResolved instead
   */
  async resolveErrorLog(logId: string, notes?: string): Promise<void> {
    try {
      const body = {
        errorLogId: parseInt(logId, 10),
        resolutionNotes: notes || "Marked as resolved"
      };
      await apiService.patch(`/api/LogManagement/error-logs/${logId}/resolve`, body);
    } catch (error) {
      console.warn('LogManagement resolve API not available, operation ignored:', error);
      // Silently ignore for development - in production this would be handled by the backend
    }
  }

  /**
   * Mark error log as unresolved using LogManagement API
   * @deprecated Use toggleErrorLogResolved instead
   */
  async unresolveErrorLog(logId: string): Promise<void> {
    try {
      const body = {
        errorLogId: parseInt(logId, 10),
        resolutionNotes: "Marked as unresolved"
      };
      await apiService.patch(`/api/LogManagement/error-logs/${logId}/resolve`, body);
    } catch (error) {
      console.warn('LogManagement unresolve API not available, operation ignored:', error);
      // Silently ignore for development - in production this would be handled by the backend
    }
  }

  /**
   * Clear error logs using new LogManagement bulk delete API
   */
  async clearErrorLogs(filters?: {
    level?: string;
    source?: string;
    olderThan?: string;
    resolved?: boolean;
    logIds?: string[];
  }): Promise<void> {
    try {
      const body = {
        logIds: filters?.logIds || [],
        filters: {
          level: filters?.level,
          source: filters?.source,
          olderThan: filters?.olderThan,
          resolved: filters?.resolved
        }
      };
      await apiService.post('/api/LogManagement/error-logs/bulk-delete', body);
    } catch (error) {
      console.warn('LogManagement bulk delete API not available, operation ignored:', error);
      // Silently ignore for development - in production this would be handled by the backend
    }
  }

  /**
   * Bulk resolve error logs using new LogManagement API
   */
  async bulkResolveErrorLogs(logIds: string[], notes?: string): Promise<void> {
    try {
      const body = {
        logIds,
        notes
      };
      await apiService.patch('/api/LogManagement/error-logs/bulk-resolve', body);
    } catch (error) {
      console.warn('LogManagement bulk resolve API not available, operation ignored:', error);
      // Silently ignore for development - in production this would be handled by the backend
    }
  }

  /**
   * Get error logs statistics using new LogManagement API
   */
  async getErrorLogsStats(params?: EnhancedLogQueryParams): Promise<any> {
    try {
      this.ensureAdminToken();

      const queryParams = new URLSearchParams();
      if (params) {
        // Map parameters to API parameter names
        const paramMapping: Record<string, string> = {
          logId: 'LogId',
          level: 'Level',
          source: 'Source',
          category: 'Category',
          userId: 'UserId',
          userName: 'UserName',
          resource: 'Resource',
          method: 'Method',
          httpMethod: 'Method', // Also handle httpMethod from ErrorLogsTab
          ipAddress: 'IpAddress',
          traceId: 'TraceId',
          startDate: 'StartDate',
          endDate: 'EndDate',
          searchTerm: 'SearchTerm',
          isResolved: 'IsResolved',
          page: 'Page',
          pageSize: 'PageSize',
          sortBy: 'SortBy',
          sortDirection: 'SortDirection'
        };

        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            const apiKey = paramMapping[key] || key;
            queryParams.append(apiKey, value.toString());
          }
        });
      }

      const queryString = queryParams.toString();
      const response = await apiService.get<any>(`/api/LogManagement/error-logs/stats${queryString ? `?${queryString}` : ''}`);

      return response;
    } catch (error: any) {
      console.error('Failed to get error logs stats:', error);
      console.error('Stats endpoint error details:', {
        url: '/api/LogManagement/error-logs/stats',
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText
      });

      // Return mock data structure for development
      return {
        totalErrors: 0,
        resolvedErrors: 0,
        unresolvedErrors: 0,
        errorsByLevel: {},
        errorsBySource: {},
        errorsByCategory: {},
        topErrors: [],
        errorTrends: [],
        resolutionRate: 0
      };
    }
  }

  /**
   * Delete a single error log using new LogManagement API
   */
  async deleteErrorLog(logId: string): Promise<void> {
    try {
      await apiService.delete(`/api/LogManagement/error-logs/${logId}`);
    } catch (error) {
      console.warn('LogManagement delete API not available, operation ignored:', error);
      // Silently ignore for development - in production this would be handled by the backend
    }
  }

  /**
   * Bulk delete error logs using new LogManagement API
   */
  async bulkDeleteErrorLogs(logIds: string[]): Promise<void> {
    try {
      await this.ensureValidAdminToken();
      
      // Convert string IDs to integers as expected by API
      const numericIds = logIds.map(id => {
        const numericId = parseInt(id, 10);
        if (isNaN(numericId)) {
          throw new Error(`Invalid log ID format: ${id}`);
        }
        return numericId;
      });

      if (numericIds.length === 0) {
        throw new Error('No valid log IDs provided for deletion');
      }

      console.log('AdminService: Bulk deleting error logs:', numericIds);

      const bulkOperation = {
        LogIds: numericIds  // Note: capital L to match backend DTO
      };

      // Use fetch directly for DELETE with body since apiService.delete doesn't support body properly
      const baseUrl = apiService.getBaseURL();
      const token = localStorage.getItem('adminToken');

      const response = await fetch(`${baseUrl}/api/LogManagement/error-logs/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bulkOperation)
      });

      console.log('AdminService: Bulk delete error logs response status:', response.status);

      // Read response body once
      const responseText = await response.text();
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { message: responseText || `HTTP ${response.status}: ${response.statusText}` };
        }
        const errorMessage = errorData.message || errorData.details?.validationSummary || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      // Log successful response
      if (responseText) {
        console.log('AdminService: Bulk delete error logs response:', responseText);
      }
    } catch (error) {
      console.error('AdminService: Failed to bulk delete error logs:', error);
      throw this.handleAdminError(error);
    }
  }



  /**
   * Get system diagnostics - DISABLED to prevent logout issues
   */
  async getSystemDiagnostics(): Promise<SystemDiagnosticsDto> {
    // Return mock data to prevent authentication issues
    return {
      systemHealth: 'healthy',
      apiConnectivity: true,
      databaseConnectivity: true,
      cacheConnectivity: true,
      diskSpace: {
        total: '100GB',
        used: '50GB',
        available: '50GB',
        percentage: 50
      },
      memory: {
        total: '8GB',
        used: '4GB',
        available: '4GB',
        percentage: 50
      },
      uptime: '24h',
      lastHealthCheck: new Date().toISOString(),
      issues: []
    };
  }

  /**
   * Run system health check - DISABLED to prevent logout issues
   */
  async runHealthCheck(): Promise<SystemDiagnosticsDto> {
    // Return mock data to prevent authentication issues
    return {
      systemHealth: 'healthy',
      apiConnectivity: true,
      databaseConnectivity: true,
      cacheConnectivity: true,
      diskSpace: {
        total: '100GB',
        used: '50GB',
        available: '50GB',
        percentage: 50
      },
      memory: {
        total: '8GB',
        used: '4GB',
        available: '4GB',
        percentage: 50
      },
      uptime: '24h',
      lastHealthCheck: new Date().toISOString(),
      issues: []
    };
  }

  // ============================================================================
  // SUBSCRIPTION MANAGEMENT METHODS
  // ============================================================================

  /**
   * Get all subscription plans for admin management
   */
  async getSubscriptionPlans(): Promise<SubscriptionPlanDto[]> {
    try {
      const plans = await apiService.get<any[]>('/api/Admin/subscription-plans', {
        cache: false
      });

      return plans.map(plan => {
        const durationValue = plan.duration ?? plan.durationDays ?? plan.duration_days ?? 0;
        return {
          id: plan.id,
          name: plan.name,
          duration: durationValue,
          durationDays: plan.durationDays ?? durationValue,
          price: plan.price,
          description: plan.description,
          icon: plan.icon,
          color: plan.color,
          isActive: plan.isActive,
          createdAt: plan.createdAt
        };
      });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async getXpRedemptionAudit(limit = 100, userId?: string): Promise<XpRedemptionHistoryAdminDto[]> {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (userId) params.set('userId', userId);
      return await apiService.get<XpRedemptionHistoryAdminDto[]>(
        `/api/Admin/xp-redemptions?${params.toString()}`,
        { cache: false }
      );
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async getXpSubscriptionOffers(): Promise<XpSubscriptionOfferDto[]> {
    try {
      return await apiService.get<XpSubscriptionOfferDto[]>('/api/Admin/xp-subscription-offers', { cache: false });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async createXpSubscriptionOffer(data: CreateUpdateXpSubscriptionOfferDto): Promise<XpSubscriptionOfferDto> {
    try {
      return await apiService.post<XpSubscriptionOfferDto>('/api/Admin/xp-subscription-offers', data);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async updateXpSubscriptionOffer(offerId: number, data: CreateUpdateXpSubscriptionOfferDto): Promise<XpSubscriptionOfferDto> {
    try {
      return await apiService.put<XpSubscriptionOfferDto>(`/api/Admin/xp-subscription-offers/${offerId}`, data);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async deleteXpSubscriptionOffer(offerId: number): Promise<void> {
    try {
      await apiService.delete(`/api/Admin/xp-subscription-offers/${offerId}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Create new subscription plan
   */
  async createSubscriptionPlan(planData: CreateSubscriptionPlanDto): Promise<SubscriptionPlanDto> {
    try {
      return await apiService.post<SubscriptionPlanDto>(
        '/api/Admin/subscription-plans',
        planData,
        { retries: 0, timeout: 45000 }
      );
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update existing subscription plan
   */
  async updateSubscriptionPlan(planId: number, planData: UpdateSubscriptionPlanDto): Promise<SubscriptionPlanDto> {
    try {
      return await apiService.put<SubscriptionPlanDto>(`/api/Admin/subscription-plans/${planId}`, planData);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Delete subscription plan
   */
  async deleteSubscriptionPlan(planId: number): Promise<void> {
    try {
      await apiService.delete(`/api/Admin/subscription-plans/${planId}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get default trial settings
   */
  async getDefaultTrialSettings(): Promise<DefaultTrialSettingsDto> {
    try {
      return await apiService.get<DefaultTrialSettingsDto>('/api/Admin/trial-settings/default');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update default trial settings
   */
  async updateDefaultTrialSettings(settings: UpdateDefaultTrialSettingsDto): Promise<DefaultTrialSettingsDto> {
    try {
      return await apiService.put<DefaultTrialSettingsDto>('/api/Admin/trial-settings/default', settings);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get user trial settings
   */
  async getUserTrialSettings(userId: string): Promise<UserTrialSettingsDto | null> {
    try {
      return await apiService.get<UserTrialSettingsDto>(`/api/Admin/users/${userId}/trial-settings`);
    } catch (error) {
      if ((error as any)?.status === 404) {
        return null;
      }
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update user trial settings
   */
  async updateUserTrialSettings(settings: UpdateUserTrialSettingsDto): Promise<UserTrialSettingsDto> {
    try {
      return await apiService.put<UserTrialSettingsDto>('/api/Admin/users/trial-settings', settings);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Assign subscription plan to user
   */
  async assignSubscriptionPlanToUser(assignDto: AssignSubscriptionPlanDto): Promise<UserSubscriptionDto> {
    try {
      return await apiService.post<UserSubscriptionDto>('/api/Admin/users/assign-subscription', assignDto);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get system settings including trial configuration
   */
  async getSystemSettings(): Promise<SystemSettings> {
    try {
      this.ensureAdminToken();
      // Add cache busting to ensure fresh data
      return await apiService.get<SystemSettings>('/api/Admin/settings', { cache: false });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update system settings including trial configuration
   */
  async updateSystemSettings(settings: Partial<SystemSettings>): Promise<SystemSettings> {
    try {
      return await apiService.put<SystemSettings>('/api/Admin/settings', settings);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get static site page contents (About, Contact, Privacy, Terms)
   */
  async getStaticPages(): Promise<StaticPagesMap> {
    try {
      this.ensureAdminToken();
      const raw = await apiService.get<unknown>('/api/Admin/static-pages', { cache: false });
      return normalizeStaticPagesMap(raw);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update static site page contents
   */
  async updateStaticPages(contents: StaticPagesMap): Promise<void> {
    try {
      await apiService.put('/api/Admin/static-pages', contents);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async updateStaticPages(contents: StaticPagesMap): Promise<void> {
    try {
      await apiService.put('/api/Admin/static-pages', contents);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  private normalizeAppReleaseDto(raw: unknown): AppReleaseDto {
    const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const changesFaRaw = source.changesFa ?? source.ChangesFa;
    return {
      id: Number(source.id ?? source.Id ?? 0),
      versionLabel: String(source.versionLabel ?? source.VersionLabel ?? ''),
      title: (source.title ?? source.Title) as string | null | undefined,
      titleFa: (source.titleFa ?? source.TitleFa) as string | null | undefined,
      summary: String(source.summary ?? source.Summary ?? ''),
      summaryFa: (source.summaryFa ?? source.SummaryFa) as string | null | undefined,
      heroImageUrl: (source.heroImageUrl ?? source.HeroImageUrl) as string | null | undefined,
      changes: normalizeReleaseChanges(source.changes ?? source.Changes),
      changesFa: changesFaRaw != null ? normalizeReleaseChanges(changesFaRaw) : null,
      releasedAt: String(source.releasedAt ?? source.ReleasedAt ?? ''),
      isPublished: Boolean(source.isPublished ?? source.IsPublished),
      pwaNotificationActive: Boolean(source.pwaNotificationActive ?? source.PwaNotificationActive),
      createdAt: String(source.createdAt ?? source.CreatedAt ?? ''),
      updatedAt: (source.updatedAt ?? source.UpdatedAt) as string | null | undefined,
    };
  }

  async getAppReleases(): Promise<AppReleaseDto[]> {
    try {
      this.ensureAdminToken();
      const rows = await apiService.get<unknown[]>('/api/Admin/app-releases', { cache: false });
      return (rows ?? []).map((row) => this.normalizeAppReleaseDto(row));
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async createAppRelease(payload: CreateAppReleasePayload): Promise<AppReleaseDto> {
    try {
      this.ensureAdminToken();
      const created = await apiService.post<unknown>('/api/Admin/app-releases', payload);
      return this.normalizeAppReleaseDto(created);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async updateAppRelease(id: number, payload: UpdateAppReleasePayload): Promise<AppReleaseDto> {
    try {
      this.ensureAdminToken();
      const updated = await apiService.put<unknown>(`/api/Admin/app-releases/${id}`, payload);
      return this.normalizeAppReleaseDto(updated);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async deleteAppRelease(id: number): Promise<void> {
    try {
      this.ensureAdminToken();
      await apiService.delete(`/api/Admin/app-releases/${id}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async announcePwaRelease(id: number): Promise<AnnouncePwaReleaseResult> {
    try {
      this.ensureAdminToken();
      return await apiService.post<AnnouncePwaReleaseResult>(`/api/Admin/app-releases/${id}/announce-pwa`, {});
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async deactivatePwaRelease(id: number): Promise<void> {
    try {
      this.ensureAdminToken();
      await apiService.post(`/api/Admin/app-releases/${id}/deactivate-pwa`, {});
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async getPwaAnnouncementSettings(): Promise<{ updateReminderSeconds: number }> {
    try {
      this.ensureAdminToken();
      const raw = await apiService.get<Record<string, unknown>>('/api/Admin/app-releases/pwa-settings', {
        cache: false,
      });
      return {
        updateReminderSeconds: Number(
          raw.updateReminderSeconds ?? raw.UpdateReminderSeconds ?? 300
        ),
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async updatePwaAnnouncementSettings(updateReminderSeconds: number): Promise<{ updateReminderSeconds: number }> {
    try {
      this.ensureAdminToken();
      const raw = await apiService.put<Record<string, unknown>>('/api/Admin/app-releases/pwa-settings', {
        updateReminderSeconds,
      });
      return {
        updateReminderSeconds: Number(
          raw.updateReminderSeconds ?? raw.UpdateReminderSeconds ?? updateReminderSeconds
        ),
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get security settings (now part of unified settings)
   */
  async getSecuritySettings(): Promise<UpdateSecuritySettingsDto> {
    try {
      const allSettings = await apiService.get<any>('/api/Admin/settings');
      console.log('AdminService: getSecuritySettings - raw response:', allSettings);

      // Extract security settings from unified response - handle both nested and flat structures
      const securitySettings = {
        tokenExpirationMinutes: allSettings.securitySettings?.tokenExpirationMinutes || allSettings.tokenExpirationMinutes,
        maxLoginAttempts: allSettings.securitySettings?.maxLoginAttempts || allSettings.maxLoginAttempts,
        captchaAfterFailedAttempts: allSettings.securitySettings?.captchaAfterFailedAttempts || allSettings.captchaAfterFailedAttempts || 1,
        lockoutDurationMinutes: allSettings.securitySettings?.lockoutDurationMinutes || allSettings.lockoutDurationMinutes || 15,
      };

      console.log('AdminService: getSecuritySettings - extracted settings:', securitySettings);
      return securitySettings;
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update security settings using new unified endpoint
   */
  async updateSecuritySettings(settings: UpdateSecuritySettingsDto): Promise<UpdateSecuritySettingsDto> {
    try {
      return await apiService.put<UpdateSecuritySettingsDto>('/api/Admin/settings/security', settings);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update all settings using unified endpoint
   */
  async updateAllSettings(settings: UpdateCombinedSettingsDto): Promise<UpdateCombinedSettingsDto> {
    try {
      console.log('AdminService: updateAllSettings - sending data:', settings);
      const response = await apiService.put<UpdateCombinedSettingsDto>('/api/Admin/settings', settings);
      console.log('AdminService: updateAllSettings - received response:', response);
      return response;
    } catch (error) {
      console.error('AdminService: updateAllSettings - error:', error);
      throw this.handleAdminError(error);
    }
  }

  /**
   * Initialize user trial
   */
  async initializeUserTrial(userId: string, trialDays: number): Promise<void> {
    try {
      await apiService.post(`/api/Admin/users/${userId}/trial`, { trialDays });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Extend user trial
   */
  async extendUserTrial(userId: string, additionalDays: number, reason?: string): Promise<void> {
    try {
      await apiService.post(`/api/Admin/users/${userId}/extend-trial`, {
        additionalDays,
        reason
      });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get subscription analytics
   */
  async getSubscriptionAnalytics(): Promise<SubscriptionAnalyticsDto> {
    try {
      return await apiService.get<SubscriptionAnalyticsDto>('/api/Admin/subscription-analytics');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get user subscription overview for admin management
   */
  async getUserSubscriptionOverview(): Promise<UserSubscriptionOverviewDto[]> {
    try {
      return await apiService.get<UserSubscriptionOverviewDto[]>('/api/Admin/users/subscription-overview');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // SYSTEM MANAGEMENT METHODS
  // ============================================================================


  // ============================================================================
  // POEM MANAGEMENT METHODS
  // ============================================================================

  /**
   * Get all poems with pagination and filtering (Admin only)
   */
  async getPoems(pageNumber: number = 1, pageSize: number = 20, category?: string, isActive?: boolean, forceRefresh?: boolean): Promise<PoemDto[]> {
    try {
      this.ensureAdminToken();
      const params = new URLSearchParams();
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      if (category) params.append('category', category);
      if (isActive !== undefined) params.append('isActive', isActive.toString());

      // Add cache busting parameter when forcing refresh
      if (forceRefresh) {
        params.append('_t', Date.now().toString());
      }

      return await apiService.get<PoemDto[]>(`/api/Admin/poems?${params.toString()}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get active poems for public use (no authentication required)
   */
  async getPublicPoems(pageNumber: number = 1, pageSize: number = 100): Promise<PoemDto[]> {
    try {
      const params = new URLSearchParams();
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      params.append('isActive', 'true'); // Only get active poems for public use

      // Try public endpoint first
      try {
        return await apiService.get<PoemDto[]>(`/api/poems?${params.toString()}`);
      } catch (publicError) {
        console.log('Public poems endpoint not available, trying admin endpoint...');

        // Try admin endpoint if public endpoint fails and we have admin token
        try {
          const adminToken = localStorage.getItem('adminToken');
          if (adminToken) {
            console.log('Trying admin endpoint with authentication...');
            this.ensureAdminToken();
            const result = await apiService.get<PoemDto[]>(`/api/Admin/poems?${params.toString()}`);
            console.log('Successfully loaded poems from admin endpoint:', result.length);
            return result;
          } else {
            console.log('No admin token available, using fallback poems');
            return [];
          }
        } catch (adminError) {
          console.log('Admin poems endpoint failed:', adminError);
          return [];
        }
      }
    } catch (error) {
      console.error('Failed to load public poems:', error);
      return []; // Return empty array instead of throwing error
    }
  }

  /**
   * Get poem by ID
   */
  async getPoemById(poemId: number): Promise<PoemDto> {
    try {
      this.ensureAdminToken();
      return await apiService.get<PoemDto>(`/api/Admin/poems/${poemId}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Create a new poem
   */
  async createPoem(poemData: CreatePoemDto): Promise<PoemDto> {
    const operationStartTime = Date.now();

    try {
      this.ensureAdminToken();
      const poem = await apiService.post<PoemDto>('/api/Admin/poems', poemData);
      const operationDuration = Date.now() - operationStartTime;

      // Log the admin operation
      await userActionLogger.logUserAction({
        type: 'admin_create_poem',
        resourceType: 'poem',
        resourceId: poem.id.toString(),
        duration: operationDuration,
        newValue: poem,
        details: {
          operation: 'create',
          poemTitle: poemData.title,
          poemAuthor: poemData.author,
          poemCategory: poemData.category,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'content_management'
      });

      // Log the database operation
      await databaseLogger.logContentManagementOperation(
        'CREATE',
        'poems',
        poem.id.toString(),
        operationDuration,
        true,
        undefined,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['poem-creation', 'content-management'],
          metadata: {
            poemTitle: poemData.title,
            poemAuthor: poemData.author,
            poemCategory: poemData.category,
            component: 'AdminService'
          }
        }
      );

      return poem;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed operation
      await userActionLogger.logUserAction({
        type: 'admin_create_poem_failed',
        resourceType: 'poem',
        duration: operationDuration,
        details: {
          operation: 'create',
          error: error.message,
          statusCode: error.response?.status,
          poemTitle: poemData.title,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'content_management'
      });

      // Log the failed database operation
      await databaseLogger.logContentManagementOperation(
        'CREATE',
        'poems',
        'unknown',
        operationDuration,
        false,
        error,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['poem-creation', 'content-management', 'error'],
          metadata: {
            errorMessage: this.getErrorInfo(error).message,
            statusCode: this.getErrorInfo(error).statusCode,
            poemTitle: poemData.title,
            component: 'AdminService'
          }
        }
      );

      throw this.handleAdminError(error);
    }
  }

  /**
   * Update an existing poem
   */
  async updatePoem(poemId: number, poemData: UpdatePoemDto): Promise<PoemDto> {
    const operationStartTime = Date.now();

    try {
      this.ensureAdminToken();
      const poem = await apiService.put<PoemDto>(`/api/Admin/poems/${poemId}`, poemData);
      const operationDuration = Date.now() - operationStartTime;

      // Log the admin operation
      await userActionLogger.logUserAction({
        type: 'admin_update_poem',
        resourceType: 'poem',
        resourceId: poemId.toString(),
        duration: operationDuration,
        newValue: poem,
        details: {
          operation: 'update',
          poemId,
          updatedFields: Object.keys(poemData),
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'content_management'
      });

      // Log the database operation
      await databaseLogger.logContentManagementOperation(
        'UPDATE',
        'poems',
        poemId.toString(),
        operationDuration,
        true,
        undefined,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['poem-update', 'content-management'],
          metadata: {
            poemId,
            updatedFields: Object.keys(poemData),
            component: 'AdminService'
          }
        }
      );

      return poem;
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed operation
      await userActionLogger.logUserAction({
        type: 'admin_update_poem_failed',
        resourceType: 'poem',
        resourceId: poemId.toString(),
        duration: operationDuration,
        details: {
          operation: 'update',
          error: error.message,
          statusCode: error.response?.status,
          poemId,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'content_management'
      });

      // Log the failed database operation
      await databaseLogger.logContentManagementOperation(
        'UPDATE',
        'poems',
        poemId.toString(),
        operationDuration,
        false,
        error,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['poem-update', 'content-management', 'error'],
          metadata: {
            errorMessage: this.getErrorInfo(error).message,
            statusCode: this.getErrorInfo(error).statusCode,
            poemId,
            component: 'AdminService'
          }
        }
      );

      throw this.handleAdminError(error);
    }
  }

  /**
   * Delete a poem
   */
  async deletePoem(poemId: number): Promise<void> {
    const operationStartTime = Date.now();

    try {
      this.ensureAdminToken();
      await apiService.delete(`/api/Admin/poems/${poemId}`);
      const operationDuration = Date.now() - operationStartTime;

      // Log the admin operation
      await userActionLogger.logUserAction({
        type: 'admin_delete_poem',
        resourceType: 'poem',
        resourceId: poemId.toString(),
        duration: operationDuration,
        details: {
          operation: 'delete',
          poemId,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'content_management'
      });

      // Log the database operation
      await databaseLogger.logContentManagementOperation(
        'DELETE',
        'poems',
        poemId.toString(),
        operationDuration,
        true,
        undefined,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['poem-deletion', 'content-management', 'critical'],
          metadata: {
            poemId,
            component: 'AdminService'
          }
        }
      );
    } catch (error) {
      const operationDuration = Date.now() - operationStartTime;

      // Log the failed operation
      await userActionLogger.logUserAction({
        type: 'admin_delete_poem_failed',
        resourceType: 'poem',
        resourceId: poemId.toString(),
        duration: operationDuration,
        details: {
          operation: 'delete',
          error: error.message,
          statusCode: error.response?.status,
          poemId,
          adminUserId: this.getCurrentAdminUserId()
        }
      }, {
        component: 'AdminService',
        feature: 'content_management'
      });

      // Log the failed database operation
      await databaseLogger.logContentManagementOperation(
        'DELETE',
        'poems',
        poemId.toString(),
        operationDuration,
        false,
        error,
        {
          userId: this.getCurrentAdminUserId(),
          tags: ['poem-deletion', 'content-management', 'error'],
          metadata: {
            errorMessage: this.getErrorInfo(error).message,
            statusCode: this.getErrorInfo(error).statusCode,
            poemId,
            component: 'AdminService'
          }
        }
      );

      throw this.handleAdminError(error);
    }
  }

  /**
   * Toggle poem active status
   */
  async togglePoemStatus(poemId: number): Promise<PoemDto> {
    try {
      this.ensureAdminToken();

      console.log(`AdminService: Toggling status for poem ID: ${poemId}`);
      console.log(`AdminService: Using endpoint: /api/Admin/poems/${poemId}/toggle-status`);
      console.log(`AdminService: Current admin token exists:`, !!localStorage.getItem('adminToken'));

      // Use PATCH method as specified in Swagger
      const result = await apiService.patch<PoemDto>(`/api/Admin/poems/${poemId}/toggle-status`, {});

      console.log(`AdminService: Toggle successful for poem ${poemId}:`, result);
      return result;
    } catch (error: any) {
      console.error(`AdminService: Toggle failed for poem ${poemId}:`, error);
      console.error(`AdminService: Error status:`, error.response?.status);
      console.error(`AdminService: Error data:`, error.response?.data);
      console.error(`AdminService: Error config:`, error.config);

      throw this.handleAdminError(error);
    }
  }

  /**
   * Get poem categories
   */
  async getPoemCategories(): Promise<PoemCategoryDto[]> {
    try {
      this.ensureAdminToken();
      return await apiService.get<PoemCategoryDto[]>('/api/Admin/poems/categories');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Check if current user has specific admin role
   */
  hasAdminRole(role: string): boolean {
    const adminUser = this.getCurrentAdminUser();
    return adminUser?.roles.includes(role) || false;
  }

  /**
   * Check if current user has any of the specified admin roles
   */
  hasAnyAdminRole(roles: string[]): boolean {
    const adminUser = this.getCurrentAdminUser();
    if (!adminUser) return false;
    return roles.some(role => adminUser.roles.includes(role));
  }

  /**
   * Check if current user has all of the specified admin roles
   */
  hasAllAdminRoles(roles: string[]): boolean {
    const adminUser = this.getCurrentAdminUser();
    if (!adminUser) return false;
    return roles.every(role => adminUser.roles.includes(role));
  }

  /**
   * Ensure valid admin token with intelligent validation and minimal session clearing
   * This method handles comprehensive token validation without aggressive logout behavior
   */
  private async ensureValidAdminToken(): Promise<string> {
    const adminToken = localStorage.getItem('adminToken');

    if (!adminToken) {
      throw new Error('Admin authentication required. Please log in again.');
    }

    // Check local token expiration first
    const adminTokenInfo = localStorage.getItem('adminTokenInfo');
    if (adminTokenInfo) {
      try {
        const tokenInfo = JSON.parse(adminTokenInfo);
        if (tokenInfo.expiresAt) {
          const expirationTime = new Date(tokenInfo.expiresAt).getTime();
          const currentTime = Date.now();
          const timeUntilExpiry = expirationTime - currentTime;

          // Token Expiration elapsed with no admin activity: go to admin login.
          // If the admin is still working, extend the token instead.
          if (timeUntilExpiry <= 0) {
            const { adminMaySilentlyExtendSession, enforceAdminIdleTimeout } = await import('../utils/sessionExpirationPolicy');
            if (!adminMaySilentlyExtendSession()) {
              console.log('AdminService: Admin idle window elapsed, redirecting to admin login');
              await enforceAdminIdleTimeout();
              const idleError = new Error('Admin session expired. Please log in again.');
              (idleError as { adminIdleRedirect?: boolean }).adminIdleRedirect = true;
              throw idleError;
            }
            console.log('AdminService: Token expired while admin is active, refreshing');
            return await this.refreshAdminToken();
          }

          // If token expires within 5 minutes, try to refresh
          if (timeUntilExpiry < 5 * 60 * 1000) {
            console.log('AdminService: Token expires soon, attempting refresh');
            try {
              return await this.refreshAdminToken();
            } catch (refreshError) {
              if ((refreshError as { adminIdleRedirect?: boolean })?.adminIdleRedirect || (window as any).__sessionRedirectStarted) {
                throw refreshError;
              }
              console.error('AdminService: Token refresh failed:', refreshError);
              // Only clear session if it's a genuine authentication failure
              if (this.isGenuineAuthenticationFailure(refreshError)) {
                this.clearAdminSession();
                throw new Error('Admin authentication required. Please log in again.');
              }
              // If refresh failed for other reasons, continue with current token
              console.log('AdminService: Refresh failed but continuing with current token');
            }
          }
        }
      } catch (parseError) {
        if ((parseError as { adminIdleRedirect?: boolean })?.adminIdleRedirect || (window as any).__sessionRedirectStarted) {
          throw parseError;
        }
        console.error('AdminService: Failed to parse token info:', parseError);
        // Don't clear session for parse errors, just continue with token
        console.log('AdminService: Token info parse failed, continuing with existing token');
      }
    }

    // Set token in API service for all API calls
    apiService.setAdminToken(adminToken);
    return adminToken;
  }



  /**
   * Handle admin-specific errors with intelligent token management
   */
  private getErrorInfo(error: unknown): { message: string; statusCode?: number; response?: any; name?: string; code?: string } {
    if (error instanceof Error) {
      return {
        message: error.message,
        statusCode: (error as any).statusCode,
        response: (error as any).response,
        name: error.name,
        code: (error as any).code
      };
    }
    const err = error as any;
    return {
      message: err?.message || String(error),
      statusCode: err?.statusCode || err?.response?.status,
      response: err?.response,
      name: err?.name,
      code: err?.code
    };
  }

  private handleAdminError(error: any): Error {
    console.log('AdminService: handleAdminError called with:', error);

    // INTELLIGENT token management - only expire tokens on actual authentication failures
    if (error.statusCode === 401 || error.statusCode === 403) {
      console.log('AdminService: Authentication error detected');
      
      // Check if this is a genuine authentication failure vs authorization failure
      const isAuthenticationFailure = this.isGenuineAuthenticationFailure(error);
      const isCriticalEndpoint = error.config?.url?.includes('/api/Admin/login') || 
                                 error.config?.url?.includes('/api/Admin/refresh-token') ||
                                 error.config?.url?.includes('/api/Admin/verify-token');
      
      if (isAuthenticationFailure || isCriticalEndpoint) {
        console.log('AdminService: Genuine authentication failure detected, clearing session');
        this.clearAdminSession();
        
        // Dispatch event to notify components about auth failure
        window.dispatchEvent(new CustomEvent('admin:auth-failed', {
          detail: { error }
        }));
      } else {
        console.log('AdminService: Authorization error (not authentication), keeping session active');
        
        // Session validation now happens only on user actions
        console.log('AdminService: Session validation will occur on next user action');
      }

      // Check if this is a LogManagement endpoint error
      if (error.config?.url?.includes('/api/LogManagement/')) {
        const authError = new Error('Access denied for this log management operation.');
        (authError as any).statusCode = error.statusCode;
        (authError as any).isLogManagementError = true;
        return authError;
      }

      const authError = new Error('Access denied for this operation.');
      (authError as any).statusCode = error.statusCode;
      return authError;
    }

    // Handle LogManagement service unavailable
    if (error.statusCode === 503 && error.config?.url?.includes('/api/LogManagement/')) {
      const serviceError = new Error('Log management service is currently unavailable. Please try again later.');
      (serviceError as any).statusCode = error.statusCode;
      (serviceError as any).isLogManagementError = true;
      return serviceError;
    }

    // Handle LogManagement network errors
    if ((error.name === 'TypeError' && error.message.includes('fetch')) ||
      (error.name === 'NetworkError')) {
      if (error.config?.url?.includes('/api/LogManagement/')) {
        const networkError = new Error('Network error accessing log management. Please check your connection and try again.');
        (networkError as any).isLogManagementError = true;
        return networkError;
      }
      return new Error('Network error. Please check your connection and try again.');
    }

    // Handle validation errors
    if (error.statusCode === 400 && error.errors) {
      const validationError = new Error('Validation failed');
      (validationError as any).statusCode = 400;
      (validationError as any).errors = error.errors;
      return validationError;
    }

    // Handle timeout errors
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      if (error.config?.url?.includes('/api/LogManagement/')) {
        const timeoutError = new Error('Log management request timed out. Please try again.');
        (timeoutError as any).isLogManagementError = true;
        return timeoutError;
      }
      return new Error('Request timed out. Please try again.');
    }

    // Handle server errors
    if (error.statusCode >= 500) {
      if (error.config?.url?.includes('/api/LogManagement/')) {
        const serverError = new Error('Log management server error. Please try again later or contact support.');
        (serverError as any).statusCode = error.statusCode;
        (serverError as any).isLogManagementError = true;
        return serverError;
      }
      const serverError = new Error(error.message || 'Server error. Please try again later or contact support.');
      (serverError as any).statusCode = error.statusCode;
      if (error.responseData) {
        (serverError as any).responseData = error.responseData;
      }
      return serverError;
    }

    // Default error handling
    const adminError = new Error(error.message || 'Admin operation failed');
    if (error.statusCode) {
      (adminError as any).statusCode = error.statusCode;
    }
    if (error.responseData) {
      (adminError as any).responseData = error.responseData;
    }
    if (error.errors) {
      (adminError as any).errors = error.errors;
    }
    if (error.config?.url?.includes('/api/LogManagement/')) {
      (adminError as any).isLogManagementError = true;
    }

    return adminError;
  }

  /**
   * Determine if this is a genuine authentication failure vs authorization failure
   */
  private isGenuineAuthenticationFailure(error: any): boolean {
    // Check error message patterns that indicate token expiration/invalidity
    const authFailurePatterns = [
      'token expired',
      'invalid token',
      'token not found',
      'authentication failed',
      'unauthorized access',
      'session expired',
      'invalid credentials'
    ];

    const errorMessage = (error.message || '').toLowerCase();
    const responseMessage = (error.response?.data?.message || '').toLowerCase();
    const responseError = (error.response?.data?.error || '').toLowerCase();

    // Check if any authentication failure pattern matches
    const hasAuthFailurePattern = authFailurePatterns.some(pattern => 
      errorMessage.includes(pattern) || 
      responseMessage.includes(pattern) || 
      responseError.includes(pattern)
    );

    // If we have clear authentication failure indicators, it's genuine
    if (hasAuthFailurePattern) {
      return true;
    }

    // Check if this is a 401 with specific headers indicating token issues
    if (error.statusCode === 401 && error.response?.headers) {
      const wwwAuth = error.response.headers['www-authenticate'];
      if (wwwAuth && (wwwAuth.includes('expired') || wwwAuth.includes('invalid'))) {
        return true;
      }
    }

    // For 403 errors, it's usually authorization (permissions) not authentication
    if (error.statusCode === 403) {
      return false;
    }

    // Default: treat 401 as authentication failure, 403 as authorization failure
    return error.statusCode === 401;
  }

  /**
   * Asynchronously verify token validity without blocking current operation
   */
  /**
   * Validate admin session only on user actions (not proactively)
   * Replaces proactive token verification with action-based validation
   */
  validateSessionOnAction(): boolean {
    try {
      // Use the existing authentication check which is more reliable
      return this.isAdminAuthenticated();
    } catch (error) {
      console.error('AdminService: Session validation error:', error);
      return false;
    }
  }

  /**
   * Show simple notification for admin session expiration
   * Replaces countdown timers with user-friendly messaging
   */
  private showAdminSessionExpiredNotification(): void {
    try {
      window.dispatchEvent(new CustomEvent('session:expired:notification', {
        detail: {
          type: 'warning',
          persistent: false,
          duration: 8000,
          context: 'admin_session_expiration'
        }
      }));
    } catch (error) {
      console.warn('AdminService: Failed to show admin session expired notification:', error);
    }
  }

  /**
   * Enhanced token validation that checks both expiration and server validity
   */
  private async validateTokenComprehensively(): Promise<boolean> {
    try {
      // First check local expiration
      if (!this.isAdminAuthenticated()) {
        return false;
      }

      // Then verify with server (lightweight check)
      await apiService.get('/api/Admin/verify-token');
      return true;
    } catch (error: any) {
      console.log('AdminService: Comprehensive token validation failed:', error);
      
      // Only clear session if it's a genuine authentication failure
      if (this.isGenuineAuthenticationFailure(error)) {
        this.clearAdminSession();
        return false;
      }
      
      // If it's not an auth failure, assume token is still valid
      return true;
    }
  }

  /**
   * Non-blocking admin login success logging
   */
  private logAdminLoginSuccess(userId: string, email: string, loginStartTime: number, adminRole: string): void {
    // Use setTimeout to make logging truly non-blocking
    setTimeout(async () => {
      try {
        const loginDuration = Date.now() - loginStartTime;

        // Enhanced admin login logging with comprehensive metadata
        await userActionLogger.logLogin(
          userId,
          'admin_panel',
          {
            email: email,
            adminRole: adminRole,
            loginDuration: loginDuration,
            loginStartTime: new Date(loginStartTime).toISOString(),
            loginEndTime: new Date().toISOString(),
            success: true,
            authenticationMethod: 'email_password',
            sessionType: 'admin_session',
            clientInfo: {
              userAgent: navigator.userAgent,
              url: window.location.href,
              referrer: document.referrer
            }
          }
        );

        console.log('AdminService: Admin login logged successfully (non-blocking)');
      } catch (logError) {
        console.warn('AdminService: Failed to log admin login (non-blocking):', logError);
        // Store failed log attempt for debugging
        this.storeFailedLogAttempt('admin_login_success', { userId, email, adminRole }, logError);
      }
    }, 0);
  }

  /**
   * Non-blocking admin login failure logging
   */
  private logAdminLoginFailure(email: string, reason: string, statusCode: number): void {
    // Use setTimeout to make logging truly non-blocking
    setTimeout(async () => {
      try {
        // Enhanced admin login failure logging with comprehensive metadata
        await userActionLogger.logFailedLogin(
          email,
          reason as 'wrong_password' | 'user_not_found' | 'account_locked' | 'invalid_credentials',
          {
            statusCode: statusCode,
            loginType: 'admin_panel',
            timestamp: new Date().toISOString(),
            clientInfo: {
              userAgent: navigator.userAgent,
              url: window.location.href,
              referrer: document.referrer
            },
            securityInfo: {
              ipAddress: 'client-ip', // Will be populated by userActionLogger
              sessionId: 'unknown' // Will be populated by userActionLogger
            }
          }
        );

        console.log('AdminService: Admin login failure logged successfully (non-blocking)');
      } catch (logError) {
        console.warn('AdminService: Failed to log admin login failure (non-blocking):', logError);
        // Store failed log attempt for debugging
        this.storeFailedLogAttempt('admin_login_failure', { email, reason, statusCode }, logError);
      }
    }, 0);
  }

  /**
   * Non-blocking admin login error logging
   */
  private logAdminLoginError(email: string, error: any): void {
    // Use setTimeout to make logging truly non-blocking
    setTimeout(async () => {
      try {
        const statusCode = (error as any)?.response?.status || 500;
        const errorMessage = (error as any)?.message || 'Unknown error';

        // Enhanced admin login error logging with comprehensive metadata
        await userActionLogger.logFailedLogin(
          email,
          'invalid_credentials', // Default to invalid_credentials for errors
          {
            statusCode: statusCode,
            errorMessage: errorMessage,
            errorType: error?.name || 'UnknownError',
            loginType: 'admin_panel',
            timestamp: new Date().toISOString(),
            clientInfo: {
              userAgent: navigator.userAgent,
              url: window.location.href,
              referrer: document.referrer
            },
            technicalDetails: {
              stack: error?.stack,
              config: error?.config ? {
                method: error.config.method,
                url: error.config.url,
                timeout: error.config.timeout
              } : undefined
            }
          }
        );

        console.log('AdminService: Admin login error logged successfully (non-blocking)');
      } catch (logError) {
        console.warn('AdminService: Failed to log admin login error (non-blocking):', logError);
        // Store failed log attempt for debugging
        this.storeFailedLogAttempt('admin_login_error', { email, error: error?.message }, logError);
      }
    }, 0);
  }

  /**
   * Store failed log attempts for debugging purposes
   */
  private storeFailedLogAttempt(logType: string, data: any, error: any): void {
    try {
      const failedLogs = JSON.parse(localStorage.getItem('failedAdminLogs') || '[]');
      failedLogs.push({
        logType,
        data,
        error: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });

      // Keep only the last 50 failed log attempts
      if (failedLogs.length > 50) {
        failedLogs.splice(0, failedLogs.length - 50);
      }

      localStorage.setItem('failedAdminLogs', JSON.stringify(failedLogs));
    } catch (storageError) {
      console.warn('AdminService: Failed to store failed log attempt:', storageError);
    }
  }

  /**
   * Ensure all three logging services are initialized for comprehensive admin login logging
   */
  private ensureComprehensiveLoggingInitialized(): void {
    try {
      // Initialize all three logging services for comprehensive admin login logging
      Promise.all([
        import('./apiLogger').then(({ apiLogger }) => {
          apiLogger.initialize();
          console.log('AdminService: ApiLogger initialized for comprehensive admin login logging');
          return apiLogger;
        }),
        import('./databaseLogger').then(({ databaseLogger }) => {
          databaseLogger.initialize();
          console.log('AdminService: DatabaseLogger initialized for comprehensive admin login logging');
          return databaseLogger;
        }),
        import('./userActionLogger').then(({ userActionLogger }) => {
          userActionLogger.initialize();
          console.log('AdminService: UserActionLogger initialized for comprehensive admin login logging');
          return userActionLogger;
        })
      ]).then(() => {
        console.log('AdminService: All three logging services initialized successfully for comprehensive admin login logging');
      }).catch(error => {
        console.warn('AdminService: Failed to initialize some logging services (non-blocking):', error);
        // Ensure main admin login flow continues even if logging initialization fails
      });
    } catch (error) {
      console.warn('AdminService: Failed to ensure comprehensive logging services initialization (non-blocking):', error);
      // Ensure main admin login flow continues even if logging initialization fails
    }
  }

  /**
   * Log admin login API call explicitly (in addition to interceptor)
   */
  private logAdminLoginApiCall(
    method: string,
    endpoint: string,
    statusCode: number,
    responseTime: number,
    success: boolean,
    error?: any
  ): void {
    // Use setTimeout to make API logging truly non-blocking
    setTimeout(async () => {
      try {
        const { apiLogger } = await import('./apiLogger');

        // Enhanced API logging context for admin login
        const apiLogContext = {
          userId: this.getCurrentAdminUserId() || 'admin-user',
          sessionId: this.getSessionId(),
          correlationId: this.generateCorrelationId(),
          source: 'AdminService',
          category: 'admin_authentication',
          tags: ['admin-login', 'authentication', 'api-call', success ? 'success' : 'failed'],
          metadata: {
            loginAttempt: true,
            authenticationFlow: 'admin_panel',
            endpoint: endpoint,
            method: method,
            timestamp: new Date().toISOString(),
            clientInfo: {
              userAgent: navigator.userAgent,
              url: window.location.href,
              referrer: document.referrer
            },
            performance: {
              responseTime: responseTime,
              performanceCategory: responseTime > 2000 ? 'slow' : responseTime > 1000 ? 'normal' : 'fast'
            }
          }
        };

        if (success) {
          await apiLogger.logSuccessfulRequest(
            method,
            endpoint,
            statusCode,
            responseTime,
            this.calculateRequestSize({ email: '[REDACTED]', password: '[REDACTED]' }), // Sanitized request size
            this.calculateResponseSize({ token: '[REDACTED]', user: '[REDACTED]' }), // Sanitized response size
            apiLogContext
          );
        } else {
          await apiLogger.logFailedRequest(
            method,
            endpoint,
            statusCode,
            error || new Error('Admin login failed'),
            responseTime,
            {
              ...apiLogContext,
              tags: [...apiLogContext.tags, 'error'],
              metadata: {
                ...apiLogContext.metadata,
                errorDetails: {
                  message: error?.message || 'Unknown error',
                  type: error?.name || 'UnknownError',
                  stack: error?.stack
                }
              }
            }
          );
        }

        console.log('AdminService: Admin login API call logged successfully (non-blocking)');
      } catch (logError) {
        console.warn('AdminService: Failed to log admin login API call (non-blocking):', logError);
        // Store failed log attempt for debugging
        this.storeFailedLogAttempt('admin_login_api_call', { method, endpoint, statusCode, success }, logError);
      }
    }, 0);
  }

  /**
   * Log admin credential verification database operation (non-blocking)
   */
  private logAdminCredentialVerification(
    userId: string,
    email: string,
    duration: number,
    success: boolean,
    error?: any
  ): void {
    // Use setTimeout to make database logging truly non-blocking
    setTimeout(async () => {
      try {
        const { databaseLogger } = await import('./databaseLogger');

        // Enhanced database logging for admin credential verification
        await databaseLogger.logDatabaseOperation({
          type: 'READ',
          table: 'admin_users',
          recordId: success ? userId : undefined,
          duration: duration,
          rowsAffected: success ? 1 : 0,
          success: success,
          error: error,
          query: 'SELECT id, email, password_hash, roles, is_active FROM admin_users WHERE email = ? AND password_hash = ?', // More specific sanitized query
          parameters: {
            email: email,
            password_hash: '[REDACTED]',
            query_type: 'credential_verification',
            table_accessed: 'admin_users'
          },
          databaseName: 'main',
          connectionId: this.generateConnectionId()
        }, {
          userId: success ? userId : 'anonymous',
          sessionId: this.getSessionId(),
          source: 'AdminService',
          category: 'admin_authentication',
          adminOperation: true,
          tags: ['admin-login', 'credential-verification', 'database-query', success ? 'success' : 'failed'],
          metadata: {
            operation: 'credential_verification',
            email: email,
            loginAttempt: true,
            authenticationResult: success ? 'success' : 'failed',
            queryType: 'SELECT',
            tableAccessed: 'admin_users',
            securityLevel: 'high',
            timestamp: new Date().toISOString(),
            performance: {
              executionTime: duration,
              performanceCategory: duration > 1000 ? 'slow' : duration > 500 ? 'normal' : 'fast'
            },
            errorDetails: error ? {
              message: error.message || 'unknown_error',
              type: error.name || 'UnknownError',
              code: error.code || 'UNKNOWN'
            } : undefined,
            clientInfo: {
              userAgent: navigator.userAgent,
              url: window.location.href,
              ipAddress: 'client-ip' // Will be populated by databaseLogger
            }
          }
        });

        console.log('AdminService: Admin credential verification logged successfully (non-blocking)');
      } catch (logError) {
        console.warn('AdminService: Failed to log admin credential verification (non-blocking):', logError);
        // Store failed log attempt for debugging
        this.storeFailedLogAttempt('admin_credential_verification', { userId, email, success, duration }, logError);
      }
    }, 0);
  }

  /**
   * Get admin authentication headers
   */
  getAdminAuthHeaders(): Record<string, string> {
    const adminToken = localStorage.getItem('adminToken');
    return adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
  }

  /**
   * Generate correlation ID for tracking requests
   */
  private generateCorrelationId(): string {
    return `admin_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get session ID for logging
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('adminSessionId');
    if (!sessionId) {
      sessionId = `admin_session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('adminSessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Calculate request size for logging
   */
  private calculateRequestSize(data: any): number {
    try {
      return JSON.stringify(data).length;
    } catch {
      return 0;
    }
  }

  /**
   * Calculate response size for logging
   */
  private calculateResponseSize(data: any): number {
    try {
      return JSON.stringify(data).length;
    } catch {
      return 0;
    }
  }

  /**
   * Generate connection ID for database logging
   */
  private generateConnectionId(): string {
    return `admin_conn_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Comprehensive three-layer logging for successful admin login
   * Integrates User Action, API, and Database logging with proper sequencing and error handling
   */
  private async logComprehensiveAdminLoginSuccess(
    userId: string,
    email: string,
    loginStartTime: number,
    loginDuration: number,
    adminRole: string
  ): Promise<void> {
    // Use setTimeout to make all logging truly non-blocking
    setTimeout(async () => {
      try {
        console.log('AdminService: Starting comprehensive three-layer logging for successful admin login');

        // Layer 1: User Action Logging - Log the admin login event
        try {
          await this.logAdminLoginUserAction(userId, email, loginStartTime, adminRole, true);
          console.log('AdminService: ✅ User Action logging completed for admin login success');
        } catch (userActionError) {
          console.warn('AdminService: ⚠️ User Action logging failed (non-blocking):', userActionError);
        }

        // Layer 2: API Logging - Log the HTTP request/response
        try {
          await this.logAdminLoginApiLayer('POST', '/api/Admin/login', 200, loginDuration, true);
          console.log('AdminService: ✅ API logging completed for admin login success');
        } catch (apiError) {
          console.warn('AdminService: ⚠️ API logging failed (non-blocking):', apiError);
        }

        // Layer 3: Database Logging - Log the credential verification query
        try {
          await this.logAdminLoginDatabaseLayer(userId, email, loginDuration, true);
          console.log('AdminService: ✅ Database logging completed for admin login success');
        } catch (databaseError) {
          console.warn('AdminService: ⚠️ Database logging failed (non-blocking):', databaseError);
        }

        console.log('AdminService: Comprehensive three-layer logging completed for successful admin login');
      } catch (error) {
        console.warn('AdminService: Failed to complete comprehensive admin login success logging (non-blocking):', error);
        // Store failed log attempt for debugging
        this.storeFailedLogAttempt('comprehensive_admin_login_success', { userId, email, adminRole }, error);
      }
    }, 0);
  }

  /**
   * Comprehensive three-layer logging for failed admin login
   */
  private async logComprehensiveAdminLoginFailure(
    email: string,
    reason: string,
    statusCode: number,
    loginDuration: number,
    error?: Error
  ): Promise<void> {
    // Use setTimeout to make all logging truly non-blocking
    setTimeout(async () => {
      try {
        console.log('AdminService: Starting comprehensive three-layer logging for failed admin login');

        // Layer 1: User Action Logging - Log the failed login attempt
        try {
          await this.logAdminLoginUserAction('anonymous', email, Date.now() - loginDuration, 'admin', false, reason);
          console.log('AdminService: ✅ User Action logging completed for admin login failure');
        } catch (userActionError) {
          console.warn('AdminService: ⚠️ User Action logging failed (non-blocking):', userActionError);
        }

        // Layer 2: API Logging - Log the failed HTTP request/response
        try {
          await this.logAdminLoginApiLayer('POST', '/api/Admin/login', statusCode, loginDuration, false, error);
          console.log('AdminService: ✅ API logging completed for admin login failure');
        } catch (apiError) {
          console.warn('AdminService: ⚠️ API logging failed (non-blocking):', apiError);
        }

        // Layer 3: Database Logging - Log the failed credential verification
        try {
          await this.logAdminLoginDatabaseLayer('anonymous', email, loginDuration, false, error);
          console.log('AdminService: ✅ Database logging completed for admin login failure');
        } catch (databaseError) {
          console.warn('AdminService: ⚠️ Database logging failed (non-blocking):', databaseError);
        }

        console.log('AdminService: Comprehensive three-layer logging completed for failed admin login');
      } catch (error) {
        console.warn('AdminService: Failed to complete comprehensive admin login failure logging (non-blocking):', error);
        // Store failed log attempt for debugging
        this.storeFailedLogAttempt('comprehensive_admin_login_failure', { email, reason, statusCode }, error);
      }
    }, 0);
  }

  /**
   * Comprehensive three-layer logging for admin login error
   */
  private async logComprehensiveAdminLoginError(
    email: string,
    error: any,
    statusCode: number,
    loginDuration: number
  ): Promise<void> {
    // Use setTimeout to make all logging truly non-blocking
    setTimeout(async () => {
      try {
        console.log('AdminService: Starting comprehensive three-layer logging for admin login error');

        // Layer 1: User Action Logging - Log the error event
        try {
          await this.logAdminLoginUserAction('anonymous', email, Date.now() - loginDuration, 'admin', false, 'system_error');
          console.log('AdminService: ✅ User Action logging completed for admin login error');
        } catch (userActionError) {
          console.warn('AdminService: ⚠️ User Action logging failed (non-blocking):', userActionError);
        }

        // Layer 2: API Logging - Log the error HTTP request/response
        try {
          await this.logAdminLoginApiLayer('POST', '/api/Admin/login', statusCode, loginDuration, false, error);
          console.log('AdminService: ✅ API logging completed for admin login error');
        } catch (apiError) {
          console.warn('AdminService: ⚠️ API logging failed (non-blocking):', apiError);
        }

        // Layer 3: Database Logging - Log the error in credential verification
        try {
          await this.logAdminLoginDatabaseLayer('anonymous', email, loginDuration, false, error);
          console.log('AdminService: ✅ Database logging completed for admin login error');
        } catch (databaseError) {
          console.warn('AdminService: ⚠️ Database logging failed (non-blocking):', databaseError);
        }

        console.log('AdminService: Comprehensive three-layer logging completed for admin login error');
      } catch (error) {
        console.warn('AdminService: Failed to complete comprehensive admin login error logging (non-blocking):', error);
        // Store failed log attempt for debugging
        this.storeFailedLogAttempt('comprehensive_admin_login_error', { email, error: (error as Error)?.message || String(error) }, error);
      }
    }, 0);
  }

  /**
   * Layer 1: User Action Logging for admin login
   */
  private async logAdminLoginUserAction(
    userId: string,
    email: string,
    loginStartTime: number,
    adminRole: string,
    success: boolean,
    failureReason?: string
  ): Promise<void> {
    try {
      const loginDuration = Date.now() - loginStartTime;

      if (success) {
        // Log successful admin login
        await userActionLogger.logLogin(
          userId,
          'admin_panel',
          {
            email: email,
            adminRole: adminRole,
            loginDuration: loginDuration,
            loginStartTime: new Date(loginStartTime).toISOString(),
            loginEndTime: new Date().toISOString(),
            success: true,
            authenticationMethod: 'email_password',
            sessionType: 'admin_session',
            clientInfo: {
              userAgent: navigator.userAgent,
              url: window.location.href,
              referrer: document.referrer
            }
          }
        );
      } else {
        // Log failed admin login
        await userActionLogger.logFailedLogin(
          email,
          (failureReason as any) || 'invalid_credentials',
          {
            loginType: 'admin_panel',
            timestamp: new Date().toISOString(),
            clientInfo: {
              userAgent: navigator.userAgent,
              url: window.location.href,
              referrer: document.referrer
            },
            securityInfo: {
              ipAddress: 'client-ip',
              sessionId: this.getSessionId()
            }
          }
        );
      }
    } catch (error) {
      console.warn('AdminService: User Action logging failed for admin login:', error);
      throw error; // Re-throw to be caught by caller
    }
  }

  /**
   * Layer 2: API Logging for admin login
   */
  private async logAdminLoginApiLayer(
    method: string,
    endpoint: string,
    statusCode: number,
    responseTime: number,
    success: boolean,
    error?: any
  ): Promise<void> {
    try {
      const { apiLogger } = await import('./apiLogger');

      // Enhanced API logging context for admin login
      const apiLogContext = {
        userId: this.getCurrentAdminUserId() || 'admin-user',
        sessionId: this.getSessionId(),
        correlationId: this.generateCorrelationId(),
        source: 'AdminService',
        category: 'admin_authentication',
        tags: ['admin-login', 'authentication', 'api-call', success ? 'success' : 'failed'],
        metadata: {
          loginAttempt: true,
          authenticationFlow: 'admin_panel',
          endpoint: endpoint,
          method: method,
          timestamp: new Date().toISOString(),
          clientInfo: {
            userAgent: navigator.userAgent,
            url: window.location.href,
            referrer: document.referrer
          },
          performance: {
            responseTime: responseTime,
            performanceCategory: responseTime > 2000 ? 'slow' : responseTime > 1000 ? 'normal' : 'fast'
          }
        }
      };

      if (success) {
        await apiLogger.logSuccessfulRequest(
          method,
          endpoint,
          statusCode,
          responseTime,
          this.calculateRequestSize({ email: '[REDACTED]', password: '[REDACTED]' }),
          this.calculateResponseSize({ token: '[REDACTED]', user: '[REDACTED]' }),
          apiLogContext
        );
      } else {
        await apiLogger.logFailedRequest(
          method,
          endpoint,
          statusCode,
          error || new Error('Admin login failed'),
          responseTime,
          {
            ...apiLogContext,
            tags: [...apiLogContext.tags, 'error'],
            metadata: {
              ...apiLogContext.metadata,
              errorDetails: {
                message: error?.message || 'Unknown error',
                type: error?.name || 'UnknownError',
                stack: error?.stack
              }
            }
          }
        );
      }
    } catch (error) {
      console.warn('AdminService: API logging failed for admin login:', error);
      throw error; // Re-throw to be caught by caller
    }
  }

  /**
   * Layer 3: Database Logging for admin login credential verification
   */
  private async logAdminLoginDatabaseLayer(
    userId: string,
    email: string,
    duration: number,
    success: boolean,
    error?: any
  ): Promise<void> {
    try {
      const { databaseLogger } = await import('./databaseLogger');

      // Enhanced database logging for admin credential verification
      await databaseLogger.logDatabaseOperation({
        type: 'READ',
        table: 'admin_users',
        recordId: success ? userId : undefined,
        duration: duration,
        rowsAffected: success ? 1 : 0,
        success: success,
        error: error,
        query: 'SELECT id, email, password_hash, roles, is_active FROM admin_users WHERE email = ? AND password_hash = ?',
        parameters: {
          email: email,
          password_hash: '[REDACTED]',
          query_type: 'credential_verification',
          table_accessed: 'admin_users'
        },
        databaseName: 'main',
        connectionId: this.generateConnectionId()
      }, {
        userId: success ? userId : 'anonymous',
        sessionId: this.getSessionId(),
        source: 'AdminService',
        category: 'admin_authentication',
        adminOperation: true,
        tags: ['admin-login', 'credential-verification', 'database-query', success ? 'success' : 'failed'],
        metadata: {
          operation: 'credential_verification',
          email: email,
          loginAttempt: true,
          authenticationResult: success ? 'success' : 'failed',
          queryType: 'SELECT',
          tableAccessed: 'admin_users',
          securityLevel: 'high',
          timestamp: new Date().toISOString(),
          performance: {
            executionTime: duration,
            performanceCategory: duration > 1000 ? 'slow' : duration > 500 ? 'normal' : 'fast'
          },
          errorDetails: error ? {
            message: error.message || 'unknown_error',
            type: error.name || 'UnknownError',
            code: error.code || 'UNKNOWN'
          } : undefined,
          clientInfo: {
            userAgent: navigator.userAgent,
            url: window.location.href,
            ipAddress: 'client-ip'
          }
        }
      });
    } catch (error) {
      console.warn('AdminService: Database logging failed for admin login:', error);
      throw error; // Re-throw to be caught by caller
    }
  }

  /**
   * Simple logging for admin login success
   */
  private async logSimpleAdminLoginSuccess(userId: string, email: string): Promise<void> {
    try {
      // Simple user action logging
      await userActionLogger.logLogin(userId, 'admin_panel', {
        email: email,
        success: true,
        authenticationMethod: 'email_password',
        sessionType: 'admin_session'
      });
      console.log('AdminService: Admin login logged successfully');
    } catch (error) {
      console.warn('AdminService: Failed to log admin login (non-blocking):', error);
    }
  }

  /**
   * Create user-friendly error messages for admin login failures
   */
  private createUserFriendlyAdminLoginError(error: any): Error {
    const statusCode = error?.response?.status;
    const errorMessage = error?.message || 'Unknown error';

    let userMessage: string;

    switch (statusCode) {
      case 400:
        userMessage = 'Invalid login credentials. Please check your email and password.';
        break;
      case 401:
        userMessage = 'Authentication failed. Please verify your admin credentials.';
        break;
      case 403:
        userMessage = 'Access denied. You do not have admin privileges.';
        break;
      case 404:
        userMessage = 'Admin login service not found. Please contact system administrator.';
        break;
      case 429:
        userMessage = 'Too many login attempts. Please wait a few minutes before trying again.';
        break;
      case 500:
      case 502:
      case 503:
        userMessage = 'Server error occurred during login. Please try again in a few moments.';
        break;
      case 504:
        userMessage = 'Login request timed out. Please check your connection and try again.';
        break;
      default:
        if (errorMessage.toLowerCase().includes('network')) {
          userMessage = 'Network error occurred. Please check your internet connection and try again.';
        } else if (errorMessage.toLowerCase().includes('timeout')) {
          userMessage = 'Login request timed out. Please try again.';
        } else if (errorMessage.toLowerCase().includes('cors')) {
          userMessage = 'Connection error occurred. Please contact system administrator.';
        } else {
          userMessage = `Login failed: ${errorMessage}. Please try again or contact support.`;
        }
    }

    // Create enhanced error with user-friendly message
    const enhancedError = new Error(userMessage);
    (enhancedError as any).originalError = error;
    (enhancedError as any).statusCode = statusCode;
    (enhancedError as any).isAdminLoginError = true;

    return enhancedError;
  }

  /**
   * Validate admin session using lightweight validation (no heavy API calls)
   */
  async validateAdminSession(): Promise<boolean> {
    if (!this.isAdminAuthenticated()) return false;

    try {
      // Use lightweight token validation instead of calling heavy endpoints
      // This prevents unnecessary calls to /api/Admin/users or other data endpoints
      const { apiCallManager } = await import('./apiCallManager');
      const isValid = await apiCallManager.validateAdminSessionLightweight();
      
      if (!isValid) {
        console.log('AdminService: Lightweight session validation failed, clearing session');
        this.clearAdminSession();
        return false;
      }

      console.log('AdminService: Admin session validated using lightweight method');
      return true;
    } catch (error) {
      console.error('AdminService: Session validation failed:', error);
      this.clearAdminSession();
      return false;
    }
  }

  /**
   * Initialize admin authentication - restore token from localStorage if available
   * This should be called when the app starts to restore admin session
   * OPTIMIZED: Prevents unnecessary API calls during initialization
   */
  initializeAdminAuth(): void {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      // Restore the admin token to apiService
      apiService.setAdminToken(adminToken);
      
      // Lazy load apiCallManager only when needed (not during initialization)
      // This prevents blocking the login page load
      // The apiCallManager will be loaded when dashboard is accessed
      // Use setTimeout to defer this to avoid blocking
      setTimeout(() => {
        import('./apiCallManager').then(({ apiCallManager }) => {
          apiCallManager.preventAuthenticationTriggeredDataCalls();
          console.log('AdminService: Authentication-triggered data calls prevented during app initialization');
          
          // Re-enable after a short delay to allow normal operation
          setTimeout(() => {
            apiCallManager.enableAuthenticationTriggeredDataCalls();
            console.log('AdminService: Authentication-triggered data calls re-enabled');
          }, 2000); // 2 second delay
        }).catch(error => {
          console.warn('AdminService: Failed to manage auth-triggered data calls:', error);
        });
      }, 0); // Defer to next event loop tick
    }
  }

  /**
   * Lightweight token validation for authentication processes
   * This method should be used instead of calling data endpoints for validation
   */
  async validateTokenLightweight(): Promise<boolean> {
    try {
      if (!this.isAdminAuthenticated()) {
        return false;
      }

      // Use ApiCallManager's lightweight validation
      const { apiCallManager } = await import('./apiCallManager');
      return await apiCallManager.validateTokenLightweight();
    } catch (error) {
      console.error('AdminService: Lightweight token validation failed:', error);
      return false;
    }
  }

  /**
   * Check if admin authentication is valid without making API calls to data endpoints
   * This prevents unnecessary calls to /api/Admin/users or other heavy endpoints
   */
  async isAdminAuthenticationValidLightweight(): Promise<boolean> {
    try {
      // First check local authentication state
      if (!this.isAdminAuthenticated()) {
        return false;
      }

      // Use lightweight validation instead of calling data endpoints
      return await this.validateTokenLightweight();
    } catch (error) {
      console.error('AdminService: Lightweight authentication check failed:', error);
      return false;
    }
  }

  /**
   * Get system logs using new LogManagement API
   */





  /**
   * Helper method to build query parameters for log requests
   */
  private buildLogQueryParams(params: EnhancedLogQueryParams): string {
    const queryParams = new URLSearchParams();

    // Map frontend parameter names to backend parameter names
    const parameterMapping: Record<string, string> = {
      'logId': 'LogId',
      'userName': 'UserName',
      'userId': 'UserId',
      'startDate': 'StartDate',
      'endDate': 'EndDate',
      'searchTerm': 'SearchTerm',
      'sortBy': 'SortBy',
      'sortDirection': 'SortDirection',
      'pageSize': 'PageSize',
      'page': 'Page',
      'level': 'Level',
      'source': 'Source',
      'category': 'Category',
      'resource': 'Resource',
      'method': 'Method',
      'endpoint': 'Endpoint',
      'statusCode': 'StatusCode',
      'minResponseTime': 'MinResponseTimeMs',
      'maxResponseTime': 'MaxResponseTimeMs',
      'ipAddress': 'IpAddress',
      'traceId': 'TraceId',
      'isResolved': 'IsResolved'
    };

    Object.entries(params as Record<string, any>).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Use mapped parameter name if available, otherwise use original key
        const backendKey = parameterMapping[key] || key;

        if (Array.isArray(value)) {
          value.forEach(v => queryParams.append(backendKey, v.toString()));
        } else {
          queryParams.append(backendKey, value.toString());
        }
      }
    });

    const queryString = queryParams.toString();
    console.log('AdminService: Built query params:', queryString);

    // Enhanced debugging for all parameters
    console.log('=== PARAMETER MAPPING DEBUG ===');
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        const backendKey = parameterMapping[key] || key;
        console.log(`${key} -> ${backendKey}: ${value} (${typeof value})`);
        console.log(`  In query string: ${queryString.includes(`${backendKey}=`) ? 'YES' : 'NO'}`);
      }
    });
    console.log('==============================');

    // Special debugging for common filter parameters
    if (params.userName) {
      console.log('AdminService: UserName filter details:');
      console.log('  - Original value:', params.userName);
      console.log('  - Mapped to: UserName');
      console.log('  - In query string:', queryString.includes('UserName=') ? 'YES' : 'NO');
    }

    if (params.method) {
      console.log('AdminService: Method filter details:');
      console.log('  - Original value:', params.method);
      console.log('  - Mapped to: Method');
      console.log('  - In query string:', queryString.includes('Method=') ? 'YES' : 'NO');
    }

    return queryString;
  }

  // ============================================================================
  // ENHANCED ERROR LOG METHODS (Updated for comprehensive system)
  // ============================================================================

  /**
   * Enhanced error logs method with comprehensive filtering
   */
  async getEnhancedErrorLogs(params: EnhancedLogQueryParams = {}): Promise<ErrorLogDto[]> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = this.buildLogQueryParams(params);
      const response = await apiService.get<any>(`/api/LogManagement/error-logs?${queryParams}`);

      // Handle different response formats
      if (Array.isArray(response)) {
        return response as ErrorLogDto[];
      } else if (response && typeof response === 'object') {
        if (response.data && Array.isArray(response.data)) {
          return response.data as ErrorLogDto[];
        } else if (response.items && Array.isArray(response.items)) {
          return response.items as ErrorLogDto[];
        }
      }

      console.warn('Error logs response format unexpected:', response);
      return [];
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }


  // ============================================================================
  // COMPREHENSIVE LOG API SERVICE METHODS (Task 2.1)
  // ============================================================================

  /**
   * Get user action logs with comprehensive filtering
   */
  async getUserActionLogs(params: EnhancedLogQueryParams = {}): Promise<{ logs: UserActionLogDto[], totalCount: number }> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = this.buildLogQueryParams(params);
      const response = await apiService.get<any>(`/api/LogManagement/user-actions?${queryParams}`);

      console.log('AdminService: User action logs response:', response);

      // Handle different response formats
      if (Array.isArray(response)) {
        return { logs: response as UserActionLogDto[], totalCount: response.length };
      } else if (response && typeof response === 'object') {
        let logs: UserActionLogDto[] = [];
        let totalCount = 0;

        // Check for common pagination response formats
        if (response.data && Array.isArray(response.data)) {
          logs = response.data as UserActionLogDto[];
          totalCount = response.totalCount || response.total || logs.length;
        } else if (response.items && Array.isArray(response.items)) {
          logs = response.items as UserActionLogDto[];
          totalCount = response.totalCount || response.total || logs.length;
        } else if (response.results && Array.isArray(response.results)) {
          logs = response.results as UserActionLogDto[];
          totalCount = response.totalCount || response.total || logs.length;
        } else if (response.logs && Array.isArray(response.logs)) {
          logs = response.logs as UserActionLogDto[];
          totalCount = response.totalCount || response.total || logs.length;
        }

        return { logs, totalCount };
      }

      // If we can't find an array, return empty result and log the issue
      console.warn('User action logs response format unexpected:', response);
      return { logs: [], totalCount: 0 };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get user action logs method statistics using existing API with method filters
   */
  async getUserActionLogsMethodStats(params: EnhancedLogQueryParams = {}): Promise<{ method: string, count: number }[]> {
    try {
      await this.ensureValidAdminToken();

      // Common HTTP methods to check
      const methods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'];
      const methodStats: { method: string, count: number }[] = [];

      console.log('AdminService: Getting method stats with filters:', params);

      // Make parallel requests for each method with current filters
      const methodPromises = methods.map(async (method) => {
        try {
          const methodParams = {
            ...params,
            method,
            page: 1,
            pageSize: 1 // We only need the totalCount, not the actual logs
          };
          const queryParams = this.buildLogQueryParams(methodParams);
          const response = await apiService.get<any>(`/api/LogManagement/user-actions?${queryParams}`);

          // Extract totalCount from response
          let totalCount = 0;
          if (Array.isArray(response)) {
            totalCount = response.length;
          } else if (response && typeof response === 'object') {
            totalCount = response.totalCount || response.total || 0;
          }

          console.log(`AdminService: Method ${method} count:`, totalCount);

          return totalCount > 0 ? { method, count: totalCount } : null;
        } catch (error) {
          console.error(`Failed to get count for method ${method}:`, error);
          return null;
        }
      });

      const results = await Promise.all(methodPromises);

      // Filter out null results and add to methodStats
      results.forEach(result => {
        if (result && result.count > 0) {
          methodStats.push(result);
        }
      });

      console.log('AdminService: Final method stats:', methodStats);
      return methodStats;
    } catch (error) {
      console.error('Failed to get user action logs method stats:', error);
      return [];
    }
  }

  /**
   * Get user action logs status code statistics using existing API
   */
  async getUserActionLogsStatusStats(params: EnhancedLogQueryParams = {}): Promise<{ statusCode: number, count: number }[]> {
    try {
      await this.ensureValidAdminToken();

      // First, get a sample of logs to discover what status codes exist
      const sampleParams = {
        ...params,
        page: 1,
        pageSize: 100 // Get a reasonable sample to find status codes
      };
      const queryParams = this.buildLogQueryParams(sampleParams);
      const sampleResponse = await apiService.get<any>(`/api/LogManagement/user-actions?${queryParams}`);

      let sampleLogs: UserActionLogDto[] = [];
      if (Array.isArray(sampleResponse)) {
        sampleLogs = sampleResponse;
      } else if (sampleResponse && typeof sampleResponse === 'object') {
        sampleLogs = sampleResponse.data || sampleResponse.logs || sampleResponse.items || sampleResponse.results || [];
      }

      // Extract unique status codes from sample
      const uniqueStatusCodes = new Set<number>();
      sampleLogs.forEach(log => {
        if (log.statusCode && typeof log.statusCode === 'number') {
          uniqueStatusCodes.add(log.statusCode);
        }
      });

      console.log('AdminService: Found status codes in sample:', Array.from(uniqueStatusCodes));

      // If no status codes found in sample, try common ones
      if (uniqueStatusCodes.size === 0) {
        [200, 201, 204, 400, 401, 403, 404, 422, 500, 502, 503].forEach(code => uniqueStatusCodes.add(code));
      }

      const statusStats: { statusCode: number, count: number }[] = [];

      // For each unique status code, get the total count with current filters
      // Note: Since the API doesn't support statusCode filtering, we'll use the sample data
      // and extrapolate based on the total count vs sample size
      const totalCountParams = { ...params, page: 1, pageSize: 1 };
      const totalQueryParams = this.buildLogQueryParams(totalCountParams);
      const totalResponse = await apiService.get<any>(`/api/LogManagement/user-actions?${totalQueryParams}`);

      let totalCount = 0;
      if (Array.isArray(totalResponse)) {
        totalCount = totalResponse.length;
      } else if (totalResponse && typeof totalResponse === 'object') {
        totalCount = totalResponse.totalCount || totalResponse.total || 0;
      }

      console.log('AdminService: Total filtered records:', totalCount);
      console.log('AdminService: Sample size:', sampleLogs.length);

      // Calculate status code distribution from sample
      const statusCodeCounts = new Map<number, number>();
      sampleLogs.forEach(log => {
        if (log.statusCode && typeof log.statusCode === 'number') {
          const current = statusCodeCounts.get(log.statusCode) || 0;
          statusCodeCounts.set(log.statusCode, current + 1);
        }
      });

      // Extrapolate to total count if we have a reasonable sample
      const sampleSize = sampleLogs.length;
      if (sampleSize > 0 && totalCount > 0) {
        const scaleFactor = totalCount / sampleSize;

        statusCodeCounts.forEach((sampleCount, statusCode) => {
          const estimatedCount = Math.round(sampleCount * scaleFactor);
          if (estimatedCount > 0) {
            statusStats.push({ statusCode, count: estimatedCount });
          }
        });
      }

      console.log('AdminService: Final status stats:', statusStats);
      return statusStats;
    } catch (error) {
      console.error('Failed to get user action logs status stats:', error);
      return [];
    }
  }

  /**
   * Get all unique users from the system
   */
  async getAllUsers(): Promise<string[]> {
    try {
      await this.ensureValidAdminToken();
      const response = await apiService.get<any>('/api/Admin/users');

      console.log('AdminService: All users response:', response);

      let users: any[] = [];
      if (Array.isArray(response)) {
        users = response;
      } else if (response && typeof response === 'object') {
        users = response.data || response.users || response.items || response.results || [];
      }

      // Extract user identifiers (email or username)
      const userNames = users.map(user => {
        return user.email || user.userName || user.username || user.id || 'Unknown User';
      }).filter(name => name && name !== 'Unknown User');

      console.log('AdminService: Extracted user names:', userNames);
      return userNames;
    } catch (error) {
      console.error('Failed to get all users:', error);
      return [];
    }
  }

  /**
   * Get top users statistics using the dedicated stats endpoint
   */
  async getUserActionLogsTopUsersStats(params: EnhancedLogQueryParams = {}): Promise<{ userName: string, count: number }[]> {
    try {
      await this.ensureValidAdminToken();

      const queryParams = this.buildLogQueryParams(params);
      console.log('AdminService: Getting top users stats from stats endpoint with params:', queryParams);

      const response = await apiService.get<any>(`/api/LogManagement/user-actions/stats?${queryParams}`);

      console.log('AdminService: Top users stats response:', response);

      // Extract top users from the stats response
      // Handle different possible response structures
      let topUsers = response?.topUsers ||
        response?.topUsersByActivity ||
        response?.userActivity ||
        response?.users ||
        [];

      // Ensure the data is in the expected format
      if (Array.isArray(topUsers)) {
        const formattedUsers = topUsers
          .filter(user => user && (user.userName || user.name || user.user) && (user.count || user.actions || user.total))
          .map(user => ({
            userName: user.userName || user.name || user.user,
            count: user.count || user.actions || user.total || 0
          }))
          .slice(0, 10); // Take top 10

        console.log('AdminService: Formatted top users stats:', formattedUsers);
        return formattedUsers;
      }

      console.log('AdminService: No top users data found in stats response, falling back');
      return this.getUserActionLogsTopUsersStatsFallback(params);
    } catch (error) {
      console.error('Failed to get user action logs top users stats from stats endpoint:', error);
      // Fallback to sample-based approach
      return this.getUserActionLogsTopUsersStatsFallback(params);
    }
  }

  /**
   * Get comprehensive user action statistics and analytics
   */
  async getUserActionLogsStats(params: EnhancedLogQueryParams = {}): Promise<any> {
    try {
      await this.ensureValidAdminToken();

      const queryParams = this.buildLogQueryParams(params);
      console.log('AdminService: Getting user action logs stats with params:', queryParams);

      // Enhanced debugging for filter parameters
      console.log('=== STATS ENDPOINT FILTER DEBUG ===');
      console.log('Original params:', params);
      console.log('Built query string:', queryParams);
      console.log('Full URL:', `/api/LogManagement/user-actions/stats?${queryParams}`);

      // Check if we have any filters applied
      const hasFilters = Object.keys(params).some(key => key !== '_t' && params[key] !== undefined && params[key] !== null && params[key] !== '');
      console.log('Has active filters:', hasFilters);
      if (hasFilters) {
        console.log('Active filter keys:', Object.keys(params).filter(key => key !== '_t' && params[key] !== undefined && params[key] !== null && params[key] !== ''));
      }
      console.log('===================================');

      const response = await apiService.get<any>(`/api/LogManagement/user-actions/stats?${queryParams}`);

      console.log('AdminService: User action logs stats response:', response);
      console.log('AdminService: Stats response type:', typeof response);
      console.log('AdminService: Stats response keys:', response ? Object.keys(response) : 'null');

      // Log specific fields we're looking for
      if (response) {
        console.log('AdminService: methodDistribution:', response.methodDistribution);
        console.log('AdminService: statusCodeDistribution:', response.statusCodeDistribution);
        console.log('AdminService: topUsers:', response.topUsers);
        console.log('AdminService: topUsersByActivity:', response.topUsersByActivity);
        console.log('AdminService: All available fields:', Object.keys(response));

        // Log response time related fields specifically
        const responseTimeFields = Object.keys(response).filter(key =>
          key.toLowerCase().includes('response') || key.toLowerCase().includes('time')
        );
        console.log('AdminService: Response time related fields:', responseTimeFields);
        responseTimeFields.forEach(field => {
          console.log(`AdminService: ${field}:`, response[field]);
        });

        // Try to find any arrays in the response
        Object.keys(response).forEach(key => {
          const value = response[key];
          if (Array.isArray(value)) {
            console.log(`AdminService: Found array "${key}":`, value);
          } else if (value && typeof value === 'object') {
            console.log(`AdminService: Found object "${key}":`, value);
            Object.keys(value).forEach(subKey => {
              if (Array.isArray(value[subKey])) {
                console.log(`AdminService: Found nested array "${key}.${subKey}":`, value[subKey]);
              }
            });
          }
        });
      }

      return response;
    } catch (error) {
      console.error('Failed to get user action logs stats:', error);
      const err = error as any;
      console.error('Stats endpoint error details:', {
        message: err?.message,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data
      });
      return null;
    }
  }

  async getApiLogs(params: EnhancedLogQueryParams = {}): Promise<{ logs: ApiLogDto[], totalCount: number }> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = this.buildLogQueryParams(params);
      const response = await apiService.get<any>(`/api/LogManagement/api-logs?${queryParams}`);

      if (Array.isArray(response)) {
        return { logs: response as ApiLogDto[], totalCount: response.length };
      }
      if (response && typeof response === 'object') {
        const logs = (response.items || response.data || response.logs || response.results || []) as ApiLogDto[];
        const totalCount = response.totalCount || response.total || logs.length;
        return { logs, totalCount };
      }
      return { logs: [], totalCount: 0 };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  async getApiLogsStats(params: EnhancedLogQueryParams = {}): Promise<any> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = this.buildLogQueryParams(params);
      return await apiService.get<any>(`/api/LogManagement/api-logs/stats?${queryParams}`);
    } catch (error) {
      console.error('Failed to get API logs stats:', error);
      return null;
    }
  }

  async deleteApiLog(logId: string): Promise<void> {
    await this.ensureValidAdminToken();
    const numericId = parseInt(logId, 10);
    if (isNaN(numericId)) throw new Error('Invalid log ID');
    await apiService.delete(`/api/LogManagement/api-logs/${numericId}`);
  }

  async bulkDeleteApiLogs(logIds: string[]): Promise<void> {
    await this.ensureValidAdminToken();
    const numericIds = logIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
    await apiService.delete('/api/LogManagement/api-logs/bulk-delete', { logIds: numericIds });
  }

  async deleteApiLogsOlderThan(
    cutoffDate: string,
    onProgress?: (progress: LogPurgeProgress) => void
  ): Promise<{ message: string; deletedCount: number }> {
    return this.purgeLogsOlderThan(
      {
        deletePath: `/api/LogManagement/api-logs/delete-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        countPath: `/api/LogManagement/api-logs/count-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        tableName: 'ApiLogs'
      },
      onProgress
    );
  }

  /**
   * Get top users by time spent (from UserBlockTimeTracking)
   */
  async getTopUsersByTimeSpent(limit: number = 10): Promise<Array<{ userName: string; timeSpent: number }>> {
    try {
      await this.ensureValidAdminToken();
      console.log('AdminService: Getting top users by time spent, limit:', limit);
      const response = await apiService.get<any>(`/api/Admin/users/top-time-spent?limit=${limit}`);
      console.log('AdminService: Top users by time spent response:', response);
      
      if (response && Array.isArray(response)) {
        const mapped = response.map((user: any) => ({
          userName: user.userName || user.name || user.user || 'Unknown',
          timeSpent: user.timeSpentMinutes || user.timeSpent || 0
        })).slice(0, limit);
        console.log('AdminService: Mapped top users by time spent:', mapped);
        return mapped;
      }
      
      console.warn('AdminService: Top users by time spent response is not an array:', response);
      return [];
    } catch (error) {
      console.error('Failed to get top users by time spent:', error);
      return [];
    }
  }

  /**
   * Get top active users from log management endpoint
   */
  async getTopActiveUsers(limit: number = 10): Promise<Array<{ userName: string; count: number }>> {
    try {
      await this.ensureValidAdminToken();
      console.log('AdminService: Getting top active users, limit:', limit);
      const response = await apiService.get<any>(`/api/LogManagement/top-active-users?limit=${limit}`);
      console.log('AdminService: Top active users response:', response);
      
      if (response && Array.isArray(response)) {
        const mapped = response.map((user: any) => {
          // UserActivitySummaryDto has UserEmail and potentially UserName
          const userName = user.userName || user.userEmail || user.name || user.user || 'Unknown';
          const count = user.totalActions || user.actionCount || user.count || user.actions || 0;
          return { userName, count };
        }).slice(0, limit);
        console.log('AdminService: Mapped top active users:', mapped);
        return mapped;
      }
      
      console.warn('AdminService: Top active users response is not an array:', response);
      return [];
    } catch (error) {
      console.error('Failed to get top active users:', error);
      return [];
    }
  }

  /**
   * Get database log statistics
   */
  async getDatabaseLogsStats(params: EnhancedLogQueryParams = {}): Promise<any> {
    try {
      await this.ensureValidAdminToken();

      const queryParams = this.buildLogQueryParams(params);
      console.log('AdminService: Getting database logs stats with params:', queryParams);

      const response = await apiService.get<any>(`/api/LogManagement/database-logs/stats?${queryParams}`);

      console.log('AdminService: Database logs stats response:', response);
      console.log('AdminService: Stats response type:', typeof response);
      console.log('AdminService: Stats response keys:', response ? Object.keys(response) : 'null');

      return response;
    } catch (error) {
      console.error('Failed to get database logs stats:', error);
      const err = error as any;
      console.error('Stats endpoint error details:', {
        message: err?.message,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data
      });
      return null;
    }
  }

  /**
   * Get security log statistics
   */
  async getSecurityLogsStats(params: EnhancedLogQueryParams = {}): Promise<any> {
    try {
      await this.ensureValidAdminToken();

      const queryParams = this.buildLogQueryParams(params);
      console.log('AdminService: Getting security logs stats with params:', queryParams);

      const response = await apiService.get<any>(`/api/LogManagement/security-logs/stats?${queryParams}`);

      console.log('AdminService: Security logs stats response:', response);
      console.log('AdminService: Stats response type:', typeof response);
      console.log('AdminService: Stats response keys:', response ? Object.keys(response) : 'null');

      return response;
    } catch (error) {
      console.error('Failed to get security logs stats:', error);
      const err = error as any;
      console.error('Stats endpoint error details:', {
        message: err?.message,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data
      });
      return null;
    }
  }

  /**
   * Get table sizes for log management tables
   */
  async getLogTableSizes(): Promise<Record<string, string>> {
    try {
      await this.ensureValidAdminToken();
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = new Date().getTime();
      const response = await apiService.get<Record<string, string>>(`/api/LogManagement/table-sizes?_=${timestamp}`);
      return response || {};
    } catch (error) {
      console.error('Failed to get table sizes:', error);
      return {};
    }
  }

  /**
   * Get database recovery model
   */
  async getDatabaseRecoveryModel(): Promise<string> {
    try {
      await this.ensureValidAdminToken();
      const response = await apiService.get<{ recoveryModel: string }>('/api/Seed/database/recovery-model');
      return response?.recoveryModel || 'Unknown';
    } catch (error) {
      console.error('Failed to get recovery model:', error);
      throw this.handleAdminError(error);
    }
  }

  /**
   * Set database recovery model
   */
  async setDatabaseRecoveryModel(recoveryModel: string): Promise<void> {
    try {
      await this.ensureValidAdminToken();
      await apiService.post('/api/Seed/database/recovery-model', { recoveryModel });
    } catch (error) {
      console.error('Failed to set recovery model:', error);
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get database tables with fields, record counts, and sizes for monitoring
   */
  async getDatabaseTablesMonitoring(): Promise<{
    totalSize: string;
    tables: Array<{ name: string; fields: Array<{ name: string; type: string }>; recordCount: number; size: string }>;
  }> {
    try {
      await this.ensureValidAdminToken();
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = new Date().getTime();
      const response = await apiService.get<{
        totalSize?: string;
        tables?: Array<{ name: string; fields: Array<{ name: string; type: string }>; recordCount: number; size: string }>;
      } | Array<{ name: string; fields: Array<{ name: string; type: string }>; recordCount: number; size: string }>>(
        `/api/Admin/database-tables-monitoring?_=${timestamp}`,
        {
          timeout: 120000,
        }
      );

      // Backward compatibility if API returns a bare array
      if (Array.isArray(response)) {
        return { totalSize: '', tables: response };
      }

      return {
        totalSize: response?.totalSize ?? '',
        tables: response?.tables ?? [],
      };
    } catch (error) {
      console.error('Failed to get database tables monitoring:', error);
      throw this.handleAdminError(error);
    }
  }

  /**
   * Delete a single user action log
   */
  async deleteUserActionLog(logId: string): Promise<void> {
    try {
      await this.ensureValidAdminToken();

      // Convert string ID to integer as expected by API
      const numericId = parseInt(logId, 10);
      if (isNaN(numericId)) {
        throw new Error(`Invalid log ID format: ${logId}`);
      }

      console.log('AdminService: Deleting user action log using bulk delete:', numericId);

      // Use bulk delete endpoint for single item since individual delete endpoint returns 404
      await this.bulkDeleteUserActionLogs([logId]);

      console.log('AdminService: Successfully deleted user action log:', numericId);
    } catch (error) {
      console.error('Failed to delete user action log:', error);
      throw error;
    }
  }

  /**
   * Bulk delete user action logs
   */
  async bulkDeleteUserActionLogs(logIds: string[]): Promise<void> {
    try {
      await this.ensureValidAdminToken();

      // Convert string IDs to integers as expected by API
      const numericIds = logIds.map(id => {
        const numericId = parseInt(id, 10);
        if (isNaN(numericId)) {
          throw new Error(`Invalid log ID format: ${id}`);
        }
        return numericId;
      });

      console.log('AdminService: Bulk deleting user action logs:', numericIds);

      const bulkOperation = {
        logIds: numericIds
      };

      // Use fetch directly for DELETE with body since apiService.delete doesn't support body
      const baseUrl = apiService.getBaseURL();
      const token = localStorage.getItem('adminToken');

      const response = await fetch(`${baseUrl}/api/LogManagement/user-actions/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bulkOperation)
      });

      console.log('AdminService: Bulk delete response status:', response.status);
      console.log('AdminService: Bulk delete response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Try to read response body for additional info
      const responseText = await response.text();
      console.log('AdminService: Bulk delete response body:', responseText);

      // Parse response to see if it contains actual deletion info
      try {
        const responseData = JSON.parse(responseText);
        console.log('AdminService: Parsed response data:', responseData);

        if (responseData.deletedCount !== undefined) {
          console.log('AdminService: Backend reports', responseData.deletedCount, 'records actually deleted');
        }
        if (responseData.message) {
          console.log('AdminService: Backend message:', responseData.message);
        }
      } catch (parseError) {
        console.log('AdminService: Response is not JSON, raw text:', responseText);
      }

      console.log('AdminService: Successfully bulk deleted user action logs:', numericIds.length);
    } catch (error) {
      console.error('Failed to bulk delete user action logs:', error);
      throw error;
    }
  }

  /**
   * Fallback method using sample-based approach when user list is not available
   */
  private async getUserActionLogsTopUsersStatsFallback(params: EnhancedLogQueryParams = {}): Promise<{ userName: string, count: number }[]> {
    try {
      // Get a larger sample to better capture user distribution
      const sampleParams = {
        ...params,
        page: 1,
        pageSize: 500 // Larger sample for better user coverage
      };
      const queryParams = this.buildLogQueryParams(sampleParams);
      const sampleResponse = await apiService.get<any>(`/api/LogManagement/user-actions?${queryParams}`);

      let sampleLogs: UserActionLogDto[] = [];
      if (Array.isArray(sampleResponse)) {
        sampleLogs = sampleResponse;
      } else if (sampleResponse && typeof sampleResponse === 'object') {
        sampleLogs = sampleResponse.data || sampleResponse.logs || sampleResponse.items || sampleResponse.results || [];
      }

      // Get total count with current filters
      const totalCountParams = { ...params, page: 1, pageSize: 1 };
      const totalQueryParams = this.buildLogQueryParams(totalCountParams);
      const totalResponse = await apiService.get<any>(`/api/LogManagement/user-actions?${totalQueryParams}`);

      let totalCount = 0;
      if (Array.isArray(totalResponse)) {
        totalCount = totalResponse.length;
      } else if (totalResponse && typeof totalResponse === 'object') {
        totalCount = totalResponse.totalCount || totalResponse.total || 0;
      }

      console.log('AdminService: Fallback - Total filtered records for users:', totalCount);
      console.log('AdminService: Fallback - Sample size for users:', sampleLogs.length);

      // Count users in sample
      const userCounts = new Map<string, number>();
      sampleLogs.forEach(log => {
        const userName = log.userEmail || log.userId || 'Unknown User';
        const current = userCounts.get(userName) || 0;
        userCounts.set(userName, current + 1);
      });

      // Convert to array and sort by count
      const userStats: { userName: string, count: number }[] = [];
      const sampleSize = sampleLogs.length;

      if (sampleSize > 0 && totalCount > 0) {
        const scaleFactor = totalCount / sampleSize;

        userCounts.forEach((sampleCount, userName) => {
          const estimatedCount = Math.round(sampleCount * scaleFactor);
          if (estimatedCount > 0) {
            userStats.push({ userName, count: estimatedCount });
          }
        });

        // Sort by count descending and take top 5
        userStats.sort((a, b) => b.count - a.count);
        const topUsers = userStats.slice(0, 5);

        console.log('AdminService: Fallback - Top users stats:', topUsers);
        return topUsers;
      }

      return [];
    } catch (error) {
      console.error('Failed to get user action logs top users stats (fallback):', error);
      return [];
    }
  }

  /**
   * Get database logs with comprehensive filtering
   */
  async getDatabaseLogs(params: EnhancedLogQueryParams = {}): Promise<{ items: any[], totalCount: number }> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = this.buildLogQueryParams(params);
      const response = await apiService.get<any>(`/api/LogManagement/database-logs?${queryParams}`);
      
      console.log('AdminService: Database logs response:', response);
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return { items: response, totalCount: response.length };
      } else if (response && response.items) {
        return { items: response.items, totalCount: response.totalCount || response.items.length };
      } else {
        return { items: [], totalCount: 0 };
      }
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Delete user action logs older than a specified date
   */
  async deleteUserActionLogsOlderThan(
    cutoffDate: string,
    onProgress?: (progress: LogPurgeProgress) => void
  ): Promise<{ message: string; deletedCount: number }> {
    return this.purgeLogsOlderThan(
      {
        deletePath: `/api/LogManagement/user-actions/delete-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        countPath: `/api/LogManagement/user-actions/count-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        tableName: 'UserActionLogs'
      },
      onProgress
    );
  }

  /**
   * Delete database logs older than a specified date
   */
  async deleteDatabaseLogsOlderThan(
    cutoffDate: string,
    onProgress?: (progress: LogPurgeProgress) => void
  ): Promise<{ message: string; deletedCount: number }> {
    return this.purgeLogsOlderThan(
      {
        deletePath: `/api/LogManagement/database-logs/delete-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        countPath: `/api/LogManagement/database-logs/count-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        tableName: 'DatabaseLogs'
      },
      onProgress
    );
  }

  /**
   * Delete security logs older than a specified date
   */
  async deleteSecurityLogsOlderThan(
    cutoffDate: string,
    onProgress?: (progress: LogPurgeProgress) => void
  ): Promise<{ message: string; deletedCount: number }> {
    return this.purgeLogsOlderThan(
      {
        deletePath: `/api/LogManagement/security-logs/delete-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        countPath: `/api/LogManagement/security-logs/count-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        tableName: 'SecurityLogs'
      },
      onProgress
    );
  }

  /**
   * Delete error logs older than a specified date
   */
  async deleteErrorLogsOlderThan(
    cutoffDate: string,
    onProgress?: (progress: LogPurgeProgress) => void
  ): Promise<{ message: string; deletedCount: number }> {
    return this.purgeLogsOlderThan(
      {
        deletePath: `/api/LogManagement/error-logs/delete-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        countPath: `/api/LogManagement/error-logs/count-older-than?cutoffDate=${encodeURIComponent(cutoffDate)}`,
        tableName: 'ErrorLogs'
      },
      onProgress
    );
  }

  /**
   * Rebuild a log table so InnoDB returns free pages after large DELETE batches.
   */
  async optimizeLogTable(tableName: string): Promise<{
    message: string;
    size?: string;
    sizeBefore?: string;
    sizeAfter?: string;
    shrunk?: boolean;
  }> {
    try {
      await this.ensureValidAdminToken();
      const token = localStorage.getItem('adminToken');
      const baseUrl = apiService.getBaseURL();
      const response = await fetch(
        `${baseUrl}/api/LogManagement/tables/${encodeURIComponent(tableName)}/optimize`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to optimize table' }));
        throw new Error(errorData.message || 'Failed to optimize table');
      }
      return await response.json();
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Chunked purge with optional progress + automatic disk reclaim after large deletes.
   */
  private async purgeLogsOlderThan(
    paths: { deletePath: string; countPath: string; tableName: string },
    onProgress?: (progress: LogPurgeProgress) => void
  ): Promise<{ message: string; deletedCount: number }> {
    try {
      onProgress?.({ phase: 'counting', deletedCount: 0, totalCount: 0 });

      let totalCount = 0;
      try {
        await this.ensureValidAdminToken();
        const countResponse = await apiService.get<{ count: number }>(paths.countPath);
        totalCount = Number(countResponse?.count ?? 0);
      } catch (countError) {
        console.warn('Failed to count logs before purge; continuing without total.', countError);
      }

      onProgress?.({ phase: 'deleting', deletedCount: 0, totalCount });

      let totalDeleted = 0;
      let hasMore = true;

      while (hasMore) {
        await this.ensureValidAdminToken();
        const token = localStorage.getItem('adminToken');
        const baseUrl = apiService.getBaseURL();
        const response = await fetch(`${baseUrl}${paths.deletePath}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Failed to delete logs' }));
          throw new Error(errorData.message || 'Failed to delete logs');
        }

        const chunk = (await response.json()) as {
          message?: string;
          deletedCount?: number;
          hasMore?: boolean;
        };
        const chunkDeleted = chunk.deletedCount ?? 0;
        totalDeleted += chunkDeleted;
        onProgress?.({
          phase: 'deleting',
          deletedCount: totalDeleted,
          totalCount: Math.max(totalCount, totalDeleted)
        });
        hasMore = Boolean(chunk.hasMore) && chunkDeleted > 0;
      }

      // InnoDB keeps file size after DELETE; reclaim when a meaningful amount was removed.
      if (totalDeleted >= 1000) {
        onProgress?.({
          phase: 'reclaiming',
          deletedCount: totalDeleted,
          totalCount: Math.max(totalCount, totalDeleted)
        });
        try {
          await this.optimizeLogTable(paths.tableName);
        } catch (optimizeError) {
          console.warn('Log table optimize failed after purge:', optimizeError);
        }
      }

      onProgress?.({
        phase: 'done',
        deletedCount: totalDeleted,
        totalCount: Math.max(totalCount, totalDeleted)
      });

      return {
        message: `Successfully deleted ${totalDeleted} logs`,
        deletedCount: totalDeleted
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Map log level from various formats to standard LogLevel
   */
  private mapLogLevel(level: any): 'error' | 'warning' | 'info' | 'debug' {
    if (!level) return 'info';
    const levelStr = String(level).toLowerCase();
    switch (levelStr) {
      case 'error':
      case 'err':
        return 'error';
      case 'warning':
      case 'warn':
        return 'warning';
      case 'information':
      case 'info':
        return 'info';
      case 'debug':
      case 'trace':
        return 'debug';
      default:
        return 'info';
    }
  }

  /**
   * Parse headers from string or object format
   */
  private parseHeaders(headers: any): Record<string, string> | undefined {
    if (!headers) return undefined;

    if (typeof headers === 'string') {
      try {
        return JSON.parse(headers);
      } catch {
        // If it's not JSON, treat as plain text
        return { 'raw': headers };
      }
    }

    if (typeof headers === 'object') {
      return headers;
    }

    return undefined;
  }

  /**
   * Get security logs with comprehensive filtering
   */
  async getSecurityLogs(params: EnhancedLogQueryParams = {}): Promise<{ items: SecurityLogDto[], totalCount: number }> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = this.buildLogQueryParams(params);
      const response = await apiService.get<any>(`/api/LogManagement/security-logs?${queryParams}`);
      
      // Handle paginated response (has Items property) or direct array
      if (response && response.items && Array.isArray(response.items)) {
        return { items: response.items, totalCount: response.totalCount || response.items.length };
      } else if (Array.isArray(response)) {
        return { items: response, totalCount: response.length };
      } else {
        return { items: [], totalCount: 0 };
      }
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Delete a single security log
   */
  async deleteSecurityLog(logId: string): Promise<void> {
    try {
      await this.ensureValidAdminToken();

      // Convert string ID to integer as expected by API
      const numericId = parseInt(logId, 10);
      if (isNaN(numericId)) {
        throw new Error(`Invalid log ID format: ${logId}`);
      }

      console.log('AdminService: Deleting security log:', numericId);

      // Use bulk delete endpoint for single item since individual delete endpoint may not exist
      await this.bulkDeleteSecurityLogs([logId]);

      console.log('AdminService: Successfully deleted security log:', numericId);
    } catch (error) {
      console.error('Failed to delete security log:', error);
      throw this.handleAdminError(error);
    }
  }

  /**
   * Bulk delete security logs
   */
  async bulkDeleteSecurityLogs(logIds: string[]): Promise<void> {
    try {
      await this.ensureValidAdminToken();

      // Convert string IDs to integers as expected by API
      const numericIds = logIds.map(id => {
        const numericId = parseInt(id, 10);
        if (isNaN(numericId)) {
          throw new Error(`Invalid log ID format: ${id}`);
        }
        return numericId;
      });

      if (numericIds.length === 0) {
        throw new Error('No valid log IDs provided for deletion');
      }

      console.log('AdminService: Bulk deleting security logs:', numericIds);

      const bulkOperation = {
        logIds: numericIds
      };

      // Use fetch directly for DELETE with body since apiService.delete doesn't support body
      const baseUrl = apiService.getBaseURL();
      const token = localStorage.getItem('adminToken');

      const response = await fetch(`${baseUrl}/api/LogManagement/security-logs/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bulkOperation)
      });

      console.log('AdminService: Bulk delete security logs response status:', response.status);

      const responseText = await response.text();

      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { message: responseText || `HTTP ${response.status}: ${response.statusText}` };
        }
        const errorMessage = errorData.message || errorData.details?.validationSummary || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      if (responseText) {
        try {
          const responseData = JSON.parse(responseText);
          console.log('AdminService: Bulk delete security logs response:', responseData);
        } catch {
          console.log('AdminService: Bulk delete security logs response body:', responseText);
        }
      }
    } catch (error) {
      console.error('AdminService: Error bulk deleting security logs:', error);
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get logs by type with unified filtering system
   */
  async getLogsByType(logType: LogType, params: EnhancedLogQueryParams = {}): Promise<BaseLogDto[]> {
    try {
      await this.ensureValidAdminToken();

      switch (logType) {
        case LogType.ERROR_LOGS:
          return await this.getEnhancedErrorLogs(params);
        case LogType.USER_ACTION_LOGS: {
          const result = await this.getUserActionLogs(params);
          return result.logs;
        }
        case LogType.DATABASE_LOGS: {
          const result = await this.getDatabaseLogs(params);
          return result.items;
        }
        case LogType.SECURITY_LOGS: {
          const result = await this.getSecurityLogs(params);
          return result.items;
        }
        default:
          throw new Error(`Unsupported log type: ${logType}`);
      }
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Search across all log types with unified filtering
   */
  async searchAllLogs(filters: UnifiedLogFilters, params: LogQueryParams = {}): Promise<{
    results: Array<BaseLogDto & { logType: LogType }>;
    totalCount: number;
    logTypeCounts: Record<LogType, number>;
  }> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = this.buildLogQueryParams(params);
      return await apiService.post(`/api/LogManagement/search-all?${queryParams}`, filters);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get log statistics and analytics
   */
  async getLogStatistics(params: {
    logTypes?: LogType[];
    startDate?: string;
    endDate?: string;
    groupBy?: 'hour' | 'day' | 'week' | 'month';
  } = {}): Promise<LogStatistics> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });

      const response = await apiService.get<any>(`/api/LogManagement/statistics?${queryParams}`);

      // Ensure the response has the expected structure with proper defaults
      return {
        volumeMetrics: response.volumeMetrics || {
          totalLogs: 0,
          errorLogs: 0,
          warningLogs: 0,
          infoLogs: 0,
          debugLogs: 0,
          growthRate: 0
        },
        systemHealth: response.systemHealth || {
          overallScore: 85,
          errorRate: 0.5,
          performanceScore: 90,
          securityScore: 95,
          availabilityScore: 99,
          lastAssessment: new Date(),
          recommendations: ['System is operating normally'],
          criticalIssues: [],
          warnings: []
        },
        crossLogAnalytics: response.crossLogAnalytics || {
          totalLogVolume: { totalLogs: 0, warningLogs: 0, infoLogs: 0, debugLogs: 0, growthRate: 0 },
          errorRateAnalysis: { previousRate: 0, trend: 'stable', criticalThreshold: 5 },
          userActivityPatterns: { activeUsers: 0, averageSessionDuration: 0 },
          systemPerformanceMetrics: { averageApiResponseTime: 0, throughput: 0, errorRate: 0 },
          securityThreatLevel: { score: 95, recentIncidents: 0 },
          correlatedEvents: []
        },
        generatedAt: response.generatedAt || new Date().toISOString(),
        timeRange: response.timeRange || {
          startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date().toISOString()
        }
      };
    } catch (error) {
      console.warn('LogManagement statistics API not available, returning mock data:', error);
      // Return mock data when API is not available
      return {
        volumeMetrics: {
          totalLogs: 1250,
          warningLogs: 120,
          infoLogs: 980,
          debugLogs: 105,
          growthRate: 2.5
        },
        systemHealth: {
          overallScore: 85,
          errorRate: 0.5,
          performanceScore: 90,
          securityScore: 95,
          availabilityScore: 99,
          lastAssessment: new Date(),
          recommendations: ['System is operating normally'],
          criticalIssues: [],
          warnings: []
        },
        crossLogAnalytics: {
          totalLogVolume: { totalLogs: 1250, warningLogs: 120, infoLogs: 980, debugLogs: 105, growthRate: 2.5 },
          errorRateAnalysis: { previousRate: 0.8, trend: 'decreasing', criticalThreshold: 5 },
          userActivityPatterns: { activeUsers: 156, averageSessionDuration: 1800 },
          systemPerformanceMetrics: { averageApiResponseTime: 245, throughput: 1200, errorRate: 0.5 },
          securityThreatLevel: { score: 95, recentIncidents: 0 },
          correlatedEvents: []
        },
        generatedAt: new Date().toISOString(),
        timeRange: {
          startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date().toISOString()
        }
      };
    }
  }

  /**
   * Get log trend data for analytics
   */
  async getLogTrend(params: {
    logTypes?: LogType[];
    startDate?: string;
    endDate?: string;
    granularity?: 'hour' | 'day' | 'week';
  } = {}): Promise<LogTrendData> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });

      return await apiService.get<LogTrendData>(`/api/LogManagement/trend?${queryParams}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get top errors
   */
  async getTopErrors(limit: number = 10): Promise<any[]> {
    try {
      await this.ensureValidAdminToken();
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());

      return await apiService.get<any[]>(`/api/LogManagement/top-errors?${queryParams}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get system health status based on log analysis
   */
  async getSystemHealthStatus(): Promise<SystemHealthStatus> {
    try {
      await this.ensureValidAdminToken();
      const response = await apiService.get<any>('/api/LogManagement/health');

      // Ensure the response has the expected structure with proper defaults
      return {
        overallScore: response.overallScore ?? 0,
        errorRate: response.errorRate ?? 0,
        performanceScore: response.performanceScore ?? 0,
        securityScore: response.securityScore ?? 0,
        availabilityScore: response.availabilityScore ?? 0,
        lastAssessment: response.lastAssessment ? new Date(response.lastAssessment) : new Date(),
        recommendations: response.recommendations || [],
        criticalIssues: response.criticalIssues || [],
        warnings: response.warnings || []
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get cross-log analytics for correlation analysis
   */
  async getCrossLogAnalytics(params: {
    startDate?: string;
    endDate?: string;
    logTypes?: LogType[];
  } = {}): Promise<CrossLogAnalytics> {
    try {
      this.ensureAdminToken();
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });

      return await apiService.get<CrossLogAnalytics>(`/api/LogManagement/comprehensive-statistics?${queryParams}`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // LOG TESTING AND VALIDATION API METHODS (Task 2.2)
  // ============================================================================

  /**
   * Generate test error log
   */
  async testErrorLog(config: TestLogConfig): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      return await apiService.post<ValidationResult>('/api/LogManagement/test-error', config as any);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }


  /**
   * Generate test user action log
   */
  async testUserActionLog(config: TestLogConfig): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      // This endpoint doesn't exist in the backend, return mock validation result
      console.warn('User action log test endpoint not available, returning mock result');
      return {
        success: true,
        message: 'User action log test completed successfully (mock)',
        timestamp: new Date().toISOString(),
        duration: 0
      } as ValidationResult;
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Generate test database log
   */
  async testDatabaseLog(config: TestLogConfig): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      // This endpoint doesn't exist in the backend, return mock validation result
      console.warn('Database log test endpoint not available, returning mock result');
      return {
        success: true,
        message: 'Database log test completed successfully (mock)',
        timestamp: new Date().toISOString(),
        duration: 0
      } as ValidationResult;
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Generate test security log
   */
  async testSecurityLog(config: TestLogConfig): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      // This endpoint doesn't exist in the backend, return mock validation result
      console.warn('Security log test endpoint not available, returning mock result');
      return {
        success: true,
        message: 'Security log test completed successfully (mock)',
        timestamp: new Date().toISOString(),
        details: 'Test endpoint not implemented in backend'
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Generate test logs for all log types
   */
  async testAllLogTypes(configs: TestLogConfig[]): Promise<ValidationResult[]> {
    try {
      this.ensureAdminToken();
      return await apiService.post<ValidationResult[]>('/api/LogManagement/test/all-logs', configs);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Validate log system ingestion
   */
  async validateLogIngestion(): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      // This endpoint doesn't exist in the backend, return mock validation result
      console.warn('Ingestion validation endpoint not available, returning mock result');
      return {
        success: true,
        message: 'Log ingestion validation completed successfully (mock)',
        timestamp: new Date().toISOString(),
        details: 'Validation endpoint not implemented in backend'
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Validate log system retrieval
   */
  async validateLogRetrieval(): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      // This endpoint doesn't exist in the backend, return mock validation result
      console.warn('Retrieval validation endpoint not available, returning mock result');
      return {
        success: true,
        message: 'Log retrieval validation completed successfully (mock)',
        timestamp: new Date().toISOString(),
        details: 'Validation endpoint not implemented in backend'
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Validate alert system functionality
   */
  async validateAlertSystem(): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      // This endpoint doesn't exist in the backend, return mock validation result
      console.warn('Alerts validation endpoint not available, returning mock result');
      return {
        success: true,
        message: 'Alerts validation completed successfully (mock)',
        timestamp: new Date().toISOString(),
        details: 'Validation endpoint not implemented in backend'
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Validate export system functionality
   */
  async validateExportSystem(): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      // This endpoint doesn't exist in the backend, return mock validation result
      console.warn('Export validation endpoint not available, returning mock result');
      return {
        success: true,
        message: 'Export validation completed successfully (mock)',
        timestamp: new Date().toISOString(),
        details: 'Validation endpoint not implemented in backend'
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get log system health check - DISABLED to prevent logout issues
   */
  async getLogSystemHealthCheck(): Promise<ValidationResult> {
    // Return mock validation result to prevent authentication issues
    return {
      isValid: true,
      errors: [],
      warnings: [],
      suggestions: [],
      metadata: {
        timestamp: new Date().toISOString(),
        source: 'mock'
      }
    };
  }

  /**
   * Get log system configuration status - DISABLED to prevent logout issues
   */
  async getLogSystemConfiguration(): Promise<{
    success: boolean;
    configuration: Record<string, any>;
    issues: string[];
    recommendations: string[];
  }> {
    // Return mock configuration to prevent authentication issues
    return {
      success: true,
      configuration: {},
      issues: [],
      recommendations: []
    };
  }

  /**
   * Perform load test on log system
   */
  async performLogLoadTest(config: LoadTestConfig): Promise<LoadTestResult> {
    try {
      this.ensureAdminToken();
      return await apiService.post<LoadTestResult>('/api/LogManagement/load-test', config);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get log system performance metrics
   */
  async getLogPerformanceMetrics(): Promise<{
    ingestionRate: number;
    queryPerformance: number;
    storageUtilization: number;
    alertLatency: number;
    systemLoad: number;
    bottlenecks: string[];
  }> {
    try {
      this.ensureAdminToken();
      return await apiService.get('/api/LogManagement/performance-metrics');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Run end-to-end log flow test
   */
  async runEndToEndLogTest(logType: LogType): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      return await apiService.post<ValidationResult>('/api/LogManagement/test/end-to-end', { logType });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // BULK OPERATIONS AND EXPORT API METHODS (Task 2.3)
  // ============================================================================

  /**
   * Perform bulk operations on logs
   */
  async performBulkOperation(request: BulkOperationRequest): Promise<BulkOperationResult> {
    try {
      this.ensureAdminToken();
      return await apiService.post<BulkOperationResult>('/api/LogManagement/bulk-operation', request);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Bulk resolve error logs
   */
  async bulkResolveErrors(logIds: string[], resolutionNotes?: string): Promise<BulkOperationResult> {
    try {
      this.ensureAdminToken();
      const request: BulkOperationRequest = {
        operation: 'resolve',
        logTypes: [LogType.ERROR_LOGS],
        logIds,
        options: { resolutionNotes }
      };
      return await this.performBulkOperation(request);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Bulk unresolve error logs
   */
  async bulkUnresolveErrors(logIds: string[]): Promise<BulkOperationResult> {
    try {
      this.ensureAdminToken();
      const request: BulkOperationRequest = {
        operation: 'unresolve',
        logTypes: [LogType.ERROR_LOGS],
        logIds
      };
      return await this.performBulkOperation(request);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Bulk delete logs across multiple types
   */
  async bulkDeleteLogs(logTypes: LogType[], logIds?: string[], filters?: UnifiedLogFilters): Promise<BulkOperationResult> {
    try {
      this.ensureAdminToken();
      const request: BulkOperationRequest = {
        operation: 'delete',
        logTypes,
        ...(logIds && { logIds }),
        ...(filters && { filters })
      };
      return await this.performBulkOperation(request);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Bulk archive logs
   */
  async bulkArchiveLogs(logTypes: LogType[], archiveBeforeDate: string, filters?: UnifiedLogFilters): Promise<BulkOperationResult> {
    try {
      this.ensureAdminToken();
      const request: BulkOperationRequest = {
        operation: 'archive',
        logTypes,
        ...(filters && { filters }),
        options: { archiveBeforeDate }
      };
      return await this.performBulkOperation(request);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Bulk tag logs
   */
  async bulkTagLogs(logTypes: LogType[], tags: string[], logIds?: string[], filters?: UnifiedLogFilters): Promise<BulkOperationResult> {
    try {
      this.ensureAdminToken();
      const request: BulkOperationRequest = {
        operation: 'tag',
        logTypes,
        ...(logIds && { logIds }),
        ...(filters && { filters }),
        options: { tags }
      };
      return await this.performBulkOperation(request);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Bulk untag logs
   */
  async bulkUntagLogs(logTypes: LogType[], tags: string[], logIds?: string[], filters?: UnifiedLogFilters): Promise<BulkOperationResult> {
    try {
      this.ensureAdminToken();
      const request: BulkOperationRequest = {
        operation: 'untag',
        logTypes,
        ...(logIds && { logIds }),
        ...(filters && { filters }),
        options: { tags }
      };
      return await this.performBulkOperation(request);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Export logs with comprehensive configuration
   */
  async exportLogs(config: ExportConfiguration): Promise<ExportResult> {
    try {
      this.ensureAdminToken();
      return await apiService.post<ExportResult>('/api/LogManagement/export', config);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Export logs in CSV format
   */
  async exportLogsCSV(logTypes: LogType[], filters?: UnifiedLogFilters, customFields?: string[]): Promise<ExportResult> {
    try {
      const config: ExportConfiguration = {
        format: 'csv',
        logTypes,
        ...(filters && { filters }),
        includeMetadata: true,
        includeAnalytics: false,
        compressionEnabled: true,
        encryptionEnabled: false,
        maxRecords: 10000,
        ...(customFields && { customFields })
      };
      return await this.exportLogs(config);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Export logs in JSON format
   */
  async exportLogsJSON(logTypes: LogType[], filters?: UnifiedLogFilters, includeAnalytics: boolean = false): Promise<ExportResult> {
    try {
      const config: ExportConfiguration = {
        format: 'json',
        logTypes,
        ...(filters && { filters }),
        includeMetadata: true,
        includeAnalytics,
        compressionEnabled: true,
        encryptionEnabled: false,
        maxRecords: 10000
      };
      return await this.exportLogs(config);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Export logs in Excel format
   */
  async exportLogsExcel(logTypes: LogType[], filters?: UnifiedLogFilters): Promise<ExportResult> {
    try {
      const config: ExportConfiguration = {
        format: 'xlsx',
        logTypes,
        ...(filters && { filters }),
        includeMetadata: true,
        includeAnalytics: true,
        compressionEnabled: false,
        encryptionEnabled: false,
        maxRecords: 5000
      };
      return await this.exportLogs(config);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Export logs in PDF format
   */
  async exportLogsPDF(logTypes: LogType[], filters?: UnifiedLogFilters): Promise<ExportResult> {
    try {
      const config: ExportConfiguration = {
        format: 'pdf',
        logTypes,
        ...(filters && { filters }),
        includeMetadata: true,
        includeAnalytics: true,
        compressionEnabled: false,
        encryptionEnabled: false,
        maxRecords: 1000
      };
      return await this.exportLogs(config);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get export status
   */
  async getExportStatus(exportId: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    downloadUrl?: string;
    error?: string;
  }> {
    try {
      this.ensureAdminToken();
      return await apiService.get(`/api/LogManagement/export/${exportId}/status`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Download exported logs
   */
  async downloadExportedLogs(exportId: string): Promise<any> {
    try {
      this.ensureAdminToken();
      return await apiService.get(`/api/LogManagement/export/${exportId}/download`);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Configure log retention policies
   */
  async configureRetentionPolicy(policy: {
    logType: LogType;
    retentionDays: number;
    autoCleanup: boolean;
    archiveBeforeDelete: boolean;
    compressionEnabled: boolean;
  }): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();
      return await apiService.post<ValidationResult>('/api/LogManagement/retention-policy', policy);
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get retention policies for all log types
   */
  async getRetentionPolicies(): Promise<Array<{
    logType: LogType;
    retentionDays: number;
    autoCleanup: boolean;
    archiveBeforeDelete: boolean;
    compressionEnabled: boolean;
    lastCleanup?: string;
    nextCleanup?: string;
  }>> {
    try {
      this.ensureAdminToken();
      return await apiService.get('/api/LogManagement/retention-policies');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Manually trigger log cleanup
   */
  async triggerLogCleanup(logTypes: LogType[], cleanupBeforeDate: string, dryRun: boolean = false): Promise<{
    success: boolean;
    logsToDelete: number;
    logsToArchive: number;
    estimatedSpaceFreed: string;
    deletedLogs?: number;
    archivedLogs?: number;
    actualSpaceFreed?: string;
  }> {
    try {
      this.ensureAdminToken();
      return await apiService.post('/api/LogManagement/cleanup', {
        logTypes,
        cleanupBeforeDate,
        dryRun
      });
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get log storage statistics
   */
  async getLogStorageStatistics(): Promise<{
    totalSize: string;
    sizeByLogType: Record<LogType, string>;
    oldestLog: string;
    newestLog: string;
    compressionRatio: number;
    archiveSize: string;
    availableSpace: string;
  }> {
    try {
      this.ensureAdminToken();
      return await apiService.get('/api/LogManagement/storage-statistics');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  // ============================================================================
  // TIMEZONE MANAGEMENT METHODS
  // ============================================================================

  /**
   * Get all available timezones
   */
  async getAvailableTimezones(): Promise<TimezoneInfo[]> {
    try {
      this.ensureAdminToken();
      return await apiService.get<TimezoneInfo[]>('/api/Admin/timezones');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get current system timezone
   */
  async getCurrentTimezone(): Promise<CurrentTimezoneInfo> {
    try {
      this.ensureAdminToken();
      // API returns TimezoneDto: { id, displayName, baseUtcOffset, supportsDaylightSavingTime, ... }
      // (camelCase). Frontend expects timeZoneId / currentUtcOffset so map here — otherwise
      // useTimezone falls back to the browser zone and changing system timezone has no effect.
      // Never cache: admin expects grids to reflect the latest system timezone immediately after a change.
      const raw = (await apiService.get<Record<string, unknown>>(
        '/api/Admin/timezone/current',
        { cache: false }
      )) as Record<string, unknown>;
      const timeZoneId = String(raw.timeZoneId ?? raw.id ?? '');
      const displayName = String(raw.displayName ?? '');
      const offsetRaw = raw.currentUtcOffset ?? raw.baseUtcOffset;
      const currentUtcOffset =
        offsetRaw === undefined || offsetRaw === null ? '' : String(offsetRaw);
      return {
        timeZoneId,
        displayName,
        currentUtcOffset,
        isDaylightSavingTime: Boolean(raw.isDaylightSavingTime ?? raw.supportsDaylightSavingTime),
        notes: raw.notes != null ? String(raw.notes) : undefined,
        lastUpdated: raw.lastUpdated != null ? String(raw.lastUpdated) : undefined,
        updatedBy: raw.updatedBy != null ? String(raw.updatedBy) : undefined
      };
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }

  /**
   * Update system timezone
   */
  async updateTimezone(timezoneData: UpdateTimezoneDto): Promise<ValidationResult> {
    try {
      this.ensureAdminToken();

      // Log the timezone update action
      await userActionLogger.logUserAction({
        type: 'UPDATE_SYSTEM_TIMEZONE',
        resourceType: 'system_configuration',
        details: {
          newTimezone: timezoneData.timeZoneId,
          notes: timezoneData.notes
        }
      });

      const result = await apiService.put<ValidationResult>('/api/Admin/timezone', timezoneData);

      if (result.success) {
        await userActionLogger.logUserAction({
          type: 'SYSTEM_TIMEZONE_UPDATED',
          resourceType: 'system_configuration',
          details: {
            newTimezone: timezoneData.timeZoneId,
            notes: timezoneData.notes,
            success: true
          }
        });
      }

      return result;
    } catch (error) {
      await userActionLogger.logUserAction({
        type: 'UPDATE_SYSTEM_TIMEZONE_FAILED',
        resourceType: 'system_configuration',
        details: {
          error: error instanceof Error ? error.message : 'Unknown error',
          attemptedTimezone: timezoneData.timeZoneId,
          success: false
        }
      });
      throw this.handleAdminError(error);
    }
  }

  /**
   * Get current system time in the configured timezone
   */
  async getCurrentSystemTime(): Promise<SystemTimeInfo> {
    try {
      this.ensureAdminToken();
      return await apiService.get<SystemTimeInfo>('/api/Admin/timezone/current-time');
    } catch (error) {
      throw this.handleAdminError(error);
    }
  }
}

export const adminService = new AdminService();

// Initialize admin auth on service creation - deferred to avoid blocking page load
// Use setTimeout to defer initialization to next event loop tick
setTimeout(() => {
  adminService.initializeAdminAuth();
}, 0);

export default adminService;